/**
 * EmployeeBuilderPage — Employee Builder v3: guided wizard for creating a new Digital Employee (Agent).
 *
 * Eight steps: specialist template + name, employee identity (avatar, role title, specialties,
 * personality, intro, availability — EMPLOYEE-IDENTITY-PERSONA-001; no authority, but NOT
 * presentation-only: these are the manager's routing score, and availability "offline" is
 * dropped from the candidate set), customer
 * scope, requested governed skill domains, model + AI-provider readiness, requested systems with
 * live configuration status, permission limits with a live entitlement projection, and a final
 * read-only preview of the eight domains that make up the employee (persona, role, specialist
 * pack, approved skills, model policy, connections, limits, readiness) whose confirm is the only
 * creating action. Creates a real Agent record via POST /api/agents/.
 *
 * Truthfulness rules (mirrored throughout the live-source steps and the preview):
 *  - every state is explicit; a load failure renders an explicit unknown, never success;
 *  - the verdict is never "ready" while any evidence is unknown or still loading;
 *  - evidence that cannot exist before creation (measured skills, live connection tests, actual
 *    permissions, server model-registry registration) is disclosed as not-verified rather than implied;
 *  - the preview grants nothing: it writes no record, no permission, no connection, no skill
 *    assignment; the wizard-only collections (requested systems, requested skill domains) are intent
 *    only — systems fold into the create payload's guidance text — and they are never silently
 *    dropped: after the single creating action the wizard replays both collections to the governed
 *    publish endpoint (POST /api/agents/{id}/builder-publish-v3) and shows one explicit outcome per
 *    requested item — applied, already present, platform-managed, unsupported, pending or failed.
 *
 * Route: /employee-builder
 * Access: Admin/Owner only (agents_manage).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { agentConnectionsApi, agentsApi, companiesApi, connectionsApi, scheduledChecksApi } from "../services/api";
import { isUnrestrictedAllowlist } from "../utils/toolAllowlist";
import { parseSpecialties } from "../utils/agentPayload";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "../components/EmployeeAvatar";
import { riskGradeLabel } from "../components/riskGradeVocabulary";

// ─── Constants ────────────────────────────────────────────────────────────────

// Steps 1-7 collect configuration; step 8 is the v3 composed read-only preview of the eight
// domains and its confirm is the only creating action.
const TOTAL_STEPS = 8;

// Mirrors the delivered backend v3 preview contract's bounds (schemas/employee_builder_v3.py).
const MAX_REQUESTED_SKILL_DOMAINS = 12;
const MAX_SKILL_DOMAIN_CHARS = 64;
const PROMPT_EXCERPT_LENGTH = 160;

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 22): the bounds AgentCreate enforces on the
// SINGLE CREATING ACTION (schemas/agent.py), mirrored here because crossing any one of them makes
// POST /api/agents/ refuse the whole draft and create nothing at all - measured field by field.
// role_title (120), avatar_key (16) and the two persona texts (2000) are already held down by
// maxLength on their own inputs; these four were named nowhere in this file. MAX_SPECIALTY_CHARS is
// the sibling arm of the very validator (_valid_specialties) whose other arm the shared parser
// clamps silently, which is how one validator came to be mirrored wrong in both directions at
// once. Pinned against the server's own constants by backend/tests/test_employee_builder_v3.py,
// which reads this file off disk the way the §28 vocabulary and the skill bounds already are.
const MAX_NAME_CHARS = 255;
const MAX_SPECIALTIES = 20;
const MAX_SPECIALTY_CHARS = 40;
const MAX_SYSTEM_PROMPT_CHARS = 10000;

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 24): three names are not labels, they are
// the platform's operational identities, and the Agent row carrying one of them is resolved BY
// NAME - routers/agents.py `_SYSTEM_AGENT_NAMES`, built from agent_runs.PILOT_AGENT_NAME,
// platform_guardian.GUARDIAN_NAME and tech_scout.SCOUT_AGENT_NAME. `Agent.name` is not unique, so
// for every other name a collision is exactly what the readiness row said it was: cosmetic. For
// these three it is one of two determinate things, and the row said neither.
//
// Taken: `create_agent` awaits `_assert_system_name_unused(db, payload.name)` BEFORE it builds the
// row, and an exact match on an existing agent is a 409 - the single creating action is refused
// and the employee is not created. The wizard sends `form.name.trim()` and AgentCreate.name has no
// stripping validator, so the membership test below is the server's own, on the server's own
// value, with no new evidence needed.
//
// Free: the create succeeds, and the platform then resolves that name to THIS employee -
// `get_or_create_guardian_agent` / the Scout's equivalent return a pre-existing row with the name
// rather than creating their own singleton, and the pilot is looked up by name on every unbound
// ticket analysis and on the scheduler's auto-analysis cycle. The tier, autonomy, allowlist and
// customer scope chosen in this wizard become that role's. That is not an error - AgentsPage's own
// name placeholder advertises "לפיילוט: AI Technician Pilot" - so it is disclosed and NOT degraded
// (pass 15: do not degrade a ladder for a correct outcome); pinned in that direction so the next
// pass cannot flip it without reddening a test.
//
// Membership is a local property of the typed text, so it is stated on every arm, including the
// ones where the employee list did not load (pass 23: a property of the gate renders even when the
// fetch behind the counts failed). Set-equality with the server is pinned off disk in
// backend/tests/test_employee_builder_v3.py - the harm differs by direction, so neither side may
// grow alone.
const RESERVED_SYSTEM_AGENT_NAMES = ["AI Technician Pilot", "Platform Guardian", "Technology Scout"];

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 45): pass 24 armed both consequences above
// completely and truly, and neither of them says whether the operator can get back out of the one
// it describes. Measured: `update_agent` is the only route that renames an Agent, and its first
// guard is `if agent.name in _SYSTEM_AGENT_NAMES and changes["name"] != agent.name:` -> 409, keyed
// on the row's OWN current name rather than on whether this row is the platform's real singleton,
// so an employee created here with a reserved name can never be renamed. And `backend/app` holds
// no hard delete of an Agent row at all: `DELETE /api/agents/{agent_id}` is `disable_agent`, which
// sets `is_enabled = False` and leaves the row - and the name - exactly where it is. None of the 14
// by-name resolutions of these three identities (5 files) carries an `is_enabled` term either, so
// switching the employee off does not release the role: `get_or_create_guardian_agent` keeps
// returning the disabled row instead of creating the singleton it would have created with
// `is_enabled=True`, and `trigger_guardian_run` then refuses because that row is not active.
//
// So the name is the one field in this draft whose choice cannot be undone after the single
// creating action, for exactly these three values. Stated on the adoption arm (the arm where a row
// is created) and at the step that COLLECTS the name - pass 43: a disclosure about what a field
// DOES belongs at the surface where it is typed, not only at the ones that report it, and the box
// is seven steps earlier and the only mandatory field there (`canAdvance`). Still NOT degraded:
// the pilot arm is the one AgentsPage's own placeholder advertises, and pass 24's pin keeping the
// membership predicate out of the row's `state:` expression is deliberately left standing.
export function isReservedSystemName(name) {
  // Exact and case-sensitive on the submitted value, the same fold as the readiness row's
  // membership test, shared so a looser copy cannot grow at one surface (pass 13).
  return RESERVED_SYSTEM_AGENT_NAMES.includes((name || "").trim());
}

export const RESERVED_NAME_IRREVERSIBLE_CLAUSE =
  "בחירת השם הזה אינה הפיכה לאחר היצירה: השרת מסרב לשנות את שמו של סוכן מערכת (409), ואין בפלטפורמה מחיקת עובד — „מחיקה” מכבה את העובד בלבד והשם נשאר תפוס, וכל עוד הרשומה הזאת קיימת הפלטפורמה לא תיצור את סוכן המערכת שלה בשם הזה.";

// The collecting surface's own statement of the axis. Deliberately NOT the readiness row's
// wording: at step 1 nothing has been fetched, so which of the two outcomes applies is not yet
// decided, and the authority chosen in the later steps is still in the future tense (pass 27: a
// surface before the choice states the condition, not the state).
export const RESERVED_NAME_COLLECT_NOTICE =
  "השם הזה שמור לסוכן מערכת של הפלטפורמה. אם סוכן בשם הזה כבר קיים, השרת ידחה את היצירה (409) והעובד לא ייווצר; ואם אינו קיים, העובד שייווצר כאן הוא זה שהפלטפורמה תאתר לפי השם ותפעיל בתפקיד המערכת הזה — עם רמת ההרשאה, האוטונומיה, רשימת הכלים וסקופ הלקוחות שייבחרו בשלבים הבאים.";

// ─── The draft's free text and the server's secret check (pass 46) ────────────
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141. Pass 22 deliberately did NOT mirror the three
// secret-refusal validators (a weaker local copy of a security check does not fail safe) and
// stated the boundary in prose instead, pinned by
// `test_the_three_secret_refusal_validators_are_exactly_the_fields_the_wizard_names`. That pin
// holds and the field list is right. The SENTENCE was wrong twice, both times in the reassuring
// direction, and no mutation census could see it because the string is fully asserted:
//
//  1. It said text that "looks like" (שנראה כמו) a password, key or token is refused. It is not.
//     `_refuse_secret_system_prompt` -> `contains_labelled_secret` -> `has_labelled_secret`
//     fires only on a LABELLED value ("password: X1!", "הסיסמה היא X1!", "DB_PASSWORD=X1!") or on
//     a recognized format (JWT, PEM private-key block, bearer, sk-/pk-/rk-, AKIA, ghp_, xox,
//     user:pw@host, the Entra `8Q~` shape). Its own docstring calls the gap a "Deliberate limit"
//     and `contains_labelled_secret`'s comment says `{"password": "Summer2024!"}` is caught
//     "although the value alone is not". Measured by constructing AgentCreate one value at a
//     time: of 15 shapes, the 9 labelled-or-recognized ones are REFUSED on all three fields and
//     the 5 bare ones (a bare value, a bare value inside prose, a 40-char random token, a 64-hex
//     string, a purely alphabetic password) are ACCEPTED - which is the shape pasting just the
//     value produces, i.e. the common case the sentence promised to stop.
//  2. "שדות אלה אינם מקום לסודות" is pass 33's UNGUARDED CONTRAST: naming three fields as the
//     ones that refuse a secret asserts something about the rest by omission. Of the SEVEN
//     free-text identity/role fields this wizard collects, FOUR carry no secret validator at all
//     - `name`, `role_title`, `specialties` items and `description` - and in those four even a
//     labelled credential is accepted and stored raw on the row. `description` also has no length
//     bound anywhere (`Optional[str]` with no MaxLen over a `Text` column; 1,000,000 chars
//     accepted), and `routers/agents.py::_to_response` returns it UNREDACTED under `_view`
//     (`agents_view`), a wider grant than the `agents_manage` this wizard itself needs - while
//     `growth_profile._declared`, `professional_profile` and the server's own builder preview all
//     pass it through `redact_secrets` first, under a docstring that says "The stored row is
//     never touched - only the projected copy changes". So the platform's own projections treat
//     this field as a possible credential carrier and the create path neither refuses nor bounds
//     it, with nothing said at the surface that collects it.
//
// Measured and NOT a defect, so nobody re-chases it: `description` does not reach a model.
// `conversation_service.persona_of` is "the only agent attributes ever handed to converse()" and
// carries name / role_title / personality / specialties, not `description`; `agent.system_prompt`
// is the one field reaching the analysis prompt (pass 33). Which is why this is closed by
// labelling and NOT by adding a validator in an unowned schema.
//
// CORRECTED by pass 47: the sentence that stood here - "so the refusal set tracks the
// model-exposure axis and excluding `description` is coherent" - was the right CONCLUSION from a
// false REASON, and the reason is refuted by the line above it. Of the four fields `persona_of`
// hands to converse(), THREE carry no secret validator (`name`, `role_title`, `specialties`);
// only `personality` does. So the refusal set does not track model exposure in 3 of 4 cases. What
// keeps the "no LLM receives raw credentials" invariant intact is not the schema but the
// TRANSPORT: `direct_llm_analyst` runs `redact_secrets(user_content)` over the whole prompt string
// before the provider call, and `redact_secrets` is `redact_labelled_secrets` (the gate's own
// patterns) plus three well-known-format patterns. Verify an inherited explained zero by its own
// mechanism - a conclusion can survive a false reason. The operator-visible consequence of the
// real mechanism is IDENTITY_MODEL_EGRESS_NOTICE below.
export const SECRET_REFUSAL_REACH_NOTICE =
  "הסירוב אינו „כל מה שנראה כמו סיסמה”: השרת מזהה סוד המופיע לצד תווית (למשל „הסיסמה היא …”) או בתצורה מוכרת של טוקן או מפתח פרטי, וערך שהודבק לבדו — בלי תווית ובלי תצורה מוכרת — אינו מזוהה, אינו נדחה ונשמר כפי שהוקלד.";

// The other term of the contrast, self-contained so it reads correctly both on the readiness row
// (beside the three refusing fields) and at the box that collects one of the four (pass 25:
// interpolate a cross-surface sentence rather than quoting it, so the two cannot drift).
export const DRAFT_UNCHECKED_SECRET_FIELDS_NOTICE =
  "השם, התפקיד, תחומי ההתמחות והתיאור אינם נבדקים לסוד כלל — בשונה מהאישיות, הפתיח והוראות ההפעלה, סוד שיודבק בהם יישמר על רשומת העובד גם כשהוא מופיע לצד תווית.";

// The description field's own axes at the surface that COLLECTS it (pass 43). The routing half is
// armed today only at `StepIdentity`'s identity-boundary-note and at `Step7Preview` - and
// `StepIdentity` collects neither `description` nor `name`; both are typed at `Step1Role`, whose
// in-region sweep for the secret, refusal, routing and bound vocabularies is 0/0/0/0.
export const DESCRIPTION_COLLECT_NOTICE =
  "התיאור אינו לתצוגה בלבד: הוא אחד הגורמים שמדרגים את העובד בבחירה לפנייה, אין לו מגבלת אורך בשרת, והוא נשמר ומוצג כפי שהוקלד לכל מי שהרשאתו מאפשרת לצפות בעובדים.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 71): the UNGRADED OPTION. Pass 29
// closed this step's display-only framing with `identity-boundary-note`, whose surviving
// sentence enumerates the ranking basis as FOUR fields -- specialties, role, name,
// description -- and names `availability_status` only for its categorical arm, the
// `offline` exclusion from the candidate set. Both halves are true, and the enumeration is
// what makes the field read as a binary. It is not: availability is itself a ranking term.
//
// Measured. `conversation_service._AVAILABILITY_PENALTY` is {available: 0, busy: 1,
// away: 2}, `score_agent_for_message` returns it as `penalty`, and `penalty` is the SECOND
// sort key at every one of the four ranking sites -- `_select_agents_for_routing`
// (-score, penalty, name), and `routers/agents.py::routing_recommendation`,
// `manager_console` and `routing_desk` (-score, penalty, workload, name, id). So after the
// match score and before every other tie-breaker, a `busy` employee loses to an
// `available` one and an `away` employee loses to both -- and this router's own no-match
// sentence says it outright to the operator, on four rendered sites: the candidate list
// "is sorted by availability and active load only".
//
// Second axis, same choice: the lifetime. `employee_workspace.effective_status` has three
// arms -- a disabled row is `offline`; an `away`/`offline` self-status is RESPECTED; else
// `busy` while there is active work, otherwise the stored value. So `available` is a live
// baseline the platform itself raises to `busy`, while `busy` and `away` are returned
// unconditionally. And nothing in the platform ever writes the column back: 0
// `update(Agent)` statements in `backend/app`, every other occurrence a response builder,
// and the single mutating path is `update_agent`'s generic `setattr` loop -- reachable only
// from `AgentDetailPage`'s edit modal, the page pass 70 measured as `agents_view`-gated.
//
// Arm count on the graded cost before this pass: 0. The interpolation-proof measurement is
// the predicate set -- the whole file compares `form.availability_status` against
// `"offline"` (twice) and `"available"` (once) and against `"busy"`/`"away"` NEVER, so the
// two middle rungs are named by no branch anywhere. Fixture-shape census, decisive again:
// `EmployeeProfileCard` is rendered with `available` and `offline` only, and `busy` appears
// in 0 of this suite's fixtures, so 364 green tests could not reach either rung.
//
// Disclosed, nothing degraded (pass 21.6): `busy` and `away` are legitimate choices, so no
// readiness row reads this value list and the verdict is untouched -- pinned as the
// decision. `identity-boundary-note` is left byte-identical; its enumeration is corrected
// by naming availability with the SAME phrase `DESCRIPTION_COLLECT_NOTICE` uses for a
// ranking term, SLICED out of it rather than retyped, and the three rung labels are
// interpolated from `STATUS_META`. Both halves hold for both armed values, so one
// condition serves both clauses (pass 53).
export const AVAILABILITY_RANKING_PHRASE =
  DESCRIPTION_COLLECT_NOTICE.split("הוא ")[1].split(", אין לו")[0];
export const AVAILABILITY_RANKING_TERM_CLAUSE =
  `הזמינות עצמה היא ${AVAILABILITY_RANKING_PHRASE}: אחרי ההתאמה ולפני כל שאר הגורמים — „${STATUS_META.available.label}” לפני „${STATUS_META.busy.label}”, ו„${STATUS_META.busy.label}” לפני „${STATUS_META.away.label}”.`;
export const AVAILABILITY_STICKY_VALUE_CLAUSE =
  `בשונה מ„${STATUS_META.available.label}”, שהמערכת עצמה מציגה כ„${STATUS_META.busy.label}” בזמן עבודה פעילה, הערך שנבחר כאן נשאר בתוקף עד שהזמינות תשתנה מדף העובד.`;
export const AVAILABILITY_GRADED_COST_NOTICE = [
  AVAILABILITY_RANKING_TERM_CLAUSE,
  AVAILABILITY_STICKY_VALUE_CLAUSE,
].filter(Boolean).join(" ");
// the two rungs between the live baseline and the exclusion -- exactly the members of
// `_AVAILABILITY_PENALTY` with a non-zero penalty, pinned as a set in the other language.
export const AVAILABILITY_GRADED_VALUES = ["busy", "away"];

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 67): the WIDER COPY. Pass 66 censused the
// two durable landing places one click writes `form.tools` into; this is the same census on the
// field its handover called the sharpest, and the second landing place is not a column at all.
// `create_agent` (this task's own owned router) calls
// `log_activity(..., details=f"{agent.name}; tier=..., autonomy=..., enabled=...")`, so the single
// creating action writes the NAME verbatim into an `activity_log` row: `log_activity` bounds
// `details` at 2000 and `name` at 255, so verbatim in every reachable case.
//
// What makes that a disclosure gap rather than a bookkeeping detail is that the second copy has a
// WIDER audience than the one DESCRIPTION_COLLECT_NOTICE names, and no remedy reaches it:
//
//   * `GET /api/activity-log/` is that router's ONLY route and is gated on
//     `Depends(get_current_user)` -- authentication alone. `get_current_user` decodes the token,
//     loads the user and checks `is_active`; no role, no permission, and the `company_id` query
//     parameter is a FILTER, not a scope restriction. An active API token reaches it too.
//     `ActivityLogResponse` exposes `details`/`old_value`/`new_value` in full and
//     `ActivityLogPage.jsx` renders `entry.details` for every non-ticket entity.
//   * censused over the 328 route blocks in `backend/app/routers`: 28 expose an employee name and
//     ZERO are authentication-only (9 agents_view, 7 require_admin, 6 agents_manage, 4 agents_run,
//     1 require_mcp_bridge, 1 require_role) -- and `require_agent_permission` needs an explicit DB
//     grant for everyone but OWNER, so Admin does not even get `agents_view` by role. The audit
//     copy is the widest-audience copy of the name in the product, and the frontend does not
//     narrow it (`App.jsx` registers /activity-log with no gate, Layout lists it adminOnly:false).
//   * it is permanent: `delete(ActivityLog` is 0 across `backend/app` against a same-concept
//     control of 24 `db.delete(` sites, the owned `activityApi` has exactly one member (`list`, a
//     GET), and there is no purge/retention/prune code at all (control: 66 references).
//   * and the operator's own remedy writes it AGAIN. Editing the name is supported
//     (`AgentUpdate.name`, EditModal seeds it), and `update_agent`'s per-field audit call passes
//     `old_value=_audit_value(old)` -- so renaming away from a pasted secret records that secret
//     in a NEW row's `old_value` (`_audit_value` truncates at 500; name <= 255, verbatim).
//     `system_prompt` is the one field routed to a char-count-only branch, so that module's author
//     knew these columns are a second store of free text and protected the one field already
//     covered by a secret validator.
//
// The authority was already in the tree for the tenth time, and this time as a comment about this
// exact concern: `AgentConnectionCreate` carries `ConfigDict(hide_input_in_errors=True)` under
// "A rejected secret_ref may be a pasted secret: keep it out of ValidationError text / logs."
// Applied to a schema's validation errors, never to the audit row's free text.
//
// Reachable with one paste: `name` is the only mandatory field (`canAdvance` step 1 is
// `form.name.trim().length > 0`), it is typed here, and it is one of the four pass 46 measured as
// carrying no secret validator at all. Absence sweep before this change: 0 arms on the
// audit-record axis over the whole wizard (the audit vocabulary's 7 comment-stripped hits all
// resolve to other subjects -- five are role-preset text about CVE/DR/knowledge duties, one is a
// `systemPrompt` body naming the unrelated AgentAction trail, one is pass 31's platform-duty
// note) against a same-concept control of 42 storage/audience hits. Pass 65's rule: count sites
// whose SUBJECT is the audit record, never occurrences.
//
// Closed by LABELLING only, and the refusal is pinned rather than merely taken (pass 21.6): the
// fix is NOT to stop naming the employee in the audit entry. Pass 56 deliberately strengthened
// that entry's attribution, and an audit row that cannot name its subject is worse than this
// disclosure gap -- so the backend asserts `create_agent`'s details still carries the name, and a
// later pass cannot "fix" this by gutting it. Narrowing the read gate is a change to the unowned
// `routers/activity_log.py` affecting every entity's audit data, so it is escalated, not made
// here. Unconditional, for pass 46's reason at this very box: the only moment this can change
// what gets pasted is BEFORE the paste. The audience clause is SLICED from
// DESCRIPTION_COLLECT_NOTICE so the two cannot drift about who can read an employee's fields, and
// the log is named with the platform's own nav label so the sentence names a screen the operator
// can open -- both pinned cross-language by equality, not by containment (pass 65).
export const NAME_AUDIT_COPY_NOTICE =
  "שם העובד נרשם ביצירה גם ביומן הפעולות של המערכת — רשומה נוספת לצד רשומת העובד: היומן פתוח לכל משתמש פעיל במערכת ולא רק למי שהרשאתו מאפשרת לצפות בעובדים, השם שנרשם בו נשאר גם לאחר שינוי שם העובד, ובשינוי עצמו נרשם ביומן גם השם הקודם.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 47): the TRANSITIVE EGRESS. Pass 46 closed
// `description`'s axes at the box that collects it and stated the unchecked-fields contrast
// (DRAFT_UNCHECKED_SECRET_FIELDS_NOTICE) with exactly ONE consequence: a secret pasted into one of
// the four unchecked fields "יישמר על רשומת העובד" - it is STORED. True, and the weakest of the
// two things that happen to it. Measured for `role_title`, this pass's named field:
//
//   * no secret validator (`AgentCreate` accepts "password: Sup3rSecret!" in `name`,
//     `role_title`, `description` and `specialties`; refuses it in `personality`, `intro_text`,
//     `system_prompt` - pass 46's census, reproduced field by field);
//   * `conversation_service.persona_of` hands name / role_title / personality / specialties to
//     `converse()`, and `helpdesk_analyst.render_persona_context` interpolates the name and the
//     role VERBATIM AND UNTRUNCATED into the prompt's first line ("אתה <name>, <role> — ..."),
//     while `personality` is cut at [:400] and `specialties` at [:20]. A 500-char role title
//     survives whole. (`intro_text` is NOT in that set and reaches no provider at all. This
//     parenthetical asserted the opposite until pass 49; the measurement that refutes it is under
//     INTRO_TEXT_PUBLICATION_NOTICE below, which establishes that `_recent_transcript` admits
//     `operator_message` / `human_response` only, so the intro's `system` row never enters
//     converse()'s input, and that `manager_agent` reads no ConversationMessage at all on the
//     ticket-analysis path. The false claim is retired HERE, at the sentence that made it, rather
//     than left standing 45 lines above its own correction; pass 47's conclusion survives for a
//     different reason, since the intro is one of the three VALIDATED fields. Where it DOES go is
//     that notice's subject: the platform publishes it into the conversation itself.)
//   * the exit filter is `redact_secrets`, run by `direct_llm_analyst` over the whole prompt
//     string before the provider call - and it is `redact_labelled_secrets` (the GATE's own
//     patterns) plus three well-known-format patterns. Same detector at both ends.
//
// So the shapes SECRET_REFUSAL_REACH_NOTICE already names as accepted-and-kept-as-typed are, bar
// those three formats, exactly the shapes the exit does not remove either: measured on the
// rendered persona block, "password: Sup3rSecret!" is removed (1 value), while a bare
// "Sup3rSecret!", a 40-char random token and a 64-hex string are each present verbatim and
// removed ZERO times. The invariant holds for what the platform recognises and the disclosure
// stopped one step short of where the unrecognised value goes.
//
// Arms on this axis before this pass: ZERO. In-region sweep of `StepIdentity` (L1457-1776,
// 15,538 chars - the surface that collects `role_title`, `specialties_text`, `personality` and
// `intro_text`): secret 0, refusal 0 against 50 and 12 file-wide; the role-title box is 11 lines
// with exactly TWO operator-visible strings (its label and its placeholder) and 0 on every axis.
// The three lexical "model" hits in the region are a near-miss - two are in a code comment and
// the third is the preset-scope note saying Step1Role's ROLE presets change the model SETTING,
// which is a different family and a different fact. And the one notice that names "התפקיד"
// renders at two sites, L1298 (the `description` box) and the readiness row - neither collects it.
//
// Labelling only, and no readiness state moves: redaction-at-transport is the designed shape and
// a global employee typing a role title has nothing to fix, only something to know. Written to
// read correctly on both surfaces, so no locative ("כאן") and no quoting of the sibling notice.
export const IDENTITY_MODEL_EGRESS_NOTICE =
  "ארבעת פרטי הזהות — השם, התפקיד, האישיות וסגנון העבודה ותחומי ההתמחות — אינם מוצגים לצוות בלבד: הם נשלחים לספק המודל של המערכת בכל תור שבו העובד משיב בשיחה, כקטע הזהות שלו בטקסט שנשלח למודל. לפני הקריאה מוסר מן הטקסט סוד מזוהה, באותו גלאי שדוחה סוד ביצירה ובתוספת כמה תצורות מפתח מוכרות — ולכן דווקא ערך שהודבק לבדו, זה שהיצירה אינה דוחה, גם אינו מוסר לפני הקריאה ומגיע לספק כפי שהוקלד.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 68): the UNFILTERED SECOND EGRESS. The
// notice above names ONE model-facing egress for the four identity fields (persona_of ->
// render_persona_context -> converse()) and states the filter THAT path applies. It is not the
// only one. `routers/mcp_bridge.py` - "Read-only service bridge for the central Prodigy MCP" -
// hands the employee's identity text to five read-only projections. Censused per route and per
// field: `name` is projected RAW on FOUR of the five (/agents, /workforce, /ticket-workbench,
// /approval-center) and redacted on none; `role_title` raw on THREE and redacted on ONE; the
// `specialties` on TWO, both raw. The router itself applies redaction ZERO times (0 each of
// redact_secrets / redact_labelled_secrets / _redacted / contains_labelled_secret /
// has_labelled_secret / looks_like_secret, against an in-file control of 1 `agent.name`).
//
// The two EMPLOYEE LISTINGS are the pair where all three are raw together, and both are
// platform-wide: /agents' query carries no `client_id` filter and no `is_enabled` filter, and
// `build_workforce_overview` takes no customer parameter at all and queries a bare
// `select(Agent)`. So every employee of every customer is returned, each with its own
// `client_id`/`client_name`. That pair is what the clause's as-typed half is scoped to --
// a blanket "nothing is removed there" would be FALSE, which is the next paragraph.
//
// So the one piece of good news in the sentence above is false of the second path. Measured with
// the REAL redactor over eight shapes: `redact_secrets` removes all FOUR labelled forms
// ("the password is X", the Hebrew "הסיסמה היא X" the notice's own example uses, `token=...`,
// `api_key: ...`) and leaves the bare value, a 40-char token and a 64-hex string; the bridge
// removes 0 of 8. The shapes the operator is told ARE removed before the model call are handed
// to this one as typed.
//
// TWO exemption tells (pass 67's shape, twice). First: `workforce_overview`'s own module
// docstring states the discipline - "free text (ticket titles, result summaries) passes
// secret_redaction.redact_secrets and a length bound before it is projected" - and
// `_clip_redacted` runs on exactly `ticket_title` and `result_summary`, in the SAME employee
// payload that carries `name`, `role_title` and `declared_specialties` raw. Second, and
// sharper: `approval_center` projects `"role_title": _redacted(agent.role_title)` and
// `"name": agent.name` in ONE dict - so the redactor is already applied to one of these three
// fields, on one route, which is what proves the store is recognised AND that the coverage is
// partial. My own draft of this comment said "TWO of its five routes" and claimed the
// interface removes nothing; both were corrected by the per-route census before the commit.
// Measure every count you publish with a script (pass 50/64), including the ones inside a
// comment, where no precondition can catch them.
//
// Scope and placement. `personality` is deliberately NOT named: it is the one of the notice's
// four that no bridge route projects (and the one that DOES carry a secret validator), so a
// four-member claim here would be wrong. The as-typed half is scoped to the EMPLOYEE LISTING
// on purpose: an unscoped "nothing is removed there" is FALSE of `approval_center`'s role
// title and of the same route's ticket text. Interpolated at both sites that carry the notice
// it extends, so the egress axis
// cannot drift between them. Declined site, with its reason (pass 65): `Step1Role`, which
// collects `name`, states the audit-row copy (pass 67) and the unchecked-fields set (pass 46)
// but has never carried the egress axis - pass 47 placed that axis at `StepIdentity` and the
// readiness row, and this clause stays on the axis rather than opening a third site for it.
//
// Nothing degraded and no readiness state moves: a token-gated read-only integration is the
// designed shape (pass 21.6) and an operator who types an ordinary role title has nothing to
// fix, only something to know. The redaction gap in the unowned `routers/mcp_bridge.py` is
// recorded as carry-forward, not patched here.
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 78): the set difference between the
// population the PIN above measures and the population this sentence NAMES. Pass 77 found that
// shape on pass 70's destination pin; it is here too, in the pin pass 68 wrote for this very
// notice. `test_the_bridges_identity_carriers_are_censused_per_route_and_per_field` resolves the
// as-typed fact PER FIELD AND PER ROUTE -- `name` raw on FOUR of the bridge's five projections
// and redacted on NONE, `role_title` raw on THREE and redacted on ONE, `specialties` on TWO,
// both raw -- and the sentence scoped its as-typed half to the EMPLOYEE LISTING, i.e. to TWO
// routes, for all three fields at once. Re-measured live this pass against the same five
// modules: `name` at mcp_bridge:99, workforce_overview:376, ticket_workbench:726 and
// approval_center:405, with `_redacted` applied to it at none of them; `role_title` at the same
// four with `_redacted(agent.role_title)` at approval_center:406 alone; `specialties` at
// mcp_bridge:108 and workforce_overview:401 only, and `remote_operations` carries none of the
// three (0 hits on all three field names).
//
// So the as-typed cells the prose did not name are THREE: `name` on /ticket-workbench and
// /approval-center, and `role_title` on /ticket-workbench. ZERO cells were over-claimed. The
// direction is a withheld RISK (pass 64's test): an operator who pasted a credential into the
// NAME read that it arrives as typed "in the employee listing" and could bound the exposure to
// that one projection, while the name is as-typed on every projection of that interface which
// carries it and is filtered on none.
//
// Why pass 68's scope was narrower than its own measurement: its blanket draft was FALSE of
// `approval_center`'s redacted role title, and it closed a per-FIELD problem by narrowing the
// ROUTE population for all THREE fields -- pass 34's CONJOINED SUBJECT, applied to the fix
// rather than to the sentence. The honest grain is the field, so the clause states the two
// fields that go beyond the listing and keeps the one redacting projection named as the
// exception it is.
//
// Which half needed NO arm, measured (pass 61/66): `specialties`. Its carrier set is exactly
// the listing pair, so the first sentence is already a complete statement about it and a third
// clause would add a reassurance the finding does not need. `personality` stays out for pass
// 68's reason -- no bridge route projects it.
//
// Slice-not-retype and no new site: the clause is appended to the existing constant, so both
// render sites carry it with no new interpolation (`{X}` 2 / `${X}` 1, unchanged), every one of
// its 22 distinct Hebrew words has a base in this file at HEAD (2 are plain inflections,
// `\u05d1\u05de\u05e7\u05d5\u05dd` and `\u05e9\u05e0\u05d5\u05e9\u05d0`), the
// final-letter sweep flags 0 against a control of 5, and all three of pass 68's own pins hold
// over the composed value unchanged (as-typed 1, blanket 0, platform-wide 1).
//
// Nothing degraded, no readiness state moves and no boundary changes: the bridge is a
// token-gated read-only integration (pass 21.6) and the redaction gap in the unowned
// `routers/mcp_bridge.py` stays pass 68's carry-forward, escalated and not patched.
export const IDENTITY_MCP_BRIDGE_EGRESS_NOTICE =
  "השם, התפקיד ותחומי ההתמחות מופיעים גם בממשק ה-MCP לקריאה בלבד של המערכת, שאינו מוגבל ללקוח אחד, וברשימת העובדים שלו הם מגיעים כפי שהוקלדו — גם סוד מזוהה, זה שמוסר לפני הקריאה בשיחה. הרשימה אינה המקום היחיד: בממשק הזה השם מגיע כך בכל מקום שנושא אותו, ואף אחד מהם אינו מוסר אותו; התפקיד מוסר במקום אחד כזה בלבד.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 48): `intro_text`'s own destination. The
// clause above is a CLOSED four-member enumeration and it is right to be - those four are what
// `conversation_service.persona_of` hands to converse() - but `intro_text` is collected one box
// above it at the same step and is not a member, so the one identity field the platform PUBLISHES
// reads as outside the egress set. Measured:
//
//   * `_post_intro_if_any` (conversation_service.py:222-233) posts it as a ConversationMessage
//     with `message_type="system"`, authored by the employee's own participant, `content=intro
//     [:2000]` - and this box clamps at exactly 2000, so it is verbatim in every reachable case.
//     Two call sites: `create_conversation` (the direct-target arm) and the manager handoff, the
//     latter behind `if existing is None`, i.e. once per conversation per employee.
//   * No redaction on that path: `conversation_service.py` holds ZERO `redact` sites, against
//     same-symbol controls of 4 in `direct_llm_analyst.py` and 4 in `employee_workspace_v2.py` -
//     and 2 of this column's 5 reads DO redact it (`employee_workspace_v2.py:197`,
//     `employee_builder_v3.py:181`), which is the platform stating the column may hold one.
//   * It reaches NO model. `_recent_transcript` filters
//     `message_type.in_(("operator_message", "human_response"))`, so a `system` row never enters
//     the text handed to converse(). This REFUTES pass 47's own parenthetical ("intro_text
//     reaches a model too, transitively ... _recent_transcript folds it into the next turn");
//     that pass's conclusion - it is outside the unchecked-fields gap - holds for a different
//     reason, since it is one of the three validated fields AND it reaches no provider.
//   * Audience is internal: no customer egress of conversation content
//     (`send_customer_message` / `customer_email` / `send_email` are 0 in both
//     `conversation_service.py` and `routers/conversations.py`). Reachable only after activation,
//     because `create_conversation` refuses a disabled agent 422 and this wizard creates the row
//     `is_enabled=False` (pass 25).
//
// Arms on this axis before this pass: ZERO, anywhere. The box's label ("משפט פתיחה בשיחה") names
// the venue and not that the platform posts it there itself, unreviewed and unredacted.
export const INTRO_TEXT_PUBLICATION_NOTICE =
  "משפט הפתיחה אינו נשמר לתצוגה בלבד: כשהעובד מצטרף לשיחה המערכת מפרסמת אותו שם כהודעה משלו — אוטומטית, בלי אישור נוסף, וכפי שהוקלד, בלי שמוסר ממנו סוד מזוהה. לספק המודל הוא אינו נשלח.";

// ─── DIGEMP-141 pass 53: role_title's third "writer" is not a writer at all ──────────────────
// Pass 52 ran the write-set census on one field (`allowed_tools`) and handed over the other
// fields. Run on `role_title` the census returns a NEGATIVE on writers — the step-1 card seeds it
// guarded (`f.role_title || preset.role_title || ""`), the identity box is self-evident, the
// identity reset clears it under its own label — and a defect one layer over: the row that
// REPORTS the field fell back to a value nothing ever wrote into it.
// `preview-persona`'s `תפקיד` row was `form.role_title || preset?.label || "לא הוגדר תפקיד"`, so
// with the field empty and a role template attached the confirmation screen reported the
// TEMPLATE's label as the employee's role. Measured by rendering it (`role_preset: "custom"`,
// nothing typed): the section's four values read
// `דנה | מותאם אישית | ⚙️ מותאם אישית | כל הלקוחות` — the same string twice, once as the role and
// once, correctly, as the template one row below.
//
// `role_preset` is a wizard-only field: it is not in the create payload, and `role_preset` occurs
// 0 times in the .py files under backend/app against a same-symbol control of 120 occurrences
// over 39 files for `role_title`. So the reported role was a value the created row cannot carry,
// and
// `payload.role_title` is `form.role_title.trim() || undefined` — nothing is sent. Reachable in
// two clicks (the `custom` card seeds `role_title: ""`), and from any of the other eight cards via
// `איפוס פרטי זהות`, which clears `role_title` and leaves `role_preset` attached.
//
// What the created row then does NOT have, measured against the code that decides:
//   * `conversation_service.score_agent_for_message` puts role title and name in ONE 2-weight
//     term set (`{t for t in terms if t in role or t in name}`). Agent(role_title="diagnostics
//     specialist") scores 4 on "need help from the diagnostics specialist"; the same agent with
//     `role_title=None` scores 0, and `_select_agents_for_routing` ranks only `score > 0`.
//   * `persona_of` (4 keys, "the only agent attributes ever handed to converse()") carries
//     `role_title`, and `helpdesk_analyst.render_persona_context` drops the role from the identity
//     block entirely: `אתה Dana — עובד דיגיטלי...` instead of `אתה Dana, diagnostics specialist`.
// The honest arm was already the third member of that very expression, and the template is
// already rendered under its own `תבנית` label one row below, so the fix loses no information:
// the fallback is dropped (probe P1 red 0 of 297 — nothing depended on it) and the no-role state
// is stated. Not degraded and no readiness row added: `AgentCreate.role_title` is optional and
// skipping the identity step is a legitimate draft (§21.6).
//
// Three clauses, because the middle one has an arm the other two do not: step 1's cards are
// optional (`canAdvance` requires only the name), so with `role_preset` null a sentence about "the
// template chosen in the first step" names a template nobody picked. A first draft welded all
// three into one constant and was false in exactly that state -- the sign-flipped twin of the
// defect, caught by reading the rendered words rather than by any probe, since product and
// expectation would have carried the same bytes. Probe N7 restores the unconditional form.
export const ROLE_TITLE_UNSET_LEAD =
  "לא הוזן תפקיד, ולכן רשומת העובד תיווצר בלי תפקיד.";
export const ROLE_TITLE_UNSET_TEMPLATE_CLAUSE =
  "שם תבנית התפקיד שנבחרה בשלב הראשון אינו נשמר כתפקיד ואינו נשלח ביצירה כלל.";
export const ROLE_TITLE_UNSET_RANKING_CLAUSE =
  "בדירוג העובד לפנייה התפקיד והשם נמצאים באותו משקל, ולכן בלי תפקיד ההתאמה הזאת נשענת על השם בלבד.";

// The §28 governed-domain vocabulary (AI_HANDOFF.md §28), the closed set a requested skill domain
// can be recognized against. Anything outside it is recorded as an unrecognized request whose
// mapping is unknown — never silently dropped, never shown as approved.
const GOVERNED_SKILL_DOMAINS = [
  { id: "endpoint security",  label: "אבטחת נקודות קצה" },
  { id: "fortigate",          label: "FortiGate" },
  { id: "microsoft entra",    label: "Microsoft Entra" },
  { id: "windows networking", label: "רשתות Windows" },
];

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: this fold has to be the SERVER's fold, because
// the server is what decides the outcome this surface predicts. skill_assignment_state's
// `_canonical_domain_label` is `" ".join(unicodedata.normalize("NFKC", raw).lower().split())` and
// the classification runs against the same closed set (skill_registry.SKILL_DOMAINS). A weaker
// local fold does not fail safe: `trim().toLowerCase()` alone leaves "endpoint  security" (a
// stray double space), "windows\u00a0networking" (an NBSP, what pasting out of a ticket or a
// document produces) and any NFKC-foldable variant classified as OUTSIDE the vocabulary — so the
// pre-create surface tells the operator their request's "mapping is not known" and degrades the
// verdict, for intent the server goes on to APPLY as a governed candidate record. NFKC first,
// then lower, then collapse, in the server's order; `split(/\s+/).filter(Boolean)` is Python's
// bare `.split()`. Pinned against the server end in backend/tests/test_employee_builder_v3.py.
const canonicalSkillDomainLabel = (domain) =>
  String(domain == null ? "" : domain)
    .normalize("NFKC")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

const isGovernedDomain = (domain) => {
  const folded = canonicalSkillDomainLabel(domain);
  return GOVERNED_SKILL_DOMAINS.some((d) => d.id === folded);
};

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 50): the PHANTOM CUSTODIAN. The
// `preview-specialist` section is titled "חבילת מומחה", and its closing sentence was the only
// statement anywhere in the wizard about how a pack comes to exist. It denied the consequence at
// decision time (true) and then assigned it to a later, permission-gated action: "חבילות מאושרות
// מקושרות מעמוד העובד לאחר היצירה, בהרשאת מנהל". Measured:
//
//   * THE DEFERRAL NAMES AN ACTION THAT EXISTS NOWHERE. `backend/app/routers` holds 0 expert-pack
//     endpoints, `backend/app/models` holds no pack table at all, `services/api.js` holds 0
//     pack-named functions, and `services/expert_pack_catalog.py` holds 0 of db.add / db.commit /
//     db.flush / db.delete / update( / insert( / require_role / require_agent_permission /
//     Depends. Its own docstring says "no persisted pack registry" exists, which is why the
//     projection's `version` is null. Nothing is saved, nothing is approved, nobody links
//     anything, and there is no admin gate to pass. `specialist_packs.py` is the same shape.
//   * THE CONSEQUENCE ARRIVES BY ITSELF. `employee_workspace` embeds
//     `build_expert_pack_catalog(agent, db, tools=...)`, and `_build_pack` returns a pack whenever
//     `domain in specialty_domains`, so the declaration ALONE produces it. Measured through the
//     real projection at the default tier_1 with no connection and no article: [] gives no packs,
//     ["ESET"] gives ['eset'], ["Microsoft 365"] gives ['m365'], and this box's own placeholder
//     typed verbatim gives ['eset', 'm365']. The pack is on the employee page the first time it
//     is opened, with its read/recommend/execute blocks.
//   * THE SECTION SPOKE THE WRONG CLOSED VOCABULARY. Its recognition line reports
//     GOVERNED_SKILL_DOMAINS, and 0 of those four produce a pack. The catalog's own set is
//     DOMAIN_ORDER plus 14 aliases, named at 0 sites in the section (0/0 on eight vocabulary
//     needles) even though two of them are the placeholder's own tokens.
//
// Honest bound, and the reason nothing is degraded here: the pack GRANTS nothing. Its pinned hard
// rule is that "the read/recommend/execute partition is exactly a regroup of the entitled-tools
// projection, never a wider set", so every tool it lists is one the gate already permits at the
// tier and allowlist chosen here. A disclosure defect, not a permission change.
//
// Mirrored, rather than stated in general terms, because the arm has to name THIS declaration's
// domains and the catalog's vocabulary is rendered at no site in the wizard.
// `expert_pack_catalog._token` is `re.sub(r"[\s-]+", "_", value.strip().lower())` and
// `_domain_of` is a bare alias lookup. The alias map, the fold and the claim that every alias
// value is a label key (so the label lookup needs no `||` fallback, which would be the §8.2
// shape) are all pinned off disk in backend/tests/test_employee_builder_v3.py.
const EXPERT_PACK_DOMAIN_ORDER = ["helpdesk", "eset", "acronis", "m365"];

const EXPERT_PACK_DOMAIN_LABELS = {
  helpdesk: "Helpdesk",
  eset: "ESET PROTECT",
  acronis: "Acronis",
  m365: "Microsoft 365 / Entra",
};

const EXPERT_PACK_ALIASES = {
  helpdesk: "helpdesk", help_desk: "helpdesk", service_desk: "helpdesk",
  eset: "eset", eset_protect: "eset",
  acronis: "acronis", acronis_backup: "acronis",
  m365: "m365", microsoft_365: "m365", microsoft365: "m365",
  entra: "m365", entra_id: "m365", azure_ad: "m365", microsoft_365_entra: "m365",
};

// An unrecognized token yields null, never a domain: the conservative direction, since the only
// thing this decides is whether the surface CLAIMS a pack will appear.
const expertPackDomainOf = (raw) =>
  EXPERT_PACK_ALIASES[
    String(raw == null ? "" : raw).trim().toLowerCase().replace(/[\s-]+/g, "_")
  ] || null;

export function declaredExpertPackDomains(specialties) {
  const hit = new Set();
  for (const entry of specialties || []) {
    const domain = expertPackDomainOf(entry);
    if (domain) hit.add(domain);
  }
  return EXPERT_PACK_DOMAIN_ORDER.filter((d) => hit.has(d));
}

export const EXPERT_PACK_DERIVATION_NOTICE =
  "חבילת מומחה אינה משויכת לעובד בשום שלב: אף אחד אינו מקשר אותה, אין פעולה כזאת בעמוד העובד, אין חבילה שנשמרת ואין חבילה שמאושרת. היא תצוגה נגזרת שנבנית בכל קריאה ומופיעה מעצמה עבור כל תחום שקטלוג החבילות מזהה בהצהרה — מיד לאחר היצירה, בלי אישור ובלי פעולת מנהל. היא אינה מעניקה כלי, הרשאה או אישור: מה שמוצג בה הוא בדיוק מה שהעובד מורשה לו לפי הדרגה והרשימה שנבחרו כאן.";

const ROLE_PRESETS = [
  {
    id: "helpdesk",
    icon: "🎫",
    avatar_key: "🎫",
    avatar_color: "blue",
    label: "IT Helpdesk",
    desc: "ניתוח קריאות, איסוף עדויות, הצעת פתרונות",
    role_title: "טכנאי תמיכה טכנית",
    specialties: ["תמיכת משתמשים", "אבחון תקלות", "ניתוח קריאות", "מעקב לוגים"],
    model: "claude-sonnet-4-6",
    autonomy: "approval_required",
    permission_tier: "tier_1",
    systemPrompt: `You are an expert IT Helpdesk technician agent for Prodigy IT.

Your role:
- Analyze IT support tickets from context (title, description, customer history)
- Gather deterministic diagnostic evidence via approved tools (check_executable, TeamViewer lookup)
- Identify the likely root cause with supporting evidence
- Propose a clear, step-by-step resolution plan
- Draft a professional customer-facing reply (Hebrew, respectful, concise)
- Flag all state-changing actions for technician approval

Rules:
- Never guess; acknowledge uncertainty explicitly
- Separate facts from inferences from hypotheses
- All remote actions require technician approval before execution
- No sensitive data in logs or analysis output`,
  },
  {
    id: "security",
    icon: "🛡️",
    avatar_key: "🛡️",
    avatar_color: "violet",
    label: "שומר פלטפורמה ואבטחה",
    desc: "בקרת בריאות שרת, גיבויים, אנומליות אבטחה",
    role_title: "שומר פלטפורמה ואבטחה",
    specialties: ["אבטחת מידע", "גיבויים", "ניטור שרתים", "אנומליות התחברות"],
    model: "claude-sonnet-4-6",
    autonomy: "supervised",
    permission_tier: "tier_1",
    systemPrompt: `You are a Security and Platform Guardian agent for Prodigy IT.

Your responsibilities:
- Monitor server health: disk, RAM, uptime, running services
- Verify backup completion, retention policies, and restore readiness
- Detect authentication anomalies and failed login patterns
- Identify firewall/listener drift and unauthorized config changes
- Check dependency/package advisories for known vulnerabilities
- Audit agent run failures and unusual patterns
- Produce structured findings with severity, evidence, and remediation proposals

Rules:
- Read-only evidence gathering only; propose remediation for technician approval
- Severity: CRITICAL (immediate action) / HIGH / MEDIUM / INFO
- Never automatically apply remediation without explicit approval`,
  },
  {
    id: "bug_hunter",
    icon: "🐛",
    avatar_key: "🐛",
    avatar_color: "rose",
    label: "ציד באגים",
    desc: "זיהוי שגיאות חוזרות, ניתוח לוגים, הצעת תיקונים",
    role_title: "צייד באגים",
    specialties: ["ניתוח לוגים", "רגרסיות", "תיקוני קוד", "מעקב פריסות"],
    model: "claude-sonnet-4-6",
    autonomy: "approval_required",
    permission_tier: "tier_1",
    systemPrompt: `You are a Bug Hunter and Self-Healing Engineering Agent for Prodigy IT.

Your responsibilities:
- Identify recurring application errors and regressions from logs and monitoring
- Correlate errors with recent deployments and code changes
- Analyze the root cause with supporting log evidence
- Propose a minimal safe code fix (never deploy automatically)
- Suggest a focused regression test to prevent recurrence
- Draft a structured finding report for engineering review

Rules:
- Never silently deploy fixes to production
- All code changes require human review and approval
- Include evidence: log excerpts, stack traces, reproduction steps
- Distinguish intermittent issues from systematic regressions`,
  },
  {
    id: "tech_scout",
    icon: "🔍",
    avatar_key: "🔍",
    avatar_color: "teal",
    label: "סיור טכנולוגיות",
    desc: "ביקורת גרסאות חבילות, CVE, המלצות שדרוג",
    role_title: "סייר טכנולוגיות",
    specialties: ["עדכוני גרסאות", "פגיעויות CVE", "המלצות שדרוג"],
    model: "claude-sonnet-4-6",
    autonomy: "supervised",
    permission_tier: "tier_1",
    systemPrompt: `You are a Technology Scout for a FastAPI/React/PostgreSQL helpdesk system.

When invoked with a technology catalog snapshot, perform a structured review:

1. **Dependency freshness** — identify packages that are significantly outdated
   (major version behind or known security-fix releases). Focus on direct
   dependencies only; do not enumerate transitive packages.

2. **Security advisories** — surface any known CVEs or advisories for
   packages in the catalog if you are aware of them as of your knowledge cutoff.

3. **Runtime upgrades** — note if the Python, Node.js, or PostgreSQL runtime
   version is approaching end-of-life.

4. **Recommended actions** — produce a concise prioritized action list:
   CRITICAL → HIGH → MEDIUM → LOW.

5. **New tooling opportunities** — briefly mention (max 3) relevant open-source
   tools that could benefit this IT helpdesk but are not yet in the stack.

Keep the report concise. Avoid boilerplate.`,
  },
  {
    id: "skills_evaluator",
    icon: "🎯",
    avatar_key: "🎯",
    avatar_color: "amber",
    label: "מעריך מיומנויות",
    desc: "ניתוח ביצועי סוכנים, זיהוי חולשות, הצעות שיפור",
    role_title: "מעריך מיומנויות",
    specialties: ["ביצועי סוכנים", "זמן טיפול", "עלות-תועלת", "הצעות שיפור"],
    model: "claude-sonnet-4-6",
    autonomy: "supervised",
    permission_tier: "tier_1",
    systemPrompt: `You are a Skills Evaluator for an AI agent management system.

When given a performance report for one or more agents, perform a structured evaluation:

1. **Resolution analysis** — examine resolution rate, average time-to-close,
   and failure patterns. Identify agents that underperform the fleet average.

2. **Category strengths and gaps** — for each agent, note which ticket
   categories it handles well vs. where it consistently fails or escalates.

3. **Cost efficiency** — flag agents whose cost-per-resolution is significantly
   above average without a corresponding quality benefit.

4. **Improvement recommendations** — for each underperforming agent, suggest
   specific system prompt improvements, tool additions, or autonomy level changes.

5. **Training data gaps** — identify ticket categories where no agent has been
   tested, suggesting areas to build evaluation cases.

Be specific and actionable. Cite data from the report.`,
  },
  {
    id: "business_continuity",
    icon: "🏥",
    avatar_key: "🏥",
    avatar_color: "green",
    label: "המשכיות עסקית",
    desc: "אימות תוכניות DR, בדיקת RTO/RPO, תיעוד נהלי חירום",
    role_title: "אחראי המשכיות עסקית",
    specialties: ["תוכניות DR", "RTO/RPO", "בדיקות שחזור", "נקודות כשל"],
    model: "claude-sonnet-4-6",
    autonomy: "supervised",
    permission_tier: "tier_1",
    systemPrompt: `You are a Business Continuity Guardian for Prodigy IT.

Your responsibilities:
- Verify backup age and restore readiness against RTO/RPO targets
- Check that disaster recovery runbooks are up-to-date and accessible
- Identify single points of failure in critical IT services
- Validate that failover procedures have been tested recently
- Review incident post-mortems for recurring patterns
- Confirm offsite/cloud backup replication is healthy

Output format:
1. **Continuity Status** — READY / AT_RISK / CRITICAL
2. **RTO/RPO Assessment** — last successful restore test, estimated recovery time
3. **DR Gaps** — specific gaps with severity: CRITICAL / HIGH / MEDIUM
4. **Recommended Actions** — prioritized remediation steps

Rules:
- Read-only evidence gathering only; no automated remediation
- All findings require technician review before action
- Flag CRITICAL issues immediately for management escalation`,
  },
  {
    id: "skills_scout",
    icon: "🔭",
    avatar_key: "🔭",
    avatar_color: "slate",
    label: "גלאי מיומנויות",
    desc: "זיהוי פערי כיסוי בסוכנים, המלצות לפריסת סוכנים חדשים",
    role_title: "גלאי מיומנויות",
    specialties: ["פערי כיסוי", "ניתוח עומסים", "פריסת סוכנים"],
    model: "claude-sonnet-4-6",
    autonomy: "supervised",
    permission_tier: "tier_1",
    systemPrompt: `You are a Skills Scout for the Prodigy AI agent fleet.

When given the fleet skills report and recent ticket data, perform a gap analysis:

1. **Coverage gaps** — ticket categories with high volume but no assigned agent,
   or categories where the assigned agent has a low completion rate.

2. **Underutilized agents** — agents with zero or very few runs in the past 30 days
   that could be better deployed elsewhere.

3. **New agent recommendations** — based on ticket patterns, suggest 1-3 new agent
   roles that would reduce manual technician workload. For each recommendation:
   - Ticket category and estimated volume
   - Proposed autonomy level
   - Key system prompt requirements

4. **Skill depth analysis** — agents handling many ticket categories superficially
   vs. agents that could be more specialized for better accuracy.

5. **Fleet health score** — overall assessment: HEALTHY / NEEDS_IMPROVEMENT / CRITICAL.

Be specific. Reference ticket numbers and agent names from the data.`,
  },
  {
    id: "self_healing",
    icon: "🔧",
    avatar_key: "🔧",
    avatar_color: "blue",
    label: "תיקון עצמי",
    desc: "זיהוי שגיאות חוזרות בקוד ותשתית, הצעת תיקונים אוטומטיים",
    role_title: "מהנדס תיקון עצמי",
    specialties: ["שגיאות חוזרות", "סחף תצורה", "תיקון ואימות"],
    model: "claude-sonnet-4-6",
    autonomy: "approval_required",
    permission_tier: "tier_2",
    systemPrompt: `You are a Self-Healing Engineering Agent for Prodigy IT infrastructure.

Your mission: detect recurring errors, configuration drift, and degraded services — then propose or apply minimal safe fixes.

Workflow:
1. **Error detection** — scan logs, agent run failures, alert history, and backup status.
   Identify patterns: same error 3+ times in 24h, service restart loops, backup failures.

2. **Root cause analysis** — for each pattern, classify as:
   - CONFIG: wrong setting, missing env var, stale credential
   - RESOURCE: disk full, memory pressure, CPU saturation
   - DEPENDENCY: service crash, network partition, auth failure
   - CODE: recurring exception traceback, unhandled edge case

3. **Remediation proposal** — for each root cause, propose one of:
   - AUTO_FIX: low-risk config change you can apply immediately (approval_required)
   - TICKET: create a helpdesk ticket with full context
   - ESCALATE: alert on-call with evidence package

4. **Apply with approval** — for AUTO_FIX proposals, wait for technician approval before executing.
   Log all actions in the AgentAction audit trail.

5. **Verify** — after fix, re-check the signal that triggered the alert.
   Report RESOLVED or PERSISTENT with updated evidence.

Rules:
- Never modify production databases or delete data without explicit technician approval.
- All state-changing actions require approval_required flag.
- Be specific: include exact file paths, config keys, and expected vs actual values.
- If uncertain, escalate with full context rather than guessing.`,
  },
  {
    id: "custom",
    icon: "⚙️",
    avatar_key: "",
    avatar_color: "",
    label: "מותאם אישית",
    desc: "הגדרה ידנית מלאה",
    role_title: "",
    specialties: [],
    model: "claude-sonnet-4-6",
    autonomy: "approval_required",
    permission_tier: "tier_1",
    systemPrompt: "",
  },
];

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: what an option here DOES is decided by
// autonomy_policy.check_action, not by this list, and the three labels described a ladder that
// function does not implement. Measured against it and pinned in
// backend/tests/test_employee_builder_v3.py:
//   * `approval_required` and `supervised` are handled by ONE arm ("if autonomy_mode in
//     ('supervised', 'approval_required')") and `autonomy_mode` is read by exactly three
//     predicates, so the two are the same decision for every input -- 0 of 744 (tool x tier x
//     crisis x allowlist) combinations differ, and models/agent.py calls `approval_required`
//     "legacy -- equivalent to supervised". The old labels made the first strictly safer than the
//     second ("the safest -- the technician approves every step") and the pilot note below
//     recommended it on that strength.
//   * a read-only Registry tool returns at the gate's step 3, "allowed at all autonomy levels",
//     BEFORE the mode is consulted -- so no option here can make a read approval-bound. At the
//     default tier_1 that is all 15 reachable tools, each running with no approval under the
//     option that promised one for every action.
//   * the permissive arm of `semi_autonomous` reaches only a REGISTERED, non-read-only tool that
//     declares `approval_required=False`; the live Registry has 0 of those (16 of 16 non-read-only
//     definitions are approval-bound at every mode), so today the choice changes no outcome at
//     all. That count is live in the note below rather than written here, so it cannot go stale.
// The operator's pick is still recorded verbatim on the employee row; only the promises changed.
const AUTONOMY_OPTIONS = [
  { value: "approval_required", label: "אישור לשינוי מצב (שם היסטורי)", desc: "זהה למפוקח — אותה זרוע בבדיקת המדיניות; אינה מוסיפה אישור על קריאה" },
  { value: "supervised",        label: "מפוקח",                        desc: "כל פעולת שינוי-מצב דורשת אישור; כלי לקריאה בלבד מתבצעים ללא אישור" },
  { value: "semi_autonomous",   label: "חצי אוטונומי",                 desc: "כמו מפוקח, פרט לכלי שינוי-מצב שהמרשם מסמן כלא-דורש-אישור" },
];

// One source for both surfaces that state what the pick does -- the limits step and the pre-create
// preview -- so the words before the irreversible action and the words inside the confirmation
// cannot drift apart. The equivalence is a property of check_action itself and needs no catalog, so
// it renders even when the tool catalog failed to load: a failed fetch may not shadow a
// determinate truth it has no bearing on.
export const AUTONOMY_EQUIVALENCE_NOTE =
  "שתי האפשרויות הראשונות הן אותה מדיניות בדיוק — בדיקת המדיניות בצד השרת מטפלת בשתיהן בזרוע אחת, והראשונה מתועדת כשם היסטורי של מפוקח.";

export const AUTONOMY_EFFECT_UNKNOWN =
  "לא ידוע — קטלוג הכלים לא נטען, ולכן לא ניתן לומר על אילו כלים הבחירה הזו משפיעה.";

// Counts only, over the projection's own reachable set. Returns null when there is no projection,
// so a caller must render the unknown/loading arm rather than a confident zero.
export function autonomyEffectSentence(projection) {
  if (!projection) return null;
  return [
    `${projection.autonomyExempt} מהכלים הנגישים בטיוטה זו הם לקריאה בלבד ומאושרים בכל רמת אוטונומיה — הם ירוצו ללא אישור בכל אחת מהאפשרויות.`,
    projection.autonomyBound > 0
      ? `${projection.autonomyBound} כלי שינוי-מצב נגישים דורשים אישור בכל אחת מהן.`
      : null,
    projection.autonomySensitive === 0
      ? "בקטלוג הנוכחי אין כלי שהבחירה הזו משנה עבורו את התוצאה — הבחירה נרשמת ברשומת העובד ותיאכף אם ייווסף כלי כזה."
      : `${projection.autonomySensitive} כלי שינוי-מצב נגישים אינם דורשי-אישור במרשם — רק עבורם הבחירה הזו משנה את התוצאה.`,
  ].filter(Boolean).join(" ");
}

const PERMISSION_TIER_OPTIONS = [
  {
    value: "tier_1",
    badge: "Tier 1",
    label: "קריאה בלבד",
    desc: "ברירת מחדל — ניתוח, שאילתות, והמלצות בלבד. אין ביצוע, כתיבה, או מחיקה.",
    disabled: false,
  },
  {
    value: "tier_2",
    badge: "Tier 2",
    label: "תפעולי",
    desc: "פעולות מוגדרות עם אישור אנושי לפני כל ביצוע.",
    disabled: false,
  },
  {
    value: "tier_3",
    badge: "Tier 3",
    label: "מוגבה",
    desc: "פעולות רגישות עם אישור חזק (Windows Hello / FIDO2). לא זמין בשלב הפיילוט.",
    disabled: true,
  },
];

// Stay in lockstep with schemas/agent.py AVATAR_COLORS — the palette keys the backend accepts.
const AVATAR_COLOR_OPTIONS = [
  { value: "blue",   label: "כחול" },
  { value: "violet", label: "סגול" },
  { value: "green",  label: "ירוק" },
  { value: "amber",  label: "ענבר" },
  { value: "rose",   label: "ורוד" },
  { value: "teal",   label: "טורקיז" },
  { value: "slate",  label: "אפור" },
];

const AVAILABILITY_BADGE = {
  available: "bg-green-100 text-green-700",
  busy: "bg-amber-100 text-amber-700",
  away: "bg-gray-200 text-gray-600",
  offline: "bg-gray-200 text-gray-500",
};

const MODEL_OPTIONS = [
  { value: "claude-sonnet-4-6",  provider: "Anthropic", label: "Claude Sonnet 4.6",  desc: "מהיר, חסכוני, מעולה לרוב המקרים" },
  { value: "claude-opus-4-8",    provider: "Anthropic", label: "Claude Opus 4.8",    desc: "הכי חזק — לפעולות קריטיות" },
  { value: "claude-haiku-4-5-20251001", provider: "Anthropic", label: "Claude Haiku 4.5", desc: "הכי מהיר וזול — לבדיקות תכופות" },
];

// ─── DIGEMP-141 pass 79: what the systems step says each connector CAN DO ────
// ENV_TOOLS is a DATA literal, so pass 31's 'preset data as CLAIMS' census stopped at
// ROLE_PRESETS/PERSONA_PRESETS and these six operator-visible strings were never read. They
// are also the population pass 78 named. Run per TREE with a round-trip control (the frontend
// stores Hebrew raw, the owned backend test stores it as escapes), ENV_TOOLS has 0 literal
// assertions across 204 frontend and 633 backend test files against symbol references of 5 and
// 14, so every one of them rendered byte-identically under any reword.
//
// Read against the registry each id resolves to:
//   * teamviewer caps=(device_lookup, attended_session) — its desc is EXACTLY those two, so
//     the file already knows how to describe a connector by what it declares. That is the
//     control that makes the other two zeros mean something.
//   * openclaw caps=(endpoint_diagnostics, log_collection), read_only=True. Its desc says
//     'executing commands' and names check_executable (a real tier_1 read-only tool) plus
//     system.which (the command check_executable HARDCODES; system.run is never reachable,
//     stated in three modules). Over-stated in the ALARMING direction over a read-only
//     adapter, which pass 27 makes the safe one, and endpoint_diagnosis is the sibling it
//     omits. Measured and deliberately UNCHANGED, so pass 80 weighs it rather than
//     re-measuring it.
//   * ms365 resolves to m365_graph, caps=(directory_security_read, user_security_inspection)
//     — and the desc named NEITHER. It named knowledge-document search and calendar/mail
//     access. This is the ONE id of the three for which the publish creates a per-employee,
//     credential-bearing AgentConnection (agent_connection_backed=True) whose tenant_id,
//     client_id_app and secret_ref an admin then provisions, so this sentence was the only
//     statement of what that Entra app registration is FOR.
//
// Measured over the 31-definition catalog with live controls, so the zeros mean something
// (device 13 / ticket 11):
//   * calendar tools: 0.
//   * mail-touching tools: 4 (close_ticket, escalate, send_customer_message,
//     send_email_to_customer) and 0 of them read_only. All four are OperationType.WRITE at
//     tier_2/tier_3 and customer-facing, so the catalog's only mail capability is the
//     OPPOSITE kind to the one the '(read only)' parenthetical reassures about.
//   * search_kb IS real ('Search the knowledge base / SharePoint for relevant articles') but
//     it is read_only and allowed at ALL THREE tiers: entitled_tools_for reaches 15/26/31 and
//     every one of those sets holds it. The one named capability that exists is therefore
//     reachable without this selection at all.
//   * the two capabilities m365_graph DOES declare have 0 functional consumers. Every
//     occurrence of either anchor outside the definition is a documentation string in
//     m365_specialist's evidence payload, which records the pair as 'declared_not_wired',
//     against a live control: .capabilities is read at 22 sites and BRANCHED on at two
//     (specialist_packs, model_capability_registry). action_executor, the one module that
//     executes a tool, names m365_graph 0 times and AgentConnection 0 times, so the
//     customer-facing Graph send runs on the deployment-wide AZURE_CLIENT settings and not
//     on this record.
//
// The severity argument is the file's own OTHER sentence about this vocabulary: the
// tier/preset note below names the only mail action the catalog holds correctly — a
// customer-facing SEND that one tier_2 click grants (pass 31) — so the wizard described the
// employee's mail reach in opposite terms at two sites, and the false one sat on the card an
// operator selects in order to obtain it.
//
// Closed by correcting the one false clause rather than by adding a notice: the capabilities
// named are the two the connector declares, the true '(read only)' parenthetical is kept
// (read_only=True), and the not-wired fact is the authority's own. Nothing is degraded — no
// readiness row, no verdict arm and no publish scope reads this string, and the step's own
// footnote already says a selection here grants nothing.
// ─── DIGEMP-141 pass 79 correction: the UNCORRECTED SECOND AUDIENCE ──────────
// The pass-79 delivery corrected the ms365 CARD and left the copy of the same claim that
// this very literal sends to the MODEL. One input, two durable landing places (pass 66):
// TOOL_NAMES is built from promptLabel and composeSystemPrompt appends its one configured-tools
// line, interpolating each selected id's promptLabel, to agents.system_prompt, which
// create_direct_llm_analyst PREPENDS to the base safety contract on every analysis run
// (pass 33/34). So the retired 'knowledge search' parenthetical survived at exactly 1 site
// repo-wide and it was the model-facing one, with 0 teeth in either tree: pass 66's pin
// counts how many entries DECLARE a promptLabel, never any value, and the delivery's own
// extractor reads id and desc only. Same direction as the defect it fixed -- over-stating
// this connection's reach -- and now mutually contradictory, since the card says no catalog
// tool uses the connection while the prompt line claimed a knowledge-search capability
// through it. The parenthetical now names the two capabilities m365_graph DECLARES, spelled
// so the pin derives them from CAP_DIRECTORY_SECURITY_READ / CAP_USER_SECURITY_INSPECTION
// rather than retyping them, and carries the not-wired fact the card already carries.
// 'SharePoint' is dropped from the MODEL-facing string only (knowledge search is search_kb,
// a catalog tool reachable at every tier without this selection) while the operator-facing
// `label` keeps it byte-identical: a product title the operator recognises is not a
// capability claim addressed to a model. Nothing is degraded -- no readiness row, no verdict
// arm and no publish scope reads either string.
const ENV_TOOLS = [
  {
    id: "openclaw",
    icon: "🦞",
    label: "OpenClaw Runtime",
    desc: "ביצוע פקודות בנקודות קצה (system.which, check_executable)",
    note: "נדרש הגדרת OPENCLAW_ADAPTER",
    promptLabel: "OpenClaw Runtime (remote command execution)",
  },
  {
    id: "teamviewer",
    icon: "🖥️",
    label: "TeamViewer",
    desc: "חיפוש מכשיר לפי שם/IP, ייזום סשן מרחוק עם אישור",
    note: "נדרש TEAMVIEWER_API_TOKEN",
    promptLabel: "TeamViewer (remote desktop access)",
  },
  {
    id: "ms365",
    icon: "☁️",
    label: "Microsoft 365 / SharePoint",
    desc: "מצב אבטחה ב-Entra ובדיקת אבטחה למשתמש (קריאה בלבד) — ואין בקטלוג הכלים כלי שמשתמש בחיבור הזה בגרסה זו",
    note: "נדרש הגדרת Azure/Graph",
    promptLabel: "Microsoft 365 / Entra (directory security read, user security inspection; read-only, no catalog tool uses this connection)",
  },
];

const TOOL_NAMES = Object.fromEntries(ENV_TOOLS.map((t) => [t.id, t.promptLabel]));

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 22): the ONE composition of the operating
// prompt that is actually sent. handleNext appends the configured-tools line to the operator's
// text, so `form.system_prompt` is neither the value AgentCreate bounds at SYSTEM_PROMPT_MAX_CHARS
// nor the value the server's own role domain counts (`len(draft.system_prompt or "")`). Three
// surfaces state a character count and all three count the DECLARED text, which is the honest
// number for the sentence they make; anything reasoning about what the server will ACCEPT has to
// call this instead, which is why the composition moved out of handleNext rather than being
// duplicated beside it.
export function composeSystemPrompt(form) {
  const declared = (form.system_prompt || "").trim();
  const tools = form.tools || [];
  if (tools.length === 0) return declared;
  const toolList = tools.map((t) => TOOL_NAMES[t] || t).join(", ");
  return (declared ? declared + "\n\n" : "") + `Configured tools for this environment: ${toolList}.`;
}

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 22): the CLAMPED MIRROR. `_valid_specialties`
// raises on two independent conditions - more than SPECIALTY_MAX_ITEMS entries, or any entry over
// SPECIALTY_MAX_CHARS - and the wizard mirrored each one wrong, in opposite directions:
//
//   * the item-count arm is mirrored as a SILENT CLAMP. utils/agentPayload.parseSpecialties ends
//     `.slice(0, 20)`, so a 25-tag declaration never reaches the server's refusal - five declared
//     specialties are discarded on the way into the request, and every surface that renders
//     specialties renders the parser's OUTPUT, so the tail is not shown as dropped, it is not
//     shown at all. The operator's own text is gone with no statement anywhere: the same shape as
//     the typed-but-unadded skill draft, and the acceptance's "never silently drops" half.
//   * the item-length arm is not mirrored anywhere. A 41-character specialty is sent, and
//     `AgentCreate` refuses the entire create from the schema alone - before any lookup, like the
//     ungoverned skill domain of pass 19 - while the confirmation screen rendered that specialty
//     as a declared chip and the verdict read "מוכן ליצירה", in green. What the operator then
//     sees is api.js's flattened pydantic sentence, in English, after losing the attempt.
//
// Finishing the census over the same population found two more bounds named nowhere in this file:
// `name` at 255 and `system_prompt` at 10k - the latter against the COMPOSED value, since the
// wizard's own appended tools line is what can carry a draft over the edge while all three
// rendered counts show it under. The parser stays untouched: it is shared with the employee edit
// modal and is not this task's to change, so the declared list is re-split here with the same
// separators and `sent` is taken from the parser itself, so the two cannot disagree about what is
// sent and the tail is exactly what it discards (pinned in both directions).
//
// Not mirrored, deliberately: the three secret-refusal validators on personality / intro_text /
// system_prompt. `contains_labelled_secret` is a security check and a weaker local copy of one
// does not fail safe - the fold divergences of passes 13 and 20 are what that costs - so the
// boundary is STATED on the readiness row and never re-decided here.
export function draftBoundViolations(form) {
  const declared = String(form.specialties_text || "")
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const sent = parseSpecialties(form.specialties_text);
  const nameChars = (form.name || "").trim().length;
  const sentPromptChars = composeSystemPrompt(form).length;
  return {
    nameChars,
    nameTooLong: nameChars > MAX_NAME_CHARS,
    sentPromptChars,
    promptTooLong: sentPromptChars > MAX_SYSTEM_PROMPT_CHARS,
    declaredSpecialtyCount: declared.length,
    // Only what is SENT can be refused: a 41-character tag in the discarded tail is a drop, not a
    // refusal, and reporting it as one would send the operator to shorten text the server never sees.
    overlongSpecialties: sent.filter((s) => s.length > MAX_SPECIALTY_CHARS),
    droppedSpecialties: declared.slice(MAX_SPECIALTIES),
  };
}

const RISK_CLS   = { 0: "bg-green-100 text-green-700", 1: "bg-blue-100 text-blue-700", 2: "bg-amber-100 text-amber-700", 3: "bg-red-100 text-red-700" };

const ENV_STATUS_META = {
  configured:     { label: "מוגדר במערכת",              cls: "bg-green-100 text-green-700" },
  not_configured: { label: "לא מוגדר במערכת",            cls: "bg-amber-100 text-amber-700" },
  unknown:        { label: "לא ידוע — הסטטוס לא נטען",   cls: "bg-gray-100 text-gray-500" },
  loading:        { label: "בודק…",                      cls: "bg-gray-100 text-gray-500" },
};

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: the axis that decides whether publishing a
// requested system CREATES anything for this employee -- which is NOT the axis the four
// ENV_STATUS_META labels above speak.
//
// `employee_builder_v3.resolve_environment_tool_key` classifies a requested system key against
// `connector_registry` and returns exactly one of three states: `supported` (an
// agent_connection_backed connector -- publishing writes the per-employee AgentConnection row
// through the existing connection contract), `platform_managed` (a registered platform-scope
// connector -- one deployment-wide integration, so there is NO connection record to create) and
// `unsupported` (not a registered system). Measured against the real registry, TWO of the three
// systems this wizard offers are platform-scope: openclaw and teamviewer resolve to
// `platform_managed`, and only ms365 (aliased to the m365_graph connector) resolves to
// `supported`. So for two of three selections the governed publish creates no connection record
// at all, by design and reported truthfully afterwards -- but before 141 nothing BEFORE the single
// irreversible action said which was which. The step-6 card badge, the pre-create preview badge,
// that section's own state chip and the `connections` readiness row all speak the
// DEPLOYMENT-CONFIGURATION axis (configured / not configured / unknown): each is true, and each
// answers a different question than "will my employee get this connection?". The step's footnote
// explains the taxonomy generically ("a supported system receives a connection record ... a
// platform-managed or unrecognized one is reported explicitly") without ever resolving which of
// the three offered systems is which, so the operator reads `ok` / "מוגדרות" / "all requested
// systems are configured" over a selection that will produce zero connection records. This is the
// pre-create half of the same acceptance the post-publish panel's PUBLISH_CONNECTION_STATE_META
// already satisfies -- that panel has an arm for `platform_managed`; this surface had none.
//
// Static because the classification is static: it reads connector registry *definitions*, not
// deployment state or any per-request value. Pinned against the server end -- set equality over
// the offered ids, each value compared to `resolve_environment_tool_key(id)["state"]` against the
// real registry -- in backend/tests/test_employee_builder_v3.py, so the wizard cannot drift from
// the resolver in either direction (a wizard-only `supported` promises a record the publish will
// not create; a wizard-only `platform_managed` denies one it will).
const SYSTEM_PUBLISH_SCOPE = {
  openclaw: "platform_managed",
  teamviewer: "platform_managed",
  ms365: "supported",
};

// An id with no classification is `unknown`, never the optimistic member: a surface that guessed
// `supported` would promise a connection record for a system the resolver may refuse. Unreachable
// while the parity pin holds (it asserts the key set equals the offered ids exactly), kept because
// a bundle served against a wizard whose ENV_TOOLS grew is a real rolling-deploy path.
const SYSTEM_PUBLISH_SCOPE_META = {
  supported: {
    label: "תיווצר רשומת חיבור פר-עובד",
    cls: "bg-blue-100 text-blue-700",
  },
  platform_managed: {
    label: "מנוהלת פלטפורמית — לא תיווצר רשומת חיבור",
    cls: "bg-gray-100 text-gray-600",
  },
  unsupported: {
    label: "אינה מוכרת — לא תיווצר רשומת חיבור",
    cls: "bg-amber-100 text-amber-700",
  },
  unknown: {
    label: "לא ידוע אם תיווצר רשומת חיבור",
    cls: "bg-gray-100 text-gray-500",
  },
};

const systemPublishScope = (toolId) =>
  Object.prototype.hasOwnProperty.call(SYSTEM_PUBLISH_SCOPE, toolId)
    ? SYSTEM_PUBLISH_SCOPE[toolId]
    : "unknown";

// ─── PROD-BUILDER-060: the pre-submit outcome forecast ───────────────────────
//
// The final review answers, for every requested item, what the governed publish is about to do
// BEFORE the operator presses the single creating action. The four operator-facing outcomes are
// the ones the audit's Builder closure names:
//
//   created          — a canonical record will be written for the employee;
//   requested_only   — the request is retained as intent, but no per-employee record is created
//                      (a platform-managed system is one deployment-wide integration; role text
//                      is guidance recorded on the Agent row);
//   pending_approval — a record will exist (or the deployment configuration is still required)
//                      but a human step is required before it is operative;
//   refused          — no canonical contract recognizes the request; nothing is created.
//
// `created` and `pending_approval` are not mutually exclusive: a governed Skill domain becomes a
// level-0 candidate record that still needs human evaluation, and a supported connection row is
// created with its required credential fields unset. Both chips render, because collapsing them
// into one label would hide either the record or the human step.
//
// These classifications are the same ones the server's intent map (services/employee_builder_v3
// `_skill_intent_item` / `_connection_intent_item`) computes before creation, and they use the
// same locally-pinned vocabulary constants (`isGovernedDomain`, `SYSTEM_PUBLISH_SCOPE`) that
// backend/tests/test_employee_builder_v3.py pins against the server end. This is a forecast of
// the publish, never a grant and never a claim that anything has already been created.
export const PUBLISH_FORECAST_META = {
  created:          { label: "ייווצר",                          cls: "bg-green-100 text-green-700" },
  requested_only:   { label: "רק מבוקש — ללא רשומה לעובד",      cls: "bg-gray-100 text-gray-600" },
  pending_approval: { label: "ממתין לאישור",                    cls: "bg-amber-100 text-amber-700" },
  refused:          { label: "נדחה — לא ייווצר דבר",            cls: "bg-red-100 text-red-700" },
};

// One requested Skill domain. Governed (§28) domains are registered by the publish as candidate
// records at level 0 — created, and not operative until human-reviewed evaluations. Anything
// outside the vocabulary maps to no canonical contract, so the publish refuses it.
export function builderSkillForecast(domain) {
  if (isGovernedDomain(domain)) {
    return {
      disposition: "created",
      approval: "pending_approval",
      canonical_contract: "skill_assignment",
      detail:
        "ייווצר לאחר היצירה: רשומת מיומנות מועמדת ברמה 0. הרשומה אינה הרשאה ואינה פעילה עד " +
        "אישור אנושי של הערכות מבוססות-ראיות.",
    };
  }
  return {
    disposition: "refused",
    approval: null,
    canonical_contract: "none",
    detail: "מחוץ למילון התחומים המפוקח — אין חוזה קנוני שמכיר בבקשה, ולכן לא ייווצר דבר.",
  };
}

// One requested system. `configured` is the deployment-configuration evidence the systems step
// already loaded (true / false / null-or-undefined = unknown); it decides only whether a
// platform-managed integration still needs deployment configuration. It cannot upgrade a
// platform-managed system into a per-employee record.
export function builderSystemForecast(toolId, configured = null) {
  const scope = systemPublishScope(toolId);
  if (scope === "supported") {
    return {
      disposition: "created",
      approval: "pending_approval",
      canonical_contract: "agent_connection",
      detail:
        "ייווצר לאחר היצירה: רשומת חיבור פר-עובד ללא פרטי גישה. החיבור אינו פעיל עד שמנהל " +
        "ימלא את שדות הגישה הנדרשים — יצירה אינה מעניקה גישה.",
    };
  }
  if (scope === "platform_managed") {
    return {
      disposition: "requested_only",
      approval: configured === true ? null : "pending_approval",
      canonical_contract: "platform_connection",
      detail:
        configured === true
          ? "רק מבוקש: המערכת מנוהלת פלטפורמית ומוגדרת deployment-wide — לא נוצרת רשומת חיבור לעובד."
          : "רק מבוקש: המערכת מנוהלת פלטפורמית — לא נוצרת רשומת חיבור לעובד, והגדרתה במערכת " +
            "טרם אושרה כאן, ולכן נדרשת השלמת הגדרה.",
    };
  }
  return {
    disposition: "refused",
    approval: null,
    canonical_contract: "none",
    detail: "המערכת אינה מוכרת למסלול הפרסום — לא ייווצר דבר.",
  };
}

// The aggregate over one form's requested items. `created` and `pendingApproval` are counted
// independently on purpose: an item can be both, and collapsing the two numbers would hide
// either the record or the human step waiting on it.
export function builderPublishForecast(form, environmentStatus = {}) {
  const domains = (form && form.requested_skill_domains) || [];
  const tools = form && form.tools ? form.tools : [];
  const skills = domains.map((domain) => ({
    kind: "skill",
    key: domain,
    ...builderSkillForecast(domain),
  }));
  const systems = tools.map((toolId) => ({
    kind: "connection",
    key: toolId,
    ...builderSystemForecast(
      toolId,
      environmentStatus[toolId] === "configured"
        ? true
        : environmentStatus[toolId] === "not_configured"
        ? false
        : null
    ),
  }));
  const items = [...skills, ...systems];
  return {
    skills,
    systems,
    counts: {
      created: items.filter((item) => item.disposition === "created").length,
      requestedOnly: items.filter((item) => item.disposition === "requested_only").length,
      pendingApproval: items.filter((item) => item.approval === "pending_approval").length,
      refused: items.filter((item) => item.disposition === "refused").length,
    },
  };
}

// ─── Live-source helpers (read-only projections; never second sources of truth) ─

// The same /api/connections/health payload the Connections page renders: yes/no configuration
// flags plus allow-listed labels — never credential material. A missing payload is unknown.
function envToolStatus(health, toolId) {
  if (!health || typeof health !== "object") return { state: "unknown", detail: null };
  const entry = health[toolId];
  if (!entry || typeof entry !== "object") return { state: "unknown", detail: null };
  if (entry.configured === true) return { state: "configured", detail: entry.mode_label || null };
  if (entry.configured === false) return { state: "not_configured", detail: null };
  return { state: "unknown", detail: null };
}

function providerReadiness(health) {
  const ai = health && typeof health === "object" ? health.ai_analysis : null;
  if (!ai || typeof ai !== "object") {
    // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 54): two different truths, collapsed
    // into one state, and the collapse is what let three surfaces state a determinate cause
    // over a payload that says nothing. `unknown` is "the status has not arrived"; `unreadable`
    // is "it arrived and carries no readable ai_analysis" -- neither a transport failure nor a
    // configuration fact. Every caller already guards the not-arrived case with its own loading
    // flag and the failed case with isError, so the settled-unreadable case was the ONLY one
    // reaching their else arms, and all three said something false about it: Step4Model that the
    // status "did not load" (it did), the `provider` readiness row and the preview-model card
    // that the provider "is not ready (configuration or key missing)" -- a cause nothing
    // reported -- with the row grading it `warn`, the DETERMINATE verdict bucket, so the badge
    // read "needs attention before creation" rather than "partial information". Same shape as
    // pass 24's fabricated `0 tools registered`: a projection returning no answer, rendered as
    // an answer. Reachability is a contract-shape change only -- /api/connections/health always
    // sets `ai_analysis` from `_ai_config_status()`, which always returns a dict with a boolean
    // `configured`, and the eight queries sharing the ["connectionsHealth"] cache slot all
    // return the payload unreshaped (the one queryFn that reshapes it, EmployeeWorkspace's
    // browser-worker capability, uses its own key) -- i.e. §2's rolling-deploy class, which is
    // exactly the bound pass 24 published for the twin it fixed in this file.
    return { state: health ? "unreadable" : "unknown", provider: null, model: null };
  }
  return {
    state: ai.configured ? "configured" : "not_configured",
    provider: ai.provider || null,
    model: ai.model || null,
  };
}

// The one sentence for that state, interpolated at all three surfaces that read it so the words
// before the irreversible action and the words inside the confirmation cannot drift (pass 19).
// Composed from phrasings already in this file -- the tool-catalog row's "was returned in a form
// that is not recognized" (pass 24) and Step4Model's "so there is no confirmation that the system
// is configured to run the model" -- so it introduces no new operator-facing wording.
export const PROVIDER_STATUS_UNREADABLE_NOTICE =
  "לא ידוע — סטטוס ספק ה-AI הוחזר בצורה שאינה מוכרת, ולכן אין אישור שהמערכת מוגדרת להריץ את המודל.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: the axis that decides whether the selected model
// can serve a run at all -- which is NOT the axis the provider-readiness ladder above speaks.
//
// `manager_agent._build_agent_analyst` takes the PROVIDER from global settings and only the MODEL
// NAME from the employee row ("The API key and provider are always taken from global settings --
// only the model name comes from the agent record"), and `create_direct_llm_analyst` validates the
// provider against {anthropic, openai} plus its key, never the model against the provider. So an
// openai-configured deployment plus any of the three Anthropic models this wizard offers builds an
// OpenAI adapter carrying a Claude model name, and the provider call raises AnalysisError at run
// time -- with no fallback, because the server-default fallback in _build_agent_analyst fires only
// when the adapter cannot be BUILT, and this one builds. `_ai_config_status().configured` is true
// throughout (its own test is provider set + model set + a key belonging to that provider), so the
// readiness ladder reads `configured` and three separate sentences then promised that "the
// employee's runs will use the selected model". Each was computed from one axis -- is a provider
// configured -- while asserting the other: does the selected model belong to it. The wizard already
// held both values and compared them at zero sites (`providerReadiness` even returns the
// deployment's `model`, which is read nowhere).
//
// The canonical answer to "what will this employee actually run" is
// services/model_provider_routing.build_assignment_contract, already wired as `provider_model` on
// GET /api/agents/{id}: it reports `provider_source: "server_policy"` (the provider is the
// deployment's, never the agent's), an `override_model` with `override_condition:
// "ticket_priority_high"` (a HIGH-priority ticket is routed to ANALYSIS_MODEL_HIGH_PRIORITY, not to
// the selected model) and an explicit `fallback` list. After creation the employee page can state
// all three; before it, this surface asserted none of them. The provider-mismatch half is
// detectable from the health payload the wizard already loads, so it is reported and degrades the
// readiness row; the high-priority-override half is in no payload this wizard can read, so it is
// stated as a qualification on the promise instead of being guessed at.
//
// Compared casefolded and trimmed on both sides, exactly like the server's own
// `(provider or "").strip().lower()` lookup. Pinned against the server end in
// backend/tests/test_employee_builder_v3.py: every MODEL_OPTIONS provider lowercased has to be a
// provider the server accepts, and the cross-provider pair has to still build an adapter there --
// if the server ever starts refusing it, the harm changes and this wording must change with it.
const providerKey = (value) => String(value == null ? "" : value).trim().toLowerCase();

export function modelProviderAlignment(model, readiness) {
  // Only meaningful once a provider is known to be configured: when it is not, the ladder already
  // says runs will not use the selected model, which is the stronger statement. A model outside the
  // wizard's own catalog declares no provider here, and its own readiness row already reports that
  // its runtime behaviour is unknown -- so neither case is reported as an alignment.
  if (!model || !readiness || readiness.state !== "configured") return "unknown";
  const deployed = providerKey(readiness.provider);
  const selected = providerKey(model.provider);
  if (!deployed || !selected) return "unknown";
  return deployed === selected ? "aligned" : "mismatched";
}

// The wizard's projection of the one entitlement gate (Tool Registry × tier × allowlist, the same
// projection the employee page's capabilities drawer and execution use). Counts only; grants nothing.
//
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: this must mirror autonomy_policy.check_entitlement,
// and on the allowlist gate (its step 2d) it did not. That gate denies a tool only when the allowlist
// is non-empty AND the tool is NOT registered read-only -- a read-only Registry tool stays reachable
// under ANY allowlist, because only permission_tier and the Tool Registry restrict what an employee
// may read. Projecting the allowlist as if it filtered read-only tools too UNDER-reports the
// permission surface of the draft: measured against services.employee_builder_v3._entitlement_projection
// (which calls the real gate) over 353 tier x allowlist combinations, the pre-141 filter disagreed on
// 309 of them, and at the default tier_1 with one of the fifteen read-only boxes left checked it
// reported 1 tool permitted where the employee reaches all 15. That is the dangerous direction on a
// least-privilege surface: the operator unchecks boxes, is told the restriction took effect, and the
// created employee's own capabilities drawer -- composed from entitled_tools_for, the same gate --
// then lists the tools this screen called blocked. utils/toolAllowlist already carries this lesson
// for the EMPTY allowlist ("any UI that renders an empty allowed_tools list as blocked is lying about
// what will actually happen at execution time"); it was never applied to a NON-empty one, where the
// same rule makes the restriction partial rather than absent.
//
// Not mirrored, deliberately: the gate's step 2 (tier_1 flat-denies anything not registered
// read-only) and its AUTO_ALLOWED_ACTIONS exemptions. Both are inert against the live Registry --
// all 15 tier_1-allowed definitions ARE read-only and none of the 31 definitions is an
// AUTO_ALLOWED action (censused) -- so the tier filter below already coincides with step 2. Those
// two premises are pinned in backend/tests/test_employee_builder_v3.py; if a state-changing tool is
// ever admitted to tier_1 the pin fails there, and step 2 must then be mirrored here as well.
export function entitlementProjection(tools, tier, allowedTools) {
  if (!Array.isArray(tools)) return null;
  const tierKey = tier || "tier_1";
  const tierTools = tools.filter((t) => Array.isArray(t.allowed_tiers) && t.allowed_tiers.includes(tierKey));
  const unrestricted = isUnrestrictedAllowlist(allowedTools);
  // check_entitlement step 2d: a non-empty allowlist restricts state-changing tools only.
  const allowlistExempt = (t) => t.operation_type === "read_only";
  const reachable = unrestricted
    ? tierTools
    : tierTools.filter((t) => allowlistExempt(t) || (allowedTools || []).includes(t.name));
  const strongGated = reachable.filter((t) => t.strong_approval_required).length;
  // Reachable DESPITE not being listed -- the size of what a restricted list does not restrict.
  // Zero when unrestricted: there is no list, so nothing is exempt from one.
  const listExempt = unrestricted
    ? 0
    : reachable.filter((t) => allowlistExempt(t) && !(allowedTools || []).includes(t.name)).length;
  // What the autonomy choice can and cannot change, over the SAME reachable set -- one fold, so the
  // two statements cannot diverge. check_action consults the mode only after step 3 has already
  // allowed every read-only tool, and its permissive arms are gated by
  // _needs_approval_at_any_autonomy, which returns True for an unknown action, for
  // APPROVAL_REQUIRED_ACTIONS and for any definition declaring approval_required.
  // `approval_required !== true` rather than `=== false` on purpose: the reassuring half of this
  // projection is "no tool the autonomy pick changes", so a payload that stopped carrying the
  // field must NOT silently produce that sentence -- under-reporting a permission surface is the
  // dangerous direction. The endpoint always serializes a boolean, and the backend pins that.
  const autonomyExempt = reachable.filter((t) => t.operation_type === "read_only").length;
  const autonomySensitive = reachable.filter(
    (t) => t.operation_type !== "read_only" && t.approval_required !== true,
  ).length;
  return {
    registered: tools.length,
    entitled: reachable.length - strongGated,
    strongGated,
    reachable: reachable.length,
    blocked: tools.length - reachable.length,
    listExempt,
    // Reachable and approved at every autonomy level, before the mode is read.
    autonomyExempt,
    // Reachable, state-changing and approval-bound at every autonomy level.
    autonomyBound: reachable.length - autonomyExempt - autonomySensitive,
    // The only tools for which the operator's pick changes an outcome.
    autonomySensitive,
  };
}

// ─── Step components ──────────────────────────────────────────────────────────

function StepIndicator({ current }) {
  const labels = ["תפקיד", "זהות", "סקופ", "מיומנויות", "מודל", "מערכות", "מגבלות", "סיכום"];
  return (
    <div className="flex items-center gap-1 justify-center mb-8">
      {labels.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <div key={step} className="flex items-center gap-1">
            <div className={`flex flex-col items-center`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                done   ? "bg-brand-600 border-brand-600 text-white" :
                active ? "bg-white border-brand-600 text-brand-600" :
                         "bg-white border-gray-300 text-gray-400"
              }`}>
                {done ? "✓" : step}
              </div>
              <p className={`text-xs mt-0.5 ${active ? "text-brand-600 font-medium" : "text-gray-400"}`}>
                {label}
              </p>
            </div>
            {i < labels.length - 1 && (
              <div className={`w-4 h-0.5 mb-4 ${done ? "bg-brand-600" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Role presets: what one click writes, and what it replaces ───────────────
// A role preset is the only place in this wizard that writes AUTHORITY fields with no preview and
// no confirmation. In Step1Role's single handler, permission_tier / autonomy_level / allowed_tools
// (cleared) / model / system_prompt are all assigned unconditionally, while every identity field in
// the SAME handler is guarded by `f.<field> ||` and never overwrites text the operator typed. One
// step later the persona presets preview each change, badge it "יוחלף", warn before replacing typed
// values and require "החל תבנית" -- for fields that grant nothing. The mechanism exists; it had
// never been applied to the fields that decide what the employee may do.
//
// Measured against the real gate (employee_workspace.entitled_tools_for over tool_registry's 31
// definitions): tier_1 reaches 15 tools (0 state-changing), tier_2 reaches 26 (11 state-changing),
// tier_3 reaches 31 (16) -- so the tier a card sets decides the reachable set, and one of the nine
// cards sets tier_2. The allowlist reset is inert at tier_1 (a restricted list still yields the same
// 15, because check_entitlement exempts registry read-only tools from any allowlist) and widens
// 16 -> 26 at tier_2. Two of the five writes carry no operational consequence of their own: the
// autonomy overwrite is inert against this registry (all three options are one arm of
// check_action -- see the equivalence note the limits step renders) and all nine presets carry the
// same model, so only
// a model the OPERATOR picked at step 5 can be lost. Both are still the operator's choice, so they
// are named rather than dropped in silence.
export const ROLE_PRESET_WRITES_FIELDS = [
  "permission_tier",
  "autonomy_level",
  "allowed_tools",
  "model",
  "system_prompt",
];

// Each entry names the operator's OWN value, so the warning says what is about to be lost rather
// than what the template will install. The last-resort arm prints the raw key instead of a
// confident sentence: an unlabelled key is a contract-shape error, not a state to narrate.
const ROLE_PRESET_OVERWRITE_LABELS = {
  permission_tier: (v) =>
    `רמת הרשאה שבחרתם — ${PERMISSION_TIER_OPTIONS.find((o) => o.value === v)?.label ?? v}`,
  autonomy_level: (v) =>
    `מדיניות אישור שבחרתם — ${AUTONOMY_OPTIONS.find((o) => o.value === v)?.label ?? v}`,
  allowed_tools: (v) => `רשימת כלים מצומצמת — ${v} כלים מסומנים`,
  model: (v) => `מודל שבחרתם — ${MODEL_OPTIONS.find((o) => o.value === v)?.label ?? v}`,
  system_prompt: (v) => `הוראות הפעלה שהזנתם — ${v} תווים`,
};

// Called during render only, so EMPTY_FORM (declared at the bottom of this module) is initialised
// by the time this runs. The baseline is the ATTACHED preset's own value, not the form default, so
// a value that merely came from the currently selected template is not reported as the operator's.
export function rolePresetDraftOverwrites(form) {
  const attached = ROLE_PRESETS.find((p) => p.id === form.role_preset) || null;
  const out = [];

  const tier = form.permission_tier || EMPTY_FORM.permission_tier;
  if (tier !== (attached ? attached.permission_tier : EMPTY_FORM.permission_tier)) {
    out.push({ key: "permission_tier", value: tier });
  }
  const autonomy = form.autonomy_level || EMPTY_FORM.autonomy_level;
  if (autonomy !== (attached ? attached.autonomy : EMPTY_FORM.autonomy_level)) {
    out.push({ key: "autonomy_level", value: autonomy });
  }
  // An empty array is NOT a restriction on the server (autonomy_policy enforces the allowlist only
  // when it is non-empty), so only a non-empty list is something to lose.
  const list = Array.isArray(form.allowed_tools) ? form.allowed_tools : [];
  if (list.length > 0) out.push({ key: "allowed_tools", value: String(list.length) });

  const model = form.model || EMPTY_FORM.model;
  if (model !== (attached ? attached.model : EMPTY_FORM.model)) {
    out.push({ key: "model", value: model });
  }
  const prompt = (form.system_prompt || "").trim();
  if (prompt && !ROLE_PRESETS.some((p) => (p.systemPrompt || "").trim() === prompt)) {
    out.push({ key: "system_prompt", value: String(prompt.length) });
  }
  return out;
}

// ─── DIGEMP-141 pass 31: what a role template's TIER decides, and what its DESC does not grant ──
// Pass 30 disclosed WHICH fields one card click overwrites, and that a card at a higher tier
// "widens the NUMBER of tools the employee reaches". That sentence is true and answers the count
// question. The axis it does not speak is the KIND, and the kind is the customer boundary: through
// the real gate tier_1's whole reachable set is read-only (15 tools, 0 state-changing) while
// tier_2 reaches 26 of which 11 are state-changing -- and those 11 are exactly the customer-facing
// and customer-device writes (send_customer_message / send_email_to_customer, close_ticket and
// escalate which each mail the customer, reset_user_password, install_software, restart_device,
// restart_service, run_diagnostic_script, add_comment, add_ticket_comment). Every one of the 11 is
// approval-bound in every autonomy mode and both crisis states -- 0 of 88 (tool x mode x crisis)
// combinations runs unapproved -- so the raise widens what the employee may PROPOSE from nothing
// to eleven, not what it may do unilaterally. Pass 30 measured 0/11/16 and pinned it in
// backend/tests/test_employee_builder_v3.py; it reached the operator at zero sites.
export const READ_ONLY_TIER = "tier_1";

// The one tier whose whole reachable set is read-only, so the only level at which "the employee
// cannot propose any state-changing action" is true. Asserted against entitled_tools_for in
// backend/tests/test_employee_builder_v3.py: admit one state-changing definition to tier_1 and the
// pin fails there rather than this sentence going quietly false.
export function rolePresetsRaisingTier(presets) {
  return (presets || []).filter((p) => p.permission_tier !== READ_ONLY_TIER);
}

// Built here rather than written into the JSX so both arms are reachable from a unit test: with
// the live data exactly one card raises the tier, and a zero-raising ROLE_PRESETS must leave the
// base sentence byte-identical (the .filter(Boolean).join(" ") shape this file already uses).
export function rolePresetTierKindSentence(presets, readOnlyTierLabel) {
  const all = presets || [];
  const raising = rolePresetsRaisingTier(all);
  const base =
    `רמת ההרשאה קובעת לא רק לכמה כלים העובד מגיע אלא גם איזה סוג פעולות הוא יוכל להציע: ברמת „${readOnlyTierLabel}”, ` +
    `שהיא רמתן של ${all.length - raising.length} מתוך ${all.length} התבניות, ` +
    `אין אף פעולת שינוי-מצב שהעובד יכול להציע.`;
  const raised = raising.length
    ? `${raising.length === 1 ? "התבנית" : "התבניות"} ` +
      `${raising.map((p) => `„${p.label}”`).join(", ")} ` +
      `${raising.length === 1 ? "היא היחידה שמעלה" : "הן היחידות שמעלות"} את הרמה, ` +
      `ובכך ${raising.length === 1 ? "מוסיפה" : "מוסיפות"} פעולות שינוי-מצב מול הלקוח — ` +
      `שליחת מייל ללקוח, איפוס סיסמת משתמש והפעלה מחדש או התקנה על מכשירים מנוהלים — ` +
      `שכל אחת מהן טעונה אישור טכנאי לפני ביצוע.`
    : "";
  return [base, raised].filter(Boolean).join(" ");
}

// ─── DIGEMP-141 pass 33: what the one runtime that READS these instructions supplies ─────
// Pass 31 took the role templates' `desc` strings as claims and disclosed the CATALOG axis:
// the employee reaches only the tools in the tool catalog, and that catalog holds no tool for
// backups, server health or CVE auditing. The population it named and did not take is the one
// below - the nine `systemPrompt` BODIES, 7,520 chars over 8 non-empty templates and the
// largest operator-visible text in this file. Censused by mutation, one probe per line: 121
// lines, 0 broken, and 120 held down by nothing (only `helpdesk`'s first line reds, at 1).
// Read against the code that DECIDES, the axis none of them speaks is the one that governs
// whether any of it is PERFORMABLE:
//   * agent.system_prompt reaches a model at exactly ONE site -
//     manager_agent._build_agent_analyst:258 -> create_direct_llm_analyst(agent_system_prompt=),
//     which PREPENDS it to the base safety contract (DirectLLMAnalysisAdapter.__init__).
//     browser_work reads Agent.system_prompt at 0 sites.
//   * that adapter passes the model NO tools: `tools=`/`tool_choice`/`tool_use` is 0 in
//     direct_llm_analyst.py against same-file controls `messages=` 2 and `system=` 1.
//   * the run's entire input is analyze(ticket_title, ticket_description, customer_context) -
//     no logs, no backup status, no alert history, no catalog snapshot, no fleet report.
//   * the base contract it is prepended to says so itself: 'working from ticket text alone
//     without real diagnostic data' (L155), 'not executing commands' (L168, L184), 'Do not
//     invent specific commands, file paths, or scripts' (L172), and 'Respond with ONLY valid
//     JSON' over a fixed schema (L192).
//   * of the 31 registry definitions none is a log, backup, alert-history, config-drift or
//     TeamViewer tool, and `helpdesk` instructs the model to gather evidence via
//     'check_executable, TeamViewer lookup' - the first a real definition this runtime never
//     offers, the second naming no definition at all.
// So `self_healing` - the one card of nine that raises the tier (pass 31) - instructs a closed
// remediation loop (scan logs/alert history/backup status -> AUTO_FIX 'you can apply
// immediately' -> 'wait for technician approval before executing' -> 'Log all actions in the
// AgentAction audit trail' -> 'Verify - after fix, re-check the signal') of which NO step is
// executable, and 'include exact file paths, config keys' is what rule 7 forbids outright.
// Arms rendered on this axis before this pass: 0. The two existing disclosures are both TRUE
// and both about GRANTING - the appended tools line 'does not grant a permission, tool or
// approval', and 'instructions and role are guidance only'. Neither says the instructions
// reach a model given no tools and nothing but the ticket text, which is a statement about
// whether they are CARRIED OUT, not about whether they widen authority. Pass 31's own fix
// sharpens the gap: it tells the operator which tools the employee reaches, `helpdesk` names
// one of exactly those, and the runtime that reads the body offers none of them.
// Closed by labelling only - nothing is degraded, no body is altered (all nine stay
// byte-identical) and no readiness state moves: ticket analysis is what this runtime is for,
// and the gap was the disclosure. Pinned at both ends in
// backend/tests/test_employee_builder_v3.py, including a tripwire that reds if a TeamViewer
// tool is ever added to the registry (which would make this sentence stop being true).
export const PROMPT_RUNTIME_NOTE =
  "הוראות ההפעלה נשלחות בריצת ניתוח: הריצה מקבלת את טקסט הקריאה ואת הקשר הלקוח, " +
  "ואינה מעמידה לרשות המודל שום כלי — גם לא כלי שבקטלוג. לכן שורות בהוראות שמנחות " +
  "להפעיל כלי, לקרוא לוגים, להחיל שינוי תצורה או לאמת תיקון אינן מבוצעות, והריצה " +
  "מחזירה אבחון מובנה בתבנית קבועה ולא את תבנית הפלט שנכתבה בהוראות.";

// The templates that write an operating prompt at all. `custom` writes "", so a template
// count and a prompt-writing count are two different populations.
export function presetsDeclaringOperatingInstructions(presets) {
  return (presets || []).filter((p) => (p.systemPrompt || "").trim());
}

// The bodies that open by naming the input they expect - and name one the analysis run never
// carries (a technology catalog snapshot, an agent performance report, the fleet skills
// report). Measured at 3 of the 8; computed rather than written into the prose so the
// sentence cannot go stale the way a carried figure does.
export function presetPromptsNamingNonTicketInput(presets) {
  return presetsDeclaringOperatingInstructions(presets).filter((p) =>
    /when (invoked with|given)/i.test(p.systemPrompt));
}

// Built here rather than written into the JSX so both arms are reachable from a unit test: a
// ROLE_PRESETS whose bodies name no foreign input must leave the base sentence byte-identical.
export function rolePresetPromptRuntimeSentence(presets) {
  const all = presets || [];
  const declaring = presetsDeclaringOperatingInstructions(all);
  const nonTicket = presetPromptsNamingNonTicketInput(all);
  const base =
    `טקסט ההוראות שהתבנית כותבת נכתב עבור הרצות אחרות של הפלטפורמה: ` +
    `${declaring.length} מתוך ${all.length} התבניות כותבות הוראות הפעלה, והן מנחות לאסוף ` +
    `עדויות, לנטר מערכות או להחיל ולאמת תיקון.`;
  const foreign = nonTicket.length
    ? ` ${nonTicket.length} מהן אף נפתחות בתיאור קלט שאינו קריאה.`
    : "";
  const runtime =
    ` ריצת הניתוח שמקבלת את ההוראות אינה מעמידה לרשות המודל כלי ואינה מקבלת לוגים, ` +
    `גיבויים או דוחות — ולכן הנחיות כאלה אינן מבוצעות.`;
  return base + foreign + runtime;
}
export function Step1Role({ form, setForm }) {
  const overwrites = rolePresetDraftOverwrites(form);
  const readOnlyTierLabel =
    PERMISSION_TIER_OPTIONS.find((o) => o.value === READ_ONLY_TIER)?.label ?? READ_ONLY_TIER;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">בחר תפקיד</h2>
        <p className="text-sm text-gray-500 mt-0.5">בחר תבנית מוכנה או הגדר תפקיד מותאם</p>
      </div>

      <div
        role="note"
        data-testid="role-preset-authority-note"
        className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700"
      >
        תבנית תפקיד אינה קובעת תפקיד בלבד: לחיצה אחת כותבת גם את רמת ההרשאה, את מדיניות האישור
        ואת המודל, מחליפה את הוראות ההפעלה, ומבטלת רשימת כלים מצומצמת אם הוגדרה — בלי תצוגה מקדימה
        ובלי אישור. רמת ההרשאה היא שקובעת לאילו כלים העובד מגיע, ולכן תבנית ברמה גבוהה יותר מרחיבה
        את מספר הכלים שהעובד מגיע אליהם. פרטי הזהות שהזנתם אינם נדרסים.
      </div>

      <div
        role="note"
        data-testid="role-preset-tier-kind-note"
        className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700"
      >
        {rolePresetTierKindSentence(ROLE_PRESETS, readOnlyTierLabel)}
      </div>

      <div
        role="note"
        data-testid="role-preset-duty-note"
        className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
      >
        התיאור שבכרטיס מציין את תחום העיסוק שהתבנית נכתבה עבורו, ולא כלי שהעובד מקבל: העובד
        מגיע רק לכלים שבקטלוג הכלים, וקטלוג זה אינו כולל כלי לגיבויים, לבריאות השרת או
        לביקורת גרסאות ו-CVE. תפקידי הפלטפורמה האלה מבוצעים בידי סוכני המערכת של
        הפלטפורמה, הנפתרים לפי שמם הקבוע ולא לפי התבנית שתבחרו כאן.
      </div>

      <div
        role="note"
        data-testid="role-preset-prompt-runtime-note"
        className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
      >
        {rolePresetPromptRuntimeSentence(ROLE_PRESETS)}
      </div>

      {overwrites.length > 0 && (
        <div
          role="alert"
          data-testid="role-preset-overwrite-warning"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 space-y-1"
        >
          <p className="font-medium">בחירת תבנית עכשיו תחליף בחירות שכבר עשיתם בשלבים הבאים:</p>
          <ul className="space-y-0.5 list-disc list-inside">
            {overwrites.map((item) => (
              <li key={item.key}>
                {(ROLE_PRESET_OVERWRITE_LABELS[item.key] || ((v) => `${item.key}: ${v}`))(item.value)}
              </li>
            ))}
          </ul>
          <p className="text-[11px]">כדי לשמר אותן, המשיכו לשלב הבא בלי ללחוץ על תבנית.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ROLE_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            onClick={() => setForm((f) => ({
              ...f,
              role_preset: preset.id,
              model: preset.model,
              autonomy_level: preset.autonomy,
              permission_tier: preset.permission_tier,
              // A tool allowlist restricted under a previous preset's tier can name tools that
              // don't exist (or mean something different) under this preset's tier -- reset it
              // here too, matching Step6Limits's own tier-button transition below.
              allowed_tools: null,
              system_prompt: preset.systemPrompt,
              name: f.name || (preset.id !== "custom" ? preset.label : f.name),
              // Persona seeds (identity fields -- no authority, but they ARE the routing score):
              // fill the identity step with a human starting
              // point, but never overwrite something the operator already typed -- same rule as name.
              role_title: f.role_title || preset.role_title || "",
              specialties_text: f.specialties_text || (preset.specialties?.length ? preset.specialties.join(", ") : ""),
              avatar_key: f.avatar_key || preset.avatar_key || "",
              avatar_color: f.avatar_color || preset.avatar_color || "",
            }))}
            className={`text-right rounded-xl border-2 p-4 transition-all ${
              form.role_preset === preset.id
                ? "border-brand-600 bg-brand-50"
                : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">{preset.icon}</span>
              <div>
                <p className="font-medium text-gray-800 text-sm">{preset.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{preset.desc}</p>
                <p
                  className="text-[10px] text-gray-400 mt-1"
                  data-testid={`role-preset-tier-${preset.id}`}
                >
                  קובעת רמת הרשאה:{" "}
                  {PERMISSION_TIER_OPTIONS.find((o) => o.value === preset.permission_tier)?.label
                    ?? preset.permission_tier}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="space-y-3 pt-2">
        <div>
          <label htmlFor="employee-name" className="block text-xs text-gray-500 mb-1">שם העובד *</label>
          <input
            id="employee-name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="לדוגמה: IT Helpdesk Agent — Prodigy"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          {/* Pass 45: the reserved-identity axis was armed only at the step-8 readiness row. This
              is the surface that COLLECTS the name, and the value is a local property of the typed
              text, so the statement needs no fetch - including the half nothing stated anywhere:
              for these three values the choice cannot be undone after the create. */}
          {isReservedSystemName(form.name) && (
            <p
              role="alert"
              data-testid="role-reserved-name-warning"
              className="text-xs text-amber-700 mt-1"
            >
              {RESERVED_NAME_COLLECT_NOTICE} {RESERVED_NAME_IRREVERSIBLE_CLAUSE}
            </p>
          )}
          {/* Pass 67: the audit copy of this field, unconditional for pass 46's reason at
              this box -- it can only change what gets pasted before the paste. The clause
              set is static (the create always writes the audit row, the read gate and the
              absent delete path are platform properties), so no clause's decider sits
              outside its own condition and the sentence is true in every state this step
              reaches. Future-tense about creation, never presupposing a row (pass 57). */}
          <p className="text-[11px] text-gray-400 mt-1" data-testid="role-name-audit-note">
            {NAME_AUDIT_COPY_NOTICE}
          </p>
        </div>
        <div>
          <label htmlFor="employee-description" className="block text-xs text-gray-500 mb-1">תיאור</label>
          <textarea
            id="employee-description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="מה אחריות העובד?"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          {/* Pass 46: unconditional, deliberately. Both clauses are standing properties of the
              FIELD and not findings about this draft's content, so keying them on
              `form.description` would hide them from an operator who has not typed yet - which is
              the only moment the second clause can still change what gets pasted. Same reason the
              readiness row carries the secret boundary unconditionally (pass 22). */}
          <p className="text-[11px] text-gray-400 mt-1" data-testid="role-description-note">
            {DESCRIPTION_COLLECT_NOTICE} {DRAFT_UNCHECKED_SECRET_FIELDS_NOTICE}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Persona presets (DIGEMP-EMPLOYEE-BUILDER-PRESETS-004) ────────────────────
// Ready-made helpdesk coworker identities for the identity step. A preset can only ever write
// the identity fields listed in PERSONA_PRESET_FIELDS — never tier, autonomy, tools,
// approval policy, model, system prompt, or the employee name — and nothing is written until
// the operator confirms the previewed change.

export const PERSONA_PRESET_FIELDS = [
  "role_title",
  "avatar_key",
  "avatar_color",
  "specialties_text",
  "personality",
  "intro_text",
];

export const PERSONA_PRESETS = [
  {
    id: "first_response",
    icon: "🎧",
    label: "מוקדן תגובה ראשונה",
    fields: {
      role_title: "מוקדן תמיכה",
      avatar_key: "🎧",
      avatar_color: "blue",
      specialties_text: "מענה לפניות, סיווג קריאות, הדרכת משתמשים",
      personality: "רגוע וידידותי, מבהיר את הבעיה לפני שמסיק מסקנות, מסביר פתרונות בשלבים קצרים",
      intro_text: "היי, אני כאן כדי לעזור. ספרו לי מה קרה ומה כבר ניסיתם — נמצא יחד את הצעד הבא.",
    },
  },
  {
    id: "diagnostics",
    icon: "🔎",
    label: "מאבחן תקלות",
    fields: {
      role_title: "מאבחן תקלות",
      avatar_key: "🔎",
      avatar_color: "teal",
      specialties_text: "איסוף ראיות, ניתוח לוגים, שחזור תקלות",
      personality: "שיטתי ומבוסס ראיות, מפריד עובדות מהשערות ומצרף רמת ביטחון לכל מסקנה",
      intro_text: "אתחיל באיסוף ראיות מהמערכת ואחזור עם סיבה סבירה והצעד המומלץ.",
    },
  },
  {
    id: "field_tech",
    icon: "🧰",
    label: "טכנאי שטח",
    fields: {
      role_title: "טכנאי שטח",
      avatar_key: "🧰",
      avatar_color: "amber",
      specialties_text: "התקנות וחומרה, סשנים מרחוק, תיקון והחלפה",
      personality: "מעשי ותמציתי, מדווח מה נעשה ומה נשאר פתוח לפני כל שינוי",
      intro_text: "שלום, אני הטכנאי. אבדוק את התקלה ואעדכן בכל שלב לפני כל שינוי במערכת.",
    },
  },
  {
    id: "knowledge_keeper",
    icon: "📚",
    label: "אחראי תיעוד ידע",
    fields: {
      role_title: "אחראי תיעוד ידע",
      avatar_key: "📚",
      avatar_color: "violet",
      specialties_text: "מסמכי ידע, נהלים, תיעוד פתרונות",
      personality: "מאורגן ומדויק, מתעד כל פתרון כדי שהפעם הבאה תיפתר מהר יותר",
      intro_text: "אסכם ואתעד את הפתרון, כדי שנוכל לחזור אליו בפעם הבאה בלחיצה אחת.",
    },
  },
];

const PERSONA_FIELD_LABELS = {
  role_title: "תפקיד",
  avatar_key: "אווטאר",
  avatar_color: "צבע אווטאר",
  specialties_text: "תחומי התמחות",
  personality: "אישיות וסגנון עבודה",
  intro_text: "משפט פתיחה",
};

const personaFieldDisplay = (key, value) =>
  key === "avatar_color"
    ? AVATAR_COLOR_OPTIONS.find((o) => o.value === value)?.label ?? value
    : value;

// ─── Step 2: employee identity (no authority — but the routing basis) ─────────
// All fields here map 1:1 to AgentCreate persona fields (schemas/agent.py). They never change
// tier, autonomy, or tool access — but they are not presentation-only either: specialties,
// role_title, name and description are the whole manager routing score (3/2/2/1) and an
// availability of "offline" is excluded from the candidate set before scoring runs.

export function EmployeeProfileCard({ form }) {
  const name = form.name.trim() || "עובד דיגיטלי חדש";
  const roleTitle = form.role_title.trim();
  const specialties = parseSpecialties(form.specialties_text);
  const status = STATUS_META[form.availability_status] || STATUS_META.available;

  return (
    <section aria-label="תצוגה מקדימה של כרטיס העובד" className="rounded-xl border-2 border-violet-100 bg-violet-50/40 p-4 space-y-3">
      <p className="text-xs font-semibold text-violet-700">תצוגה מקדימה — כך העובד ייראה לצוות</p>
      <div className="flex items-center gap-3">
        <EmployeeAvatar
          name={name}
          avatarKey={form.avatar_key}
          avatarColor={form.avatar_color}
          status={form.availability_status}
          size="lg"
          showStatus
        />
        <div className="min-w-0 space-y-0.5">
          <p className="font-semibold text-gray-800 truncate">{name}</p>
          <p className="text-sm text-violet-700">{roleTitle || "תפקיד העובד"}</p>
          <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full ${AVAILABILITY_BADGE[form.availability_status] || AVAILABILITY_BADGE.available}`}>
            {status.label}
          </span>
        </div>
      </div>
      {/* DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 25): the UNSETTABLE FIELD. This card
          is headed "how the employee will look to the team", and the team-visible status is not
          availability_status: every team surface renders employee_workspace.effective_status,
          whose FIRST arm is `if not agent.is_enabled: return "offline"`. is_enabled is one of the
          five agents columns AgentCreate cannot carry (id / is_enabled / created_at / updated_at /
          created_by), so the single creating action takes the column default, False -- there is no
          wizard field to get wrong, which is why every request-side census of this task (the key
          census, the predicate census, the pydantic refusal census, the route-guard census) was
          blind to it. So the green "available" badge above was a claim about the team's screens
          that creation guaranteed false. The chosen status IS stored and does take effect on
          activation, so this discloses rather than re-labels; `offline` is excluded because for
          that choice the two agree. */}
      {form.availability_status !== "offline" && (
        <p className="text-[11px] text-gray-600">
          לפני ההפעלה: כל מסך צוות יציג את העובד במצב „{STATUS_META.offline.label}” ולא „{status.label}” — העובד נוצר במצב מושבת, והסטטוס שנבחר כאן נכנס לתוקף רק לאחר הפעלתו מדף העובד.
        </p>
      )}
      {specialties.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {specialties.map((s) => (
            <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">{s}</span>
          ))}
        </div>
      )}
      {form.description.trim() && <p className="text-xs text-gray-600">{form.description.trim()}</p>}
      {form.intro_text.trim() && (
        <p className="text-sm text-gray-700 italic border-r-2 border-violet-200 pr-2">״{form.intro_text.trim()}״</p>
      )}
      {form.personality.trim() && (
        <p className="text-xs text-gray-500">סגנון עבודה: {form.personality.trim()}</p>
      )}
    </section>
  );
}

export function StepIdentity({ form, setForm, draftSubmitted = false }) {
  const identityBounds = draftBoundViolations(form);
  const [previewPresetId, setPreviewPresetId] = useState(null);
  const previewPreset = PERSONA_PRESETS.find((p) => p.id === previewPresetId) || null;

  // Editing a persona field by hand detaches the applied preset, so the "פעיל" badge never
  // keeps claiming preset values the operator has since changed.
  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.value,
      ...(PERSONA_PRESET_FIELDS.includes(key) ? { persona_preset: null } : {}),
    }));

  const presetChanges = (preset) =>
    PERSONA_PRESET_FIELDS
      .filter((key) => preset.fields[key] !== undefined)
      .map((key) => ({ key, from: form[key], to: preset.fields[key] }))
      .filter(({ from, to }) => from !== to);

  const applyPersonaPreset = (preset) => {
    setForm((f) => {
      const next = { ...f, persona_preset: preset.id };
      for (const key of PERSONA_PRESET_FIELDS) {
        if (preset.fields[key] !== undefined) next[key] = preset.fields[key];
      }
      return next;
    });
    setPreviewPresetId(null);
  };

  const resetIdentity = () => {
    setForm((f) => ({
      ...f,
      persona_preset: null,
      role_title: "",
      avatar_key: "",
      avatar_color: "",
      availability_status: "available",
      personality: "",
      specialties_text: "",
      intro_text: "",
    }));
    setPreviewPresetId(null);
  };

  const hasIdentityContent =
    PERSONA_PRESET_FIELDS.some((key) => String(form[key] || "").trim() !== "") ||
    form.availability_status !== "available";

  const changes = previewPreset ? presetChanges(previewPreset) : [];
  const hasReplacements = changes.some(({ from }) => String(from || "").trim() !== "");

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">זהות העובד</h2>
        <p className="text-sm text-gray-500 mt-0.5">איך העובד ייראה ויציג את עצמו בפני הצוות — שם, תפקיד, אווטאר וסגנון. השדות האלה אינם לתצוגה בלבד: הם גם מה שמדרג את העובד בבחירה לפנייה.</p>
      </div>

      {/* Persona presets: selecting shows a preview; fields change only after "החל תבנית" */}
      <section aria-label="תבניות זהות לעובדי תמיכה" className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-gray-600">תבניות זהות מהירות לעובדי תמיכה (אופציונלי)</p>
          <button
            type="button"
            onClick={resetIdentity}
            disabled={!hasIdentityContent}
            className="text-[11px] text-gray-500 hover:text-red-600 hover:underline disabled:opacity-40 disabled:hover:text-gray-500"
          >
            איפוס פרטי זהות
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERSONA_PRESETS.map((preset) => {
            const active = form.persona_preset === preset.id;
            const previewing = previewPresetId === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                aria-pressed={previewing}
                onClick={() => setPreviewPresetId(previewing ? null : preset.id)}
                className={`flex items-center gap-1.5 rounded-lg border-2 px-3 py-1.5 text-xs transition-all ${
                  previewing
                    ? "border-brand-600 bg-brand-50 text-gray-800"
                    : active
                    ? "border-violet-300 bg-violet-50 text-gray-800"
                    : "border-gray-200 bg-white hover:border-gray-300 text-gray-700"
                }`}
              >
                <span aria-hidden="true">{preset.icon}</span>
                <span>{preset.label}</span>
                {active && <span className="text-[10px] font-medium text-violet-700">פעיל</span>}
              </button>
            );
          })}
        </div>

        {previewPreset && (
          <div role="region" aria-label="תצוגה מקדימה של התבנית" className="rounded-lg border-2 border-brand-100 bg-white p-3 space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              תצוגה מקדימה — <span aria-hidden="true">{previewPreset.icon}</span> {previewPreset.label}
            </p>
            {changes.length === 0 ? (
              <p className="text-xs text-gray-500">התבנית כבר מיושמת — אין שינויים להחיל.</p>
            ) : (
              <ul className="space-y-1">
                {changes.map(({ key, from, to }) => {
                  const replaces = String(from || "").trim() !== "";
                  return (
                    <li key={key} className="text-xs text-gray-600">
                      <span className="font-medium text-gray-700">{PERSONA_FIELD_LABELS[key]}: </span>
                      {replaces && (
                        <>
                          <span className="line-through text-gray-400">{personaFieldDisplay(key, from)}</span>
                          <span aria-hidden="true"> ← </span>
                        </>
                      )}
                      <span className="text-gray-800">{personaFieldDisplay(key, to)}</span>
                      <span className={`mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        replaces ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                      }`}>
                        {replaces ? "יוחלף" : "יתמלא"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {changes.length > 0 && (
              <>
                {hasReplacements && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    שימו לב: ההחלה תחליף ערכים שכבר הזנתם — הם מסומנים בקו חוצה.
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => applyPersonaPreset(previewPreset)}
                    className="bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium"
                  >
                    החל תבנית
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewPresetId(null)}
                    className="border px-3 py-1.5 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
                  >
                    ביטול
                  </button>
                </div>
              </>
            )}
            <p className="text-[11px] text-gray-400">
              ההחלה משנה רק את פרטי הזהות — הרשאות, כלים ומדיניות אישור נשארים ללא שינוי.
              עם זאת, היא מחליפה את תחומי ההתמחות המוצהרים, והם הגורם בעל המשקל הרב ביותר בבחירת העובד לפנייה.
            </p>
          </div>
        )}

        {/* Scope the subject. This wizard has TWO template families and they behave oppositely:
            Step1Role's role presets apply on one unconfirmed click and are the only place that
            writes permission_tier / autonomy_level / allowed_tools / model / system_prompt -- i.e.
            they change exactly the three things the note above says a template leaves alone. An
            unqualified "choosing a template previews first" is the sentence that licenses reading
            this section's guarantee onto them, so it names the other family rather than implying
            it is governed here. */}
        <p className="text-[11px] text-gray-400" data-testid="identity-preset-scope-note">
          בחירת תבנית זהות מציגה תצוגה מקדימה בלבד; השינויים נכתבים רק לאחר אישור, וניתן לאפס או
          לערוך אותם בכל רגע. תבניות התפקיד שבשלב הראשון נוהגות אחרת: הן מוחלות בלחיצה אחת, בלי
          תצוגה מקדימה ובלי אישור, והן כן משנות רמת הרשאה, מדיניות אישור, מודל והוראות הפעלה.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label htmlFor="employee-avatar-key" className="block text-xs text-gray-500 mb-1">אווטאר (אימוג'י)</label>
              <input
                id="employee-avatar-key"
                value={form.avatar_key}
                maxLength={16}
                onChange={set("avatar_key")}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="🛡️"
              />
            </div>
            <div>
              <label htmlFor="employee-avatar-color" className="block text-xs text-gray-500 mb-1">צבע</label>
              <select
                id="employee-avatar-color"
                value={form.avatar_color}
                onChange={set("avatar_color")}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">ברירת מחדל</option>
                {AVATAR_COLOR_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="employee-availability" className="block text-xs text-gray-500 mb-1">זמינות</label>
              <select
                id="employee-availability"
                value={form.availability_status}
                onChange={set("availability_status")}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              >
                {Object.entries(STATUS_META).map(([value, meta]) => (
                  <option key={value} value={value}>{meta.label}</option>
                ))}
              </select>
              {/* Pass 29: routing_eligible_agents filters `availability_status != "offline"`, so
                  this one option removes the employee from the candidate set outright rather than
                  only deprioritising it. The label is interpolated, never retyped. */}
              {form.availability_status === "offline" && (
                <p role="alert" data-testid="identity-availability-unrouted" className="text-xs text-amber-700 mt-1">
                  „{STATUS_META.offline.label}” מוציא את העובד מרשימת המועמדים לניתוב — גם לאחר הפעלתו הוא לא ייבחר לשום פנייה עד שהזמינות תשתנה.
                </p>
              )}
              {AVAILABILITY_GRADED_VALUES.includes(form.availability_status) && (
                <p role="alert" data-testid="identity-availability-ranked" className="text-xs text-amber-700 mt-1">
                  {AVAILABILITY_GRADED_COST_NOTICE}
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="employee-role-title" className="block text-xs text-gray-500 mb-1">תפקיד</label>
            <input
              id="employee-role-title"
              value={form.role_title}
              maxLength={120}
              onChange={set("role_title")}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="למשל: טכנאי אבטחת מידע"
            />
          </div>

          <div>
            <label htmlFor="employee-specialties" className="block text-xs text-gray-500 mb-1">תחומי התמחות (מופרדים בפסיק)</label>
            <input
              id="employee-specialties"
              value={form.specialties_text}
              onChange={set("specialties_text")}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="ESET, רשת, Microsoft 365"
            />
            {/* Pass 22: the skills step has bounded its free text, counted it and alerted on an
                overlong entry since 141; this field - whose server validator has the SAME two arms
                - had a counter for neither, no maxLength and no alert, and its item-count arm was
                enforced only by a silent slice inside the shared parser. Disclosed where it can
                still be fixed, and again on the readiness row so the verdict cannot read "ready"
                over either case. */}
            <p className="text-[11px] text-gray-400 mt-1">
              {identityBounds.declaredSpecialtyCount}/{MAX_SPECIALTIES} תחומים · עד {MAX_SPECIALTY_CHARS} תווים לתחום
            </p>
            {/* Pass 29: this step is framed as presentation, and these tags are the heaviest term
                in the manager's routing score (conversation_service.score_agent_for_message
                weights a matched specialty 3, role title / name 2, description 1) — and an
                employee whose score is 0 is never ranked at all. Disclosed where it is typed. */}
            <p className="text-[11px] text-gray-400 mt-1" data-testid="identity-specialty-routing">
              תחומי ההתמחות הם הגורם בעל המשקל הרב ביותר בבחירת העובד לפנייה — מעל התפקיד והשם, ומעל התיאור. הם אינם מעניקים הרשאה, כלי או אישור.
            </p>
            {identityBounds.overlongSpecialties.length > 0 && (
              <p role="alert" data-testid="identity-specialty-too-long" className="text-xs text-red-600">
                תחום התמחות מוגבל ל-{MAX_SPECIALTY_CHARS} תווים וחורגים ממנו: <span dir="ltr">{identityBounds.overlongSpecialties.join(", ")}</span> — השרת ידחה את הבקשה והעובד לא ייווצר.{draftSubmitted ? ` ${IDENTITY_BOUNDS_SUBMITTED_CLAUSE}` : ""}
              </p>
            )}
            {identityBounds.droppedSpecialties.length > 0 && (
              <p role="alert" data-testid="identity-specialty-dropped" className="text-xs text-amber-700">
                הוצהרו יותר מ-{MAX_SPECIALTIES} תחומי התמחות, ואלה לא יישלחו ולא יישמרו על רשומת העובד: <span dir="ltr">{identityBounds.droppedSpecialties.join(", ")}</span>.{draftSubmitted ? ` ${IDENTITY_BOUNDS_SUBMITTED_CLAUSE}` : ""}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="employee-personality" className="block text-xs text-gray-500 mb-1">אישיות וסגנון עבודה</label>
            <textarea
              id="employee-personality"
              rows={2}
              maxLength={2000}
              value={form.personality}
              onChange={set("personality")}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="ענייני, שקט, מסביר בקצרה ומציע צעד הבא"
            />
          </div>

          <div>
            <label htmlFor="employee-intro-text" className="block text-xs text-gray-500 mb-1">משפט פתיחה בשיחה</label>
            <textarea
              id="employee-intro-text"
              rows={2}
              maxLength={2000}
              value={form.intro_text}
              onChange={set("intro_text")}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="היי, אני דנה מאבטחת מידע. במה אפשר לעזור?"
            />
            {/* Pass 48: the egress paragraph below enumerates four identity fields and this is
                not one of them - correctly, since it reaches no provider - so without its own
                arm the omission reads as "local". It is the one identity field the platform
                posts by itself, verbatim and unredacted. Interpolated, never retyped. */}
            <p className="text-[11px] text-gray-400 mt-1" data-testid="identity-intro-publication">
              {INTRO_TEXT_PUBLICATION_NOTICE}
            </p>
          </div>

          {/* Pass 29: the permission/tool/approval half of this sentence is true and pinned at
              both ends; the "only how the employee is displayed" half was not. The four fields
              this step collects are the entire manager routing score and "offline" is excluded
              from the candidate set, so the step the wizard framed as cosmetic is the only place
              the operator decides what work the employee is selected for. */}
          <p className="text-xs text-gray-400" data-testid="identity-boundary-note">
            פרטי הזהות אינם משנים הרשאות, כלים או מדיניות אישור — אך הם אינם לתצוגה בלבד: תחומי ההתמחות, התפקיד, השם והתיאור הם מה שמדרג את העובד בבחירה לפנייה, ובחירת „{STATUS_META.offline.label}” בזמינות מוציאה אותו מהמועמדים לגמרי.
          </p>
          {/* Pass 47: the note above enumerates the "not presentation-only" consequences and
              names only routing (the ranking, plus the offline exclusion from the candidate set).
              The second consequence is that these same fields become the prompt's identity block
              on every reply the employee gives - and three of the four `persona_of` hands over
              carry no secret validator at all. Both notices are interpolated, never retyped.

              Pass 48: the uncovered-fields clause is a CONTRAST, and this is the surface that
              collects two of the three fields its reassuring term names as checked
              (`personality`, `intro_text`; the third is the step-5 prompt). Pass 46 established
              that the reassurance is bounded - `contains_labelled_secret` fires on a labelled
              value or a recognized format and its own docstring records the gap as deliberate -
              and wrote SECRET_REFUSAL_REACH_NOTICE for exactly that. That qualifier rendered at
              ONE site, the step-8 readiness row: a REPORT surface. Measured in this region
              before this pass: the qualifier 0 renders, and its vocabulary 0 on all six terms
              (תווית / מזוהה / כפי שהוקלד / אינו מזוהה / נדחה בשרת / העובד לא נוצר) against 3 / 5
              / 3 / 2 / 1 / 2 file-wide, while the reassuring term itself rendered right here. So
              the strong reading pass 46 refuted was the only one available at the keyboard. The
              qualifier is interpolated, so this adds no new operator-visible string. */}
          <p className="text-xs text-gray-400" data-testid="identity-model-egress">
            {IDENTITY_MODEL_EGRESS_NOTICE} {IDENTITY_MCP_BRIDGE_EGRESS_NOTICE} {DRAFT_UNCHECKED_SECRET_FIELDS_NOTICE} {SECRET_REFUSAL_REACH_NOTICE}
          </p>
        </div>

        <div className="lg:col-span-2">
          <EmployeeProfileCard form={form} />
        </div>
      </div>
    </div>
  );
}

// ─── Customer scope: a boundary, not a workload question ───
//
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 44). `client_id` is the only field this
// wizard collects whose value is the term every per-customer refusal on the platform is
// conditioned on. routers/agent_runs._agent_scope_mismatch is
// `agent.client_id is not None and agent.client_id != ticket_company_id` and is called at 13
// sites over 5 files: 11 of them refuse (9 raise HTTP 422 with _agent_scope_detail, 2 skip an
// auto-start), one filters the manager's routing candidates and one reports `in_scope` — and
// all 13 are False for a global employee, so none of those refusals can fire. Nine more
// `agent.client_id is not None` terms carry the same condition (scheduler's ticket filter and
// its per-ticket skip, expert_pack_catalog's article scope, recovery_insights' refusal,
// browser_work_executor._connection_serves_customer) and four
// `or_(Agent.client_id == x, Agent.client_id.is_(None))` filters make a global employee a
// routing candidate for every customer. The step that collects it had SIX operator-visible
// prose lines, all about which calls reach the employee, zero data-testid and zero arms on any
// of that — while EMPTY_FORM.client_id is null, so the boundary-free option is preselected and
// reachable with no click at all.
//
// Labelling only, and the ladder is deliberately NOT degraded: a global employee is a designed
// shape (Agent.client_id is nullable and the platform's own pilot agent is normally global), so
// there is nothing here for the operator to fix — only something to know. Unconditional on
// purpose: this is the screen where the choice is MADE, so describing only the arm currently
// selected would hide what the other one does.
export const SCOPE_BOUNDARY_NOTE =
  "הבחירה כאן אינה רק שאלת עומס עבודה: הערך הזה הוא גבול הלקוח שכל סירוב לפי-לקוח בפלטפורמה מותנה בו. בבחירת לקוח ספציפי, פתיחת ריצה, ניתוח או שיחה על פנייה של לקוח אחר נדחים, ובדיקה מתוזמנת שוקלת רק פניות של אותו לקוח. בבחירת כל הלקוחות — ברירת המחדל — אין סירוב כזה: העובד הוא מועמד לניתוב אצל כל לקוח, וניתן לפתוח עבורו ריצה על פנייה של כל לקוח.";

// The second consequence is inside this task's own write: builder_publish_v3 creates
// AgentConnection(agent_id, system_type, display_name) and sets no customer_id, so
// connector_registry.resolve_connection_customer_id falls through to agent.client_id and, for a
// global employee, to integration_layer.PLATFORM_CUSTOMER ("platform"). The scope chosen here
// therefore decides what the published connection's credential resolution is scoped to.
export const SCOPE_PUBLISH_CONNECTION_SCOPE_NOTE =
  "רשומת החיבור שהאשף מפרסם לאחר היצירה גוזרת מכאן את היקף פרטי הגישה שלה: הלקוח שנבחר, או היקף הפלטפורמה כאשר נבחרו כל הלקוחות.";

// The same two facts on the surface that REPORTS the draft, one clause per arm, appended last
// so every sentence pass 20 shaped stays byte-identical.
export const SCOPE_BOUNDARY_ROW_GLOBAL =
  "בחירת הסקופ היא גבול: בלא לקוח ספציפי אין סירוב לפי-לקוח, ורשומת החיבור שתפורסם תיגזר להיקף הפלטפורמה.";
export const SCOPE_BOUNDARY_ROW_SCOPED =
  "בחירת הסקופ היא גבול: פנייה של לקוח אחר תידחה, ורשומת החיבור שתפורסם תיגזר ללקוח הזה.";
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 64), the UNDISCLOSED PERMANENCE: the
// four sentences above state what this value DOES - it is the customer boundary every
// per-customer refusal in the platform is conditional on (pass 44), and it is what the
// published connection record's credential resolution is derived from - and all four have 0
// arms on whether the choice can be changed afterwards. Measured here, not inherited:
//
//   * the SERVICE accepts a re-scope: AgentUpdate carries client_id and update_agent applies
//     every set field under agents_manage. The INTERFACE does not. There is exactly one PATCH
//     of an agent in all of frontend/src - agentsApi.update in services/api.js (control: 13
//     patch calls in that file) - it has exactly 2 product call sites, and neither can carry
//     the field: AgentDetailPage's EditModal seeds a 15-key form with no client_id, and
//     AgentsPage's update mutation sends is_enabled alone. Control, so the sweep is live:
//     client_id IS sent on CREATE, at 2 sites (AgentsPage's NewAgentModal and this wizard).
//   * the row itself is never removed: agentsApi.delete has 0 product consumers (control:
//     agentsApi.update, 2), and the route it would call is disable_agent, which sets
//     is_enabled False and leaves the row where it is. backend/app holds 0 hard deletes of an
//     Agent row; the repo's only raw delete of that table is the pilot seed migration's
//     downgrade, scoped created_by='system-migration', which cannot reach a wizard-created
//     employee.
//
// So the change claim is scoped to the interface and the removal claim names both layers: one
// sentence, two objects, two different gates - exactly the pair pass 62 found swapped, split
// before writing rather than after. Unconditional like its four siblings, because every
// clause is a static property of the platform, so no clause's decider sits outside its own
// condition (pass 53). Nothing is degraded: this is a disclosure, not a severity (pass 21.6),
// and both arms are legitimate - Agent.client_id is nullable and the platform's own pilot
// agent is normally global.
//
// Measured and deliberately NOT rendered, so a later pass weighs it rather than re-measuring:
// delete_company already refuses 409 while any Agent.client_id == company_id, and that count
// carries no is_enabled term, so disabling the employee does not release the customer either.
// Its message instructs the admin to delete those agents or re-assign them, and both are what
// the product cannot do. That is a true, protective refusal about a DIFFERENT subject (the
// customer record) taken by a different actor, so it is recorded against the unowned
// routers/companies.py rather than crowded onto this step - pass 63's rule is to ask of each
// absence whether it is the question the operator is about to bet on, not to fill them all.
export const SCOPE_IMMUTABLE_AFTER_CREATE =
  "סקופ הלקוח נקבע באשף פעם אחת: אין בממשק מסלול לשינוי הערך הזה לאחר היצירה, ולרשומת העובד אין מסלול הסרה — לא בממשק ולא בשירות. לכן הסקופ שנבחר נשאר בתוקף כל עוד העובד קיים.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 65), the UNARMED PARENT. Pass 64 handed
// over the site-set diff on the created EMPLOYEE's own two properties and said to ask pass
// 21.2's question of each absence rather than fill them all. Measured here, over this file:
//
//   * ACTIVATION (the row is created switched off) renders at 6 operator-visible sites - the
//     profile card, the availability note, the `activation` readiness row, the pre-create
//     read-only banner, the unanswered-create notice and the schedule dialog.
//   * NO REMOVAL PATH renders at 4: twice inside SCOPE_IMMUTABLE_AFTER_CREATE, where it is a
//     premise for a claim whose subject is the SCOPE value, and twice inside
//     RESERVED_NAME_IRREVERSIBLE_CLAUSE, which is gated on isReservedSystemName - three of
//     every possible name. Sites whose subject is the created employee: 0.
//
// The two halves are complements and only one of them was stated where the employee is the
// subject. "It is created disabled and does nothing until you activate it from the employee
// page" is the half that makes the single irreversible save read as a deferrable step; the
// withheld half is a RISK, not a reassurance (pass 64's test for direction). Both the
// publish's own products ARE armed on this axis - a registered skill record has no removal
// path, a created connection record can be removed from the employee page - so the one object
// with no statement is the parent both of them hang off, and the one the single creating
// action actually creates.
//
// Premise, measured live rather than inherited and already pinned by
// test_no_route_or_service_removes_an_agent_row (pass 64): backend/app holds 0 hard deletes of
// an Agent row, DELETE /api/agents/{id} is disable_agent (it sets is_enabled False and leaves
// the row), the client's delete member has 0 product consumers against a sibling control of 2,
// and the repo's only raw delete of that table is the pilot seed migration's downgrade, scoped
// created_by='system-migration'. New this pass and the severity argument: on the employee page
// the activation row names, a delete control exists for the connection record and for the
// scheduled check, and none for the employee - so of the three objects this one screen creates,
// the parent is the only one the destination cannot remove.
//
// Rendered at the two sites whose subject is the created employee and nowhere else: the
// `activation` readiness row (state `info`, so neither count moves and the verdict is
// untouched - pass 25's treatment preserved) and the unanswered-create notice, whose own
// errand is to go and look for a duplicate employee. Declined, with the reason, so a later
// pass does not fill them: the profile card and the availability note have a different subject
// (how the employee is displayed, and routing candidacy); the pre-create banner's subject is
// the arity of the write and the readiness row carries the fact on the same screen; and the
// schedule dialog renders after the row exists, where the knowledge is no longer actionable.
// The clause is SLICED from the scope sentence rather than retyped, so the two cannot drift and
// no new operator-visible wording is introduced (pass 62).
export const EMPLOYEE_ROW_NO_REMOVAL_PATH =
  "לרשומת העובד אין מסלול הסרה — לא בממשק ולא בשירות";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 70): the UNGRANTED DESTINATION. Nineteen
// operator-visible lines in this file send the operator to a destination after creation (13 name
// the employee page, 4 the employee's skills page), both of this screen's exits `navigate` to
// `/agents/${createdAgent.id}` themselves, and the arm count on whether the operator can OPEN
// that destination was 0 operator-visible sites against 3 source comments, all of which cite
// `agents_view` for a different argument (pass 46's wider-grant read of `description`, pass 67's
// audit-row audience).
//
// Measured. This wizard admits whoever passes `can("agents_manage")` (below) and the backend
// create and publish routes are both `_manage = require_agent_permission("agents_manage")`.
// `middleware/auth.require_agent_permission` special-cases the ADMIN role for `agents_manage`
// and for nothing else, and `GET /api/agent-permissions/me`'s own docstring states the model:
// OWNER gets all four implicitly, "ADMIN -> 'agents_manage' implicitly + any explicit DB grants
// for the rest", others explicit only. Every destination this file names reads `agents_view`:
// `GET /api/agents/{agent_id}` and `GET /api/agents/` plus skill-assignment-state, skill-lens,
// skills-learning, skill-history, workspace-v2, growth-profile and connection-readiness, nine
// measured routes on `_view`. There is no implication at either layer: `can` is
// `permissions.includes(permission)`, a flat membership test.
//
// So the nav item for this wizard is `adminOnly: true` with no `agentPermission` while the
// `/agents` item is `agentPermission: "agents_view"` and is HIDDEN for that same actor: the one
// role the navigation offers this wizard to is the role for which every destination it names is
// unreachable. The activation is the sharpest member -- the only control that sets `is_enabled`
// is the `is_enabled` checkbox in `AgentDetailPage`'s edit modal, whose write is
// `PATCH /api/agents/{agent_id}` (`agents_manage`, HELD) on a page whose load is `agents_view`
// (not held), so the actor owns the write and cannot reach the control. Same for the one
// `agentConnectionsApi.remove` consumer (`AgentDetailPage.jsx:1129`, the only one in
// `frontend/src`). Pass 64's lesson one step out: a remedy census must check the gate on the
// page that RENDERS the control, not only the route and the writer.
//
// Fixture-shape census, decisive again: all 9 `agentPermissionsApi.me` fixtures in
// `frontend/src` are single-permission, and the 7 that pass this gate are each exactly
// `["agents_manage"]` -- precisely the Admin-by-role shape -- so every test of this feature
// already runs as the actor for whom all 19 errands are a 403, and none said a word about it.
//
// Nothing degraded and no boundary weakened: the verdict ladder and the twelve readiness rows
// are byte-identical, this is an added element at each site rather than an edit to a pinned
// string, and the fix is emphatically NOT giving the Admin role `agents_view` (that would widen
// a read gate -- a hard approval gate, and the acceptance forbids it). The remedy clause is
// reachable for the whole actor set: `POST /api/agent-permissions/grant` admits exactly
// {OWNER, ADMIN-by-role, DB-granted agents_manage}, which is this wizard's own admit set, and
// the `הרשאות AI` nav item is gated `agents_manage`. The third clause states the
// self-escalation guard rather than promising a self-service fix, because a DB-granted
// non-admin is refused granting to THEMSELVES while an Admin is not -- stating it generally
// keeps the sentence true for every reader without a role arm (pass 27: the alarming direction
// is the safe one).
export const BUILDER_GATE_GRANT = "agents_manage";
export const EMPLOYEE_PAGE_VIEW_GRANT = "agents_view";
export const EMPLOYEE_PAGE_NO_ACCESS_WHY =
  `אין לך הרשאה לפתוח את דף העובד: האשף הזה נפתח בהרשאת ${BUILDER_GATE_GRANT}, ודף העובד דורש ${EMPLOYEE_PAGE_VIEW_GRANT} — הרשאה נפרדת, שאינה נכללת בה.`;
export const EMPLOYEE_PAGE_NO_ACCESS_COST =
  `לכן ההפעלה — שעד שתתבצע העובד לא ישובץ לפנייה, לא יריץ ניתוח ולא יצטרף לשיחה — אינה זמינה לך, וגם הסרת רשומת חיבור נעשית משם בלבד; רשומת המיומנות מוצגת בעמוד המיומנויות של העובד, שאף הוא דורש ${EMPLOYEE_PAGE_VIEW_GRANT}.`;
export const EMPLOYEE_PAGE_NO_ACCESS_REMEDY =
  "ההרשאה ניתנת בעמוד „הרשאות AI”; מי שאינו Owner או Admin אינו יכול לתת אותה לעצמו.";
export const EMPLOYEE_PAGE_NO_ACCESS_NOTICE = [
  EMPLOYEE_PAGE_NO_ACCESS_WHY,
  EMPLOYEE_PAGE_NO_ACCESS_COST,
  EMPLOYEE_PAGE_NO_ACCESS_REMEDY,
].filter(Boolean).join(" ");

export function Step2Scope({ form, setForm, companies, companiesFailed = false, onRetryCompanies }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">סקופ לקוחות</h2>
        <p className="text-sm text-gray-500 mt-0.5">האם העובד פועל על כל הלקוחות, או על לקוח ספציפי?</p>
      </div>

      <div
        role="note"
        data-testid="scope-boundary-note"
        className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700 space-y-1"
      >
        <p>{SCOPE_BOUNDARY_NOTE}</p>
        <p>{SCOPE_PUBLISH_CONNECTION_SCOPE_NOTE}</p>
        <p data-testid="scope-immutable-note">{SCOPE_IMMUTABLE_AFTER_CREATE}</p>
      </div>

      {companiesFailed && (
        // the customer list is unknown, and "all customers" is pre-selected: without this an admin who wanted a
        // customer-scoped employee would create a global one without noticing
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          לא ניתן לטעון את רשימת הלקוחות, ולכן אי אפשר לבחור לקוח ספציפי.
          {onRetryCompanies && (
            <button type="button" onClick={onRetryCompanies} className="mr-2 underline">נסה שוב</button>
          )}
        </div>
      )}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setForm((f) => ({ ...f, client_id: null }))}
          className={`w-full text-right rounded-xl border-2 p-4 transition-all ${
            form.client_id === null
              ? "border-brand-600 bg-brand-50"
              : "border-gray-200 hover:border-gray-300 bg-white"
          }`}
        >
          <p className="font-medium text-gray-800 text-sm">🌐 כל הלקוחות</p>
          <p className="text-xs text-gray-500 mt-0.5">העובד פועל על קריאות מכל החברות</p>
        </button>

        {companies.map((company) => (
          <button
            key={company.id}
            type="button"
            onClick={() => setForm((f) => ({ ...f, client_id: company.id }))}
            className={`w-full text-right rounded-xl border-2 p-4 transition-all ${
              form.client_id === company.id
                ? "border-brand-600 bg-brand-50"
                : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
          >
            <p className="font-medium text-gray-800 text-sm">🏢 {company.name}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 51): the SPLICED LIFECYCLE ──
//
// Four operator-visible sites state how a requested skill domain's LEVEL comes to move, and none
// of them said whether that move is ever performed. Measured: SkillRegistry.apply_evaluation is
// the only writer of current_level in backend/app (register() refuses a non-zero level outright),
// and apply_evaluation and record_training_event have ZERO call sites anywhere in backend/app - the
// registry's one live door is register(), reached from exactly one place, this task's own publish
// seam. routers/skills.py is two GETs and the owned services/api.js exposes skillsApi as two GETs
// with no writer. So the record this publish creates is frozen at LEVEL 0 - level 0, evidence_count
// 0, version 1 - and select_for_task, the only reader of a level, had zero callers too.
//
// PROD-SKILLWIRE-017 (FULL-0103) added the ONE status door and nothing else. The §28.4 lifecycle
// STATUS is now movable from the employee skills screen (agents_manage only) through
// routers/skill_lifecycle.py -> skill_assignment_state.transition_status, which is that method's
// single call site; the router itself holds no reference to the registry, it reads and writes
// through the service seam. That move touches neither level nor version, sets no evidence, and
// grants nothing (§28.1), and an active level-0 record is still refused by _is_production_eligible
// - which is why every LEVEL clause below is unchanged. EmployeeSkillsLearningPage still holds zero
// mutations, and EmployeeSkillsPage's one mutation cannot name a level at any layer.
//
// The inverse half sat in the `employee` readiness row, which gave the employee's MEASURED
// proficiency the record's human-review gate. Measured proficiency is
// skills_evaluator._overall_score, a weighted composite of AgentRun counters (60% resolution rate,
// 30% low failure rate, 10% throughput), served by the two skills GETs and rendered in eight
// product files including the employee skills page this very notice points at, with no human input
// at any step. The server's own employee_builder_v3._SKILLS_NOTE keeps the two subjects apart
// ("Governed skills start at level 0 ... move only through human-reviewed evaluations of run
// evidence" / "no measured proficiency can exist before runs do"); the wizard welded the governed
// gate onto the automatic subject, so each clause was true of the OTHER object: a value that moves
// by itself was reported as governed, and a value that cannot move was reported as advancing.
//
// Labelling only, and nothing is degraded (no readiness state, no verdict arm): handoff §28 is a
// deliberately locked partial contract - skill_registry's own docstring says it implements "only
// the explicitly permitted beginnings, not the full Skills system" - and a Skill grants nothing,
// pinned at both ends. There is nothing here for the operator to fix, only something to know
// before the single irreversible action. SKILL_LEVEL_GATE_NOTICE carries the whole fact where the
// request is composed and where the preview restates it; SKILL_LEVEL_GATE_SHORT is the same fact
// as a clause, composed INTO the notice so the long and short forms cannot drift.
export const SKILL_LEVEL_GATE_SHORT =
  "הערכה אנושית מבוקרת שמעלה רמת רשומת מיומנות אינה מתבצעת בגרסה זו באף מסך ובאף נקודת קצה.";
export const SKILL_LEVEL_GATE_NOTICE =
  `רמת רשומת מיומנות עולה רק בהערכה אנושית מבוקרת של ראיות ריצה. ${SKILL_LEVEL_GATE_SHORT} הרשומה שנרשמת בפרסום נוצרת כמועמדת ברמה 0, ורמתה נשארת 0 גם לאחר שסטטוס מחזור החיים שלה (§28.4) ישונה במסך מיומנויות העובד בידי מי שיש לו הרשאת ניהול עובדים — שינוי סטטוס אינו מעלה רמה ואינו מקנה כלי, הרשאה או אישור.`;

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 63), the UNDISCLOSED CERTAINTY. The
// published Skill record has two disclosed properties. Its DURABILITY is stated at four sites --
// the three pre-create ones (this step's note, the skills readiness row, the preview footer) and
// the post-publish panel, which pass 18 built as that family. Its REMOVAL path was stated at
// three, and all three are arms on which the record may not exist at all: the two withdrawn-item
// reversal clauses (pass 60) and the answered-error notice (pass 62). Arm count on the one
// direction where a record CERTAINLY exists -- an applied or already-present outcome -- was 0,
// and so was the pre-create count, so the claim was made only where it might be false.
//
// Measured here rather than inherited: SkillRegistry exposes ten public methods (register, get,
// all, for_employee, record_training_event, apply_evaluation, transition_status, change_log,
// select_for_task, to_safe_dict) and none of them removes a record -- transition_status's
// terminal deprecated status rewrites the record in place and keeps it; routers/skills.py is two
// GETs; the owned skillsApi is two GETs with no writer; EmployeeSkillsPage and
// EmployeeSkillsLearningPage hold zero mutations; and backend/app declares no delete route naming
// a skill. The sentence this clause now joins is also the one that SENDS the operator to the
// employee's skills page, which is precisely where nothing can be done about the record it names.
//
// Direction-neutral and presupposition-free, so the same words are true at the step where the
// request is composed (nothing exists yet) and on the panel where a record has just been
// reported: it is the withdrawn clause's own sentence minus its past-tense relative clause,
// sliced from the product rather than retyped, so this adds no new operator-visible word. Each
// pre-create site keeps its existing arm (at least one governed domain requested) and the panel
// keeps the outcome-state arm that already proves a record exists, so nothing is said on a
// request that registers nothing.
export const SKILL_RECORD_NO_REMOVAL_PATH =
  "לרשומת מיומנות אין מסלול הסרה — לא בממשק ולא בשירות.";

export function Step3Skills({ form, setForm }) {
  const requested = form.requested_skill_domains || [];
  const draft = form.requested_skill_domain_draft || "";
  const atLimit = requested.length >= MAX_REQUESTED_SKILL_DOMAINS;
  const draftTrimmed = draft.trim();
  const draftTooLong = draftTrimmed.length > MAX_SKILL_DOMAIN_CHARS;
  const draftDuplicate = requested.some((d) => d === draftTrimmed);

  const toggleKnown = (domainId) => {
    setForm((f) => {
      const current = f.requested_skill_domains || [];
      if (current.includes(domainId)) {
        return { ...f, requested_skill_domains: current.filter((d) => d !== domainId) };
      }
      if (current.length >= MAX_REQUESTED_SKILL_DOMAINS) return f;
      return { ...f, requested_skill_domains: [...current, domainId] };
    });
  };

  const addDraft = () => {
    const domain = draft.trim();
    if (!domain || domain.length > MAX_SKILL_DOMAIN_CHARS) return;
    setForm((f) => {
      const current = f.requested_skill_domains || [];
      if (current.length >= MAX_REQUESTED_SKILL_DOMAINS || current.includes(domain)) return f;
      return { ...f, requested_skill_domains: [...current, domain], requested_skill_domain_draft: "" };
    });
  };

  const removeDomain = (domain) => setForm((f) => ({
    ...f,
    requested_skill_domains: (f.requested_skill_domains || []).filter((d) => d !== domain),
  }));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">מיומנויות מבוקשות</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          סמן את תחומי המומחיות המפוקחים שהעובד אמור לפתח (מילון §28), או הוסף תחום חופשי
        </p>
      </div>

      <div role="note" className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700">
        יצירת העובד עצמה <strong>אינה משייכת רשומת מיומנות</strong>. עם סיום האשף הבקשות מפורסמות
        במסלול המבוקר: תחום מפוקח נרשם כרשומת מועמדת ברמה 0 (ללא הרשאה), ותחום שאינו נתמך מדווח במפורש.
        {" "}{SKILL_LEVEL_GATE_NOTICE} בקשה אינה מאשרת, משייכת או מעניקה דבר.
        {" "}<span data-testid="skills-step-durability">
          רשומת המיומנות נרשמת במרשם ההקצאות של השירות ולא בבסיס הנתונים — היא מוצגת בעמוד
          המיומנויות של העובד, ושורדת הפעלה מחדש של השירות רק כאשר מאגר המצב של השירות זמין בשרת; רשומת חיבור פר-עובד, לעומת זאת, נשמרת
          בבסיס הנתונים — אך היא נוצרת רק עבור חלק מהמערכות המוצעות, ומערכת מנוהלת פלטפורמית אינה
          יוצרת רשומת חיבור כלל.
          {" "}<span data-testid="skills-step-removal-path">{SKILL_RECORD_NO_REMOVAL_PATH}</span>
        </span>
      </div>

      <div className="space-y-2">
        {GOVERNED_SKILL_DOMAINS.map((domain) => {
          const selected = requested.includes(domain.id);
          return (
            <label
              key={domain.id}
              className={`flex items-center justify-between rounded-xl border-2 p-3 cursor-pointer transition-all ${
                selected ? "border-brand-600 bg-brand-50" : "border-gray-200 hover:border-gray-300 bg-white"
              } ${atLimit && !selected ? "opacity-60" : ""}`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={atLimit && !selected}
                  onChange={() => toggleKnown(domain.id)}
                  className="h-4 w-4 rounded text-brand-600 cursor-pointer disabled:cursor-not-allowed"
                />
                <span className="text-sm text-gray-800">{domain.label}</span>
              </div>
              <span dir="ltr" className="text-[11px] font-mono text-gray-400">{domain.id}</span>
            </label>
          );
        })}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
        <p className="text-xs font-medium text-gray-500">
          תחומים מבוקשים ({requested.length}/{MAX_REQUESTED_SKILL_DOMAINS})
        </p>
        {requested.length === 0 ? (
          <p className="text-xs text-gray-400 italic">טרם בוקשו תחומי מיומנות — גם ללא בקשה, היצירה אינה משייכת מיומנות.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {requested.map((domain) => {
              const recognized = isGovernedDomain(domain);
              return (
                <li key={domain} className="flex items-center gap-1.5 rounded-full border bg-white px-2 py-1">
                  <span dir="ltr" className="text-xs text-gray-700">{domain}</span>
                  <span
                    className={`text-[10px] rounded-full px-1.5 py-0.5 ${
                      recognized ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {recognized ? "במילון המפוקח" : "מחוץ למילון — לא ייתמך, לא ייווצר דבר"}
                  </span>
                  <button
                    type="button"
                    aria-label={`הסר ${domain}`}
                    onClick={() => removeDomain(domain)}
                    className="text-gray-400 hover:text-red-600 text-xs"
                  >
                    ✕
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(e) => setForm((f) => ({ ...f, requested_skill_domain_draft: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDraft(); } }}
            placeholder="תחום נוסף…"
            dir="ltr"
            maxLength={MAX_SKILL_DOMAIN_CHARS + 20}
            className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono"
            disabled={atLimit}
          />
          <button
            type="button"
            onClick={addDraft}
            disabled={atLimit || !draftTrimmed || draftTooLong || draftDuplicate}
            className="border px-3 py-2 rounded-lg text-sm hover:bg-white disabled:opacity-40"
          >
            הוסף
          </button>
        </div>
        {draftTooLong && <p role="alert" className="text-xs text-red-600">תחום מוגבל ל-{MAX_SKILL_DOMAIN_CHARS} תווים.</p>}
        {draftDuplicate && <p role="alert" className="text-xs text-red-600">התחום כבר מבוקש.</p>}
        {atLimit && <p className="text-xs text-gray-400">הגעת למספר המרבי של תחומים מבוקשים.</p>}
      </div>
    </div>
  );
}

export function Step4Model({ form, setForm }) {
  const { data: health, isError: healthFailed } = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: connectionsApi.health,
    staleTime: 60000,
  });
  const readiness = providerReadiness(health);
  const loading = !health && !healthFailed;
  const selectedModel = MODEL_OPTIONS.find((m) => m.value === form.model);
  const alignment = modelProviderAlignment(selectedModel, readiness);
  const misaligned = alignment === "mismatched";

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">מודל וספק</h2>
        <p className="text-sm text-gray-500 mt-0.5">בחר את המודל שיריץ את העובד ועיין במוכנות ספק ה-AI של המערכת</p>
      </div>
      <div className="space-y-2">
        {MODEL_OPTIONS.map((m) => (
          <button
            key={m.value}
            type="button"
            aria-pressed={form.model === m.value}
            onClick={() => setForm((f) => ({ ...f, model: m.value }))}
            className={`w-full text-right rounded-xl border-2 p-4 transition-all ${
              form.model === m.value
                ? "border-brand-600 bg-brand-50"
                : "border-gray-200 hover:border-gray-300 bg-white"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="font-medium text-gray-800 text-sm">{m.label}</p>
              <span className="text-[10px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">ספק: {m.provider}</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
            <p className="text-[11px] font-mono text-gray-400 mt-0.5" dir="ltr">{m.value}</p>
          </button>
        ))}
      </div>

      <div
        role={readiness.state === "not_configured" || misaligned ? "alert" : "note"}
        data-testid="provider-readiness"
        className={`rounded-lg border p-3 text-xs ${
          loading || healthFailed || readiness.state === "unknown" || readiness.state === "unreadable"
            ? "border-gray-200 bg-gray-50 text-gray-500"
            : readiness.state === "configured" && !misaligned
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-amber-200 bg-amber-50 text-amber-700"
        }`}
      >
        {loading ? (
          "בודק את מוכנות ספק ה-AI של המערכת…"
        ) : readiness.state === "unreadable" ? (
          // pass 54: the payload ARRIVED and carries no readable ai_analysis, so the arm below --
          // which says the status did not load -- is false here, and the not-ready arm at the
          // bottom would invent a cause. Its own arm, and the three surfaces share one constant.
          PROVIDER_STATUS_UNREADABLE_NOTICE
        ) : healthFailed || readiness.state === "unknown" ? (
          "לא ידוע — סטטוס ספק ה-AI לא נטען, ולכן אין אישור שהמערכת מוגדרת להריץ את המודל."
        ) : readiness.state === "configured" ? (
          // The promise cannot be unqualified: the provider is the deployment's and the model name
          // is the employee's, so a cross-vendor pair fails at the first provider call, and a
          // HIGH-priority ticket is routed to the deployment's override model instead of this one.
          <>
            ספק ה-AI של המערכת ({readiness.provider || "לא צוין"}) מוכן.{" "}
            {misaligned
              ? `אך המודל שנבחר (${selectedModel.label}) הוא של ספק ${selectedModel.provider}: הספק נקבע בהגדרת המערכת ולא ברשומת העובד, ולכן הרצות העובד ייכשלו ולא יעברו למודל אחר. בחר מודל של ${readiness.provider} או עדכן את הגדרת הספק במערכת.`
              : "הרצות העובד ישתמשו בשם המודל שיבחר כאן, מול ספק המערכת; כרטיס בעדיפות גבוהה עשוי להינתב למודל ההחלפה של המערכת."}
          </>
        ) : (
          <>ספק ה-AI של המערכת אינו מוכן (ספק, מודל או מפתח חסרים) — עד להשלמת ההגדרה, הרצות העובד לא ישתמשו במודל שנבחר.</>
        )}
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">הוראות הפעלה — System prompt (ניתן לשינוי לאחר יצירה)</label>
        <textarea
          rows={8}
          value={form.system_prompt}
          onChange={(e) => setForm((f) => ({ ...f, system_prompt: e.target.value }))}
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
          dir="ltr"
          placeholder="You are a helpful IT support agent..."
        />
        <p className="text-[11px] text-gray-400 mt-1">
          {form.system_prompt.length} תווים · ההוראות מנחות ריצות ואינן מעניקות הרשאה, כלי או אישור.
        </p>
        {/* DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 43). Pass 33 measured that the
            one runtime reading these instructions hands the model no tool (tools=/tool_choice/
            tool_use is 0 in direct_llm_analyst.py against same-file controls messages= 2 and
            system= 1), nothing but analyze(ticket_title, ticket_description, customer_context),
            and returns a fixed JSON schema. It armed the two surfaces that REPORT this text --
            the `role` readiness row and `preview-role`, both inside Step7Preview. The surface
            that COLLECTS it had 0 arms on that axis, and the sentence above is this file's only
            positive claim about what the text DOES. Pass 33's own words for the disclosures it
            found insufficient fit it exactly: both TRUE and both about GRANTING, neither saying
            whether the instructions are CARRIED OUT.

            Unconditional, unlike the copy in `preview-role`: that one reports a declared draft,
            so keying it on `promptText` keeps the no-prompt arm byte-identical. Here the claim
            being qualified renders with an EMPTY textarea too, which is the moment before the
            operator types -- and the templates seed this very box, so the text pass 33 censused
            is what sits here. Keying this on the draft would leave the unqualified claim alone
            on the empty arm, which is the sign-flipped twin probe N2. */}
        <p className="text-[11px] text-gray-400 mt-1" data-testid="model-prompt-runtime-note">
          {PROMPT_RUNTIME_NOTE}
        </p>
      </div>
    </div>
  );
}

export function Step5Systems({ form, setForm }) {
  const { data: health, isError: healthFailed } = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: connectionsApi.health,
    staleTime: 60000,
  });
  const { data: connectorTypes, isError: connectorsFailed } = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: agentConnectionsApi.connectorTypes,
    staleTime: 300000,
  });

  const toggle = (id) =>
    setForm((f) => ({
      ...f,
      tools: f.tools.includes(id)
        ? f.tools.filter((t) => t !== id)
        : [...f.tools, id],
    }));

  const implementedConnectors = Array.isArray(connectorTypes)
    ? connectorTypes.filter((c) => c.implemented)
    : null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">מערכות וחיבורים</h2>
        <p className="text-sm text-gray-500 mt-0.5">בחר אילו מערכות העובד מורשה להשתמש בהן — סטטוס ההגדרה נבדק מול המערכת</p>
      </div>
      <div className="space-y-3">
        {ENV_TOOLS.map((tool) => {
          const selected = form.tools.includes(tool.id);
          const status = healthFailed
            ? { state: "unknown", detail: null }
            : !health
            ? { state: "loading", detail: null }
            : envToolStatus(health, tool.id);
          const meta = ENV_STATUS_META[status.state] || ENV_STATUS_META.unknown;
          const scopeMeta = SYSTEM_PUBLISH_SCOPE_META[systemPublishScope(tool.id)];
          return (
            <div
              key={tool.id}
              className={`rounded-xl border-2 p-4 transition-all ${
                selected ? "border-brand-600 bg-brand-50" : "border-gray-200 bg-white"
              }`}
            >
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => toggle(tool.id)}
                className="w-full text-right"
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl mt-0.5">{tool.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <p className="font-medium text-gray-800 text-sm">{tool.label}</p>
                      {selected && <span className="text-xs text-brand-600 font-medium">✓ נבחר</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{tool.desc}</p>
                    <p className="text-xs text-gray-400 mt-0.5 italic">{tool.note}</p>
                  </div>
                </div>
              </button>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${meta.cls}`}>
                  {meta.label}
                </span>
                {status.detail && <span className="text-[10px] text-gray-400">{status.detail}</span>}
                <span
                  data-testid={`env-tool-publish-scope-${tool.id}`}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${scopeMeta.cls}`}
                >
                  {scopeMeta.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div data-testid="connector-catalog" className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
        {connectorsFailed ? (
          <span className="text-gray-500">קטלוג הקישוריות לא נטען — מספר הספקים הרשומים אינו ידוע.</span>
        ) : !implementedConnectors ? (
          <span className="text-gray-500">טוען את קטלוג הקישוריות…</span>
        ) : (
          <>
            קטלוג הקישוריות: {connectorTypes.length} ספקים רשומים, {implementedConnectors.length} ממומשים.
            {implementedConnectors.length > 0 && (
              <span className="text-gray-500"> ({implementedConnectors.map((c) => c.display_name).join(", ")})</span>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-gray-400">
        בחירה כאן מוסיפה לעובד הנחיות שימוש בלבד. עם יצירת העובד האשף מפרסם את הבקשה במסלול המבוקר:
        מערכת נתמכת מקבלת רשומת חיבור ללא פרטי גישה (השדות נשארים להגדרת מנהל), ומערכת מנוהלת-פלטפורמית
        או שאינה מוכרת מדווחת במפורש. לא נשמר מפתח, ובדיקת חיבור חיה מתבצעת בנפרד מדף העובד לאחר היצירה.
      </p>
    </div>
  );
}

// ─── DIGEMP-141 pass 52: what choosing a permission tier does to a restricted tool list ─────
// `allowed_tools` is COLLECTED by ToolsSelector below, one checkbox per tool the tier allows, and
// the operator restricts it by unchecking. Four sites in this file write `allowed_tools: null`.
// Three are accounted for: the role preset card (pass 30 discloses it unconditionally and then
// names the operator's own list in `role-preset-overwrite-warning`), the selector's own
// "כל הכלים" button (the operator asking for exactly that), and EMPTY_FORM's default. The fourth
// is the permission-tier card's own handler, which assigns `allowed_tools: null` beside
// `permission_tier` — and that was stated nowhere. Absence sweep over this file's own vocabulary
// for a discarded value (the roots בטל / אפס / חליף / מחק / אבד / דרס): 0 occurrences in
// Step6Limits, 13 file-wide, of which 3 are Step1Role disclosing this very field's other writer.
// The one ToolsSelector hit is a near-miss on a different subject (the last box cannot be
// unchecked, because an empty list means all tools), and 0 of the hits also name the tier.
//
// Measured through the real gate (employee_workspace.entitled_tools_for over tool_registry's 31
// definitions, 15 read_only / 7 write / 5 execute / 2 delete / 2 admin):
//   tier_1 — the selector offers 15 boxes and ALL 15 are registry read-only. check_entitlement's
//            allowlist gate (step 2d) never withholds a read-only tool, so the reachable set is
//            15 whether the list names one box or every box. The reset is inert here.
//   tier_2 — the selector offers 26 boxes, 11 of them state-changing. Unrestricted reaches 26;
//            leaving a single box checked reaches 15 (a read-only box) or 16 (a state-changing
//            one). So the reset re-grants up to 11 tools, and they are exactly the customer-facing
//            and customer-device writes pass 31 named: add_comment, add_ticket_comment,
//            close_ticket, escalate, install_software, reset_user_password, restart_device,
//            restart_service, run_diagnostic_script, send_customer_message,
//            send_email_to_customer. Every one of the 11 stays approval-bound in every autonomy
//            mode (pass 31's 0 of 88), so this widens what the employee may PROPOSE, never what it
//            may do unilaterally.
// The loss is therefore silent, it is an authority WIDENING, and it is tier_2-only — reachable
// from the one of nine role presets that sets tier_2, or from a click on the tier_2 card.
//
// Two arms, and the second is why the note names the tier that is already selected: the handler
// has NO same-tier guard, while `aria-pressed={form.permission_tier === tier.value}` — the exact
// predicate such a guard would need — is evaluated on the line directly above it. Clicking the
// card that is already pressed is not a transition at all, so the one justification for clearing
// the list (its names are scoped to the tier being left) does not apply to that click.
// v3Wizard's pre-existing test is titled "a tier switch clears it" and exercises tier_1 -> tier_2;
// the same-tier click was exercised by nothing, so the fact was pinned in a test name and rendered
// to the operator at zero sites.
//
// Closed by disclosure, not by guarding: the write happens on the operator's own click on an
// authority field, the step-7 preview reports the widened set honestly through the same gate, and
// changing what that handler writes is a behaviour change past labelling the gap. The same-tier
// arm is pinned in v3Wizard as the reason this sentence names it, so a later pass that adds the
// guard reds that test rather than leaving this prose quietly false.
export const TIER_ALLOWLIST_RESET_NOTE =
  "בחירת רמת הרשאה מבטלת רשימת כלים מצומצמת אם הוגדרה, ומחזירה גישה לכל הכלים שהרמה מתירה. " +
  "גם לחיצה על הרמה שמסומנת כבר עושה זאת, אף שאינה מעבר בין רמות.";

// Mirrors the role preset's warning one step back: a lead naming the click, the operator's OWN
// value as a bullet, and the remedy. The bullet interpolates ROLE_PRESET_OVERWRITE_LABELS so the
// two surfaces that can discard this list cannot drift apart in how they name the loss.
export const TIER_ALLOWLIST_RESET_LEAD =
  "לחיצה על כרטיס רמה עכשיו — גם על הרמה שמסומנת כבר — תבטל את הבחירה הזו:";

export const TIER_ALLOWLIST_RESET_REMEDY =
  "כדי לשמר אותה, המשיכו בלי ללחוץ על כרטיס רמה.";

function ToolsSelector({ tier, form, setForm }) {
  const { data: tools = [] } = useQuery({
    queryKey: ["agent-tools"],
    queryFn: agentsApi.listTools,
    staleTime: 300000,
  });
  const visible = tools.filter((t) => t.allowed_tiers.includes(tier));
  if (!visible.length) return null;

  const allNames = visible.map((t) => t.name);
  const isUnrestricted = isUnrestrictedAllowlist(form.allowed_tools);
  const selectedSet = isUnrestricted
    ? new Set(allNames)
    : new Set(form.allowed_tools);

  const toggle = (toolName) => {
    const next = new Set(isUnrestricted ? allNames : form.allowed_tools);
    if (next.has(toolName)) next.delete(toolName);
    else next.add(toolName);
    // allowed_tools=[] is NOT "no tools" on the backend — it means the SAME as null, unrestricted
    // (autonomy_policy: only enforced when non-empty). Unchecking the last box would silently grant
    // full access while the UI re-checked every box, looking like nothing happened.
    if (next.size === 0) return;
    const arr = [...next];
    setForm((f) => ({ ...f, allowed_tools: arr.length === allNames.length ? null : arr }));
  };

  const tierLabel = PERMISSION_TIER_OPTIONS.find((o) => o.value === tier)?.label ?? tier;
  const isRestricted = !isUnrestricted;

  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-1.5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-gray-500">
          כלים ב-{tierLabel}
          {isRestricted && (
            <span className="mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
              {form.allowed_tools.length}/{allNames.length} נבחרו
            </span>
          )}
        </p>
        <div className="flex gap-2 text-[11px]">
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, allowed_tools: null }))}
            className="text-blue-600 hover:underline"
          >
            כל הכלים
          </button>
        </div>
      </div>
      {visible.map((t) => {
        const isLastSelected = selectedSet.size === 1 && selectedSet.has(t.name);
        return (
        <label
          key={t.name}
          className="flex items-center justify-between text-xs cursor-pointer gap-2 hover:bg-white rounded px-1 py-0.5 transition-colors"
          title={isLastSelected ? "לא ניתן לבטל את הכלי האחרון — רשימה ריקה פירושה גישה לכל הכלים, לא לאף אחד" : undefined}
        >
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedSet.has(t.name)}
              disabled={isLastSelected}
              onChange={() => toggle(t.name)}
              className="h-3.5 w-3.5 rounded text-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
            />
            <span className="text-gray-700">{t.name}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${RISK_CLS[t.risk_level] || "bg-gray-100 text-gray-600"}`}>
              {riskGradeLabel(t.risk_level)}
            </span>
            {t.strong_approval_required && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">Step-up</span>
            )}
            {t.approval_required && !t.strong_approval_required && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">אישור</span>
            )}
          </div>
        </label>
        );
      })}
    </div>
  );
}

export function Step6Limits({ form, setForm, draftSubmitted = false, sentPayload = null }) {
  const { data: tools, isError: toolsFailed } = useQuery({
    queryKey: ["agent-tools"],
    queryFn: agentsApi.listTools,
    staleTime: 300000,
  });
  const projection = entitlementProjection(tools, form.permission_tier, form.allowed_tools);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">הרשאות ומגבלות</h2>
        <p className="text-sm text-gray-500 mt-0.5">{draftSubmitted ? LIMITS_SUBTITLE_SUBMITTED : LIMITS_SUBTITLE_DRAFT}</p>
      </div>

      {draftSubmitted && (
        <p
          data-testid="limits-draft-submitted-notice"
          role="status"
          className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700"
        >
          {limitsSubmittedNotice(sentPayload)}
        </p>
      )}

      {/* Permission Tier */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">רמת הרשאה (Permission Tier)</p>
        <div className="space-y-2">
          {PERMISSION_TIER_OPTIONS.map((tier) => (
            <button
              key={tier.value}
              type="button"
              disabled={tier.disabled}
              aria-pressed={form.permission_tier === tier.value}
              onClick={() => !tier.disabled && setForm((f) => ({ ...f, permission_tier: tier.value, allowed_tools: null }))}
              className={`w-full text-right rounded-xl border-2 p-4 transition-all ${
                tier.disabled
                  ? "border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed"
                  : form.permission_tier === tier.value
                  ? "border-brand-600 bg-brand-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  tier.value === "tier_1" ? "bg-green-100 text-green-700" :
                  tier.value === "tier_2" ? "bg-amber-100 text-amber-700" :
                  "bg-red-100 text-red-700"
                }`}>{tier.badge}</span>
                <p className="font-medium text-gray-800 text-sm">{tier.label}</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">{tier.desc}</p>
            </button>
          ))}
        </div>
        <div
          role="note"
          data-testid="limits-tier-allowlist-reset-note"
          className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700"
        >
          {TIER_ALLOWLIST_RESET_NOTE}
        </div>
        {!isUnrestrictedAllowlist(form.allowed_tools) && (
          <div
            role="alert"
            data-testid="limits-tier-allowlist-reset-warning"
            className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 space-y-1"
          >
            <p className="font-medium">{TIER_ALLOWLIST_RESET_LEAD}</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>{ROLE_PRESET_OVERWRITE_LABELS.allowed_tools(String(form.allowed_tools.length))}</li>
            </ul>
            <p className="text-[11px]">{TIER_ALLOWLIST_RESET_REMEDY}</p>
          </div>
        )}
        <ToolsSelector tier={form.permission_tier || "tier_1"} form={form} setForm={setForm} />
      </div>

      {/* Entitlement projection (live) */}
      <div data-testid="limits-projection" className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-1.5">
        {toolsFailed ? (
          <p className="text-xs text-gray-500">לא ידוע — קטלוג הכלים לא נטען, ולכן מספר הכלים הזמינים אינו ידוע.</p>
        ) : !projection ? (
          <p className="text-xs text-gray-500">טוען את קטלוג הכלים…</p>
        ) : (
          <>
            <p className="text-xs font-medium text-gray-500 mb-1">תחזית כלים ברמה זו (לפי קטלוג הכלים החי)</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-700">
              <li>מורשים כעת: <strong>{projection.entitled}</strong></li>
              <li>נגישים רק באישור מוגבר (Step-up): <strong>{projection.strongGated}</strong></li>
              <li>חסומים ברמה/ברשימה זו: <strong>{projection.blocked}</strong></li>
              <li>רשומים בקטלוג: <strong>{projection.registered}</strong></li>
            </ul>
            {projection.listExempt > 0 && (
              <p className="text-[11px] text-amber-700" data-testid="limits-allowlist-exempt">
                הרשימה המצומצמת מגבילה כלי שינוי-מצב בלבד — {projection.listExempt} כלים לקריאה בלבד נשארים נגישים גם ללא סימון, תחת כל רשימה.
              </p>
            )}
            <p className="text-[11px] text-gray-400">
              תחזית קריאה בלבד — אינה מעניקה הרשאה; כלי שינוי-מצב נשארים תחומי-אישור גם כשהם נגישים.
            </p>
          </>
        )}
      </div>

      {/* Autonomy Level */}
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">מדיניות אישור (Autonomy)</p>
        <div className="space-y-2">
          {AUTONOMY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={form.autonomy_level === opt.value}
              onClick={() => setForm((f) => ({ ...f, autonomy_level: opt.value }))}
              className={`w-full text-right rounded-xl border-2 p-4 transition-all ${
                form.autonomy_level === opt.value
                  ? "border-brand-600 bg-brand-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
            >
              <p className="font-medium text-gray-800 text-sm">{opt.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
            </button>
          ))}
        </div>
        <div data-testid="limits-autonomy-effect" className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-1">
          <p className="text-[11px] text-gray-600">{AUTONOMY_EQUIVALENCE_NOTE}</p>
          <p className="text-[11px] text-gray-600">
            {toolsFailed
              ? AUTONOMY_EFFECT_UNKNOWN
              : !projection
              ? "טוען את קטלוג הכלים…"
              : autonomyEffectSentence(projection)}
          </p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
        💡 בשלב הפיילוט מומלץ להשאיר רמה 1 (קריאה בלבד) — היא זו שקובעת לאילו כלים העובד מגיע. מדיניות האישור אינה מוסיפה אישור על קריאה בשום אפשרות, ולכן בחירה בה אינה מצמצמת את ההרשאות. הרשאות גדולות יותר מופעלות לאחר הוכחת ביצועים.
      </div>
    </div>
  );
}

// ─── Preview step (v3: eight domains + readiness) ────────────────────────────

const PREVIEW_TONES = {
  indigo:  { box: "border-indigo-200 bg-indigo-50/50",  heading: "text-indigo-800",  chip: "border-indigo-200 text-indigo-600 bg-white/70" },
  amber:   { box: "border-amber-200 bg-amber-50/50",    heading: "text-amber-800",   chip: "border-amber-200 text-amber-700 bg-white/70" },
  violet:  { box: "border-violet-200 bg-violet-50/50",  heading: "text-violet-800",  chip: "border-violet-200 text-violet-700 bg-white/70" },
  blue:    { box: "border-blue-200 bg-blue-50/50",      heading: "text-blue-800",    chip: "border-blue-200 text-blue-700 bg-white/70" },
  cyan:    { box: "border-cyan-200 bg-cyan-50/50",      heading: "text-cyan-800",    chip: "border-cyan-200 text-cyan-700 bg-white/70" },
  teal:    { box: "border-teal-200 bg-teal-50/50",      heading: "text-teal-800",    chip: "border-teal-200 text-teal-700 bg-white/70" },
  rose:    { box: "border-rose-200 bg-rose-50/50",      heading: "text-rose-800",    chip: "border-rose-200 text-rose-700 bg-white/70" },
  emerald: { box: "border-emerald-200 bg-emerald-50/50", heading: "text-emerald-800", chip: "border-emerald-200 text-emerald-700 bg-white/70" },
};

function PreviewSection({ tone, testId, title, state, editStep, onEditStep, children }) {
  const t = PREVIEW_TONES[tone];
  return (
    <section aria-label={title} data-testid={testId} className={`rounded-xl border-2 p-4 space-y-2 ${t.box}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className={`text-sm font-semibold ${t.heading}`}>{title}</h3>
        {state && <span className={`text-[10px] rounded-full border px-1.5 py-0.5 ${t.chip}`}>{state}</span>}
        {editStep && onEditStep && (
          <button
            type="button"
            data-testid={`preview-edit-${testId.replace("preview-", "")}`}
            onClick={() => onEditStep(editStep)}
            className="text-[11px] text-brand-600 hover:underline mr-auto"
          >
            עריכה ←
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

const READINESS_STATE_META = {
  ok:      { icon: "✓", cls: "text-green-600",  word: "תקין" },
  warn:    { icon: "!", cls: "text-amber-600",  word: "לתשומת לב" },
  unknown: { icon: "?", cls: "text-gray-400",   word: "לא ידוע" },
  info:    { icon: "·", cls: "text-blue-500",   word: "מידע" },
};

function ReadinessRow({ state, label, detail }) {
  const meta = READINESS_STATE_META[state] || READINESS_STATE_META.info;
  return (
    <li className="flex items-start gap-2">
      <span aria-hidden="true" className={`font-bold w-4 text-center ${meta.cls}`}>{meta.icon}</span>
      <div>
        <span className="text-gray-700">{label}</span>
        <span className="text-[10px] text-gray-400 mr-1.5">{meta.word}</span>
        {detail && <p className="text-[11px] text-gray-500">{detail}</p>}
      </div>
    </li>
  );
}

export function Step7Preview({ form, companies, companiesFailed = false, onEditStep, createFailed = false, createUnconfirmed = false, canOpenEmployeePage = true }) {
  const { data: tools, isError: toolsFailed } = useQuery({
    queryKey: ["agent-tools"],
    queryFn: agentsApi.listTools,
    staleTime: 300000,
  });
  const { data: agents, isError: agentsFailed } = useQuery({
    queryKey: ["agents"],
    queryFn: agentsApi.list,
  });
  const { data: health, isError: healthFailed } = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: connectionsApi.health,
    staleTime: 60000,
  });
  const { data: connectorTypes, isError: connectorsFailed } = useQuery({
    queryKey: ["agentConnectorTypes"],
    queryFn: agentConnectionsApi.connectorTypes,
    staleTime: 300000,
  });

  const preset = ROLE_PRESETS.find((p) => p.id === form.role_preset);
  const company = companies.find((c) => c.id === form.client_id);
  const tier = PERMISSION_TIER_OPTIONS.find((t) => t.value === form.permission_tier);
  const autonomy = AUTONOMY_OPTIONS.find((a) => a.value === form.autonomy_level);
  const model = MODEL_OPTIONS.find((m) => m.value === form.model);
  const specialties = parseSpecialties(form.specialties_text);
  const requestedDomains = form.requested_skill_domains || [];
  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: a domain typed into the skills step's
  // free-text box and never added with "הוסף" is part of NO request -- it reaches neither the
  // create payload nor the governed publish intent. Of the twenty fields the wizard collects it
  // is the only one holding operator-authored text that no sent field reads (the two presets
  // only seed other fields), and until here nothing outside step 4 mentioned it: the create
  // payload's test tree pins that it is not sent, and nothing told the operator. State it on the
  // confirmation surface, before the single creating action, instead of dropping it silently.
  // ...but a draft that is ALREADY in the request was not dropped, and saying so is the same
  // defect sign-flipped: the skills step disables "הוסף" for a duplicate (correctly — there is
  // nothing to add), so the text has no way to leave the box, and warning here would tell the
  // operator that a domain listed as requested one line above will not be published or reported,
  // and would stop the verdict short of "ready" over it. The offered fix ("add it") is the one
  // thing that cannot be done. Same comparison the skills step itself uses to refuse the add —
  // exact and case-sensitive, so a case variant (addable, therefore genuinely droppable) still
  // warns.
  const skillDraftText = (form.requested_skill_domain_draft || "").trim();
  const pendingSkillDraft = requestedDomains.includes(skillDraftText) ? "" : skillDraftText;
  const promptText = form.system_prompt.trim();
  // Pass 53: the value the single creating action SENDS, not the raw field — `handleNext` sends
  // `form.role_title.trim() || undefined`, so a whitespace-only role title is no role title and
  // the row must not print it as one (the collecting surface's own card already folds it this
  // way at `EmployeeProfileCard`). This replaces a `form.role_title || preset?.label` read.
  const declaredRoleTitle = (form.role_title || "").trim();
  // Pass 22: what the server will refuse outright, and what the shared parser discards on the way
  // into the request. Both are determinate before the single creating action, so both belong in
  // the `warn` bucket of the verdict - a certain refusal is not missing evidence (pass 19).
  const bounds = draftBoundViolations(form);
  const boundsRefused = bounds.nameTooLong || bounds.promptTooLong || bounds.overlongSpecialties.length > 0;
  const boundsViolated = boundsRefused || bounds.droppedSpecialties.length > 0;
  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 28): whether the wizard itself adds
  // text to the operating instructions it sends. Derived from the composition's own output
  // (bounds.sentPromptChars comes from composeSystemPrompt) rather than from form.tools, so a
  // change to what is appended cannot leave the disclosure behind.
  const promptLineAppended = bounds.sentPromptChars > promptText.length;

  const projection = entitlementProjection(tools, form.permission_tier, form.allowed_tools);
  const unrestricted = isUnrestrictedAllowlist(form.allowed_tools);
  const implementedConnectors = Array.isArray(connectorTypes)
    ? connectorTypes.filter((c) => c.implemented)
    : null;

  // ── Live statuses: loaded / failed / loading are three distinct truths ──
  const toolsFailedNow = toolsFailed;
  const toolsLoading = !tools && !toolsFailed;
  const agentsFailedNow = agentsFailed;
  const agentsLoading = !agents && !agentsFailed;
  const healthFailedNow = healthFailed;
  const healthLoading = !health && !healthFailed;
  const connectorsFailedNow = connectorsFailed;

  const envToolStates = ENV_TOOLS.map((tool) => ({
    tool,
    selected: form.tools.includes(tool.id),
    status: healthFailedNow
      ? { state: "unknown", detail: null }
      : health
      ? envToolStatus(health, tool.id)
      : { state: "loading", detail: null },
    publishScope: systemPublishScope(tool.id),
  }));
  const requestedTools = envToolStates.filter((t) => t.selected);
  // PROD-BUILDER-060: the per-item forecast shown before the single creating action. The
  // deployment-configuration evidence is passed in only to qualify a platform-managed system;
  // it can never turn one into a per-employee connection record.
  const environmentStatusByTool = Object.fromEntries(
    envToolStates.map(({ tool, status }) => [tool.id, status.state])
  );
  const publishForecast = builderPublishForecast(form, environmentStatusByTool);
  // What the governed publish will actually create, per SYSTEM_PUBLISH_SCOPE. Independent of the
  // deployment-configuration ladder below: a platform-managed system that IS configured still
  // yields no per-employee connection record.
  const connectionCreatingTools = requestedTools.filter((t) => t.publishScope === "supported");
  const noRecordTools = requestedTools.filter((t) => t.publishScope !== "supported");

  const connectionsState = requestedTools.length === 0
    ? "none_requested"
    : healthFailedNow || healthLoading
    ? "unknown"
    : requestedTools.some((t) => t.status.state === "not_configured")
    ? "not_configured"
    : requestedTools.some((t) => t.status.state === "unknown")
    ? "unknown"
    : "all_configured";

  const readiness = providerReadiness(health);
  // Two independent truths about the model, and before this only the first was stated: whether the
  // system's AI provider is CONFIGURED, and whether the selected model BELONGS to that provider.
  // See modelProviderAlignment -- the run path takes the provider from the deployment and only the
  // model name from the employee row, so a cross-vendor pair fails at the first provider call.
  const modelAlignment = modelProviderAlignment(model, readiness);
  const modelMisaligned = modelAlignment === "mismatched";
  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: the recognition below was a DEAD-END
  // classification. Two sections of this one screen run free text against the same §28 vocabulary
  // under the same word "מפוקח": the skills section states a publication consequence per domain
  // (since 141 — a requested governed domain "נרשם כרשומת מועמדת ברמה 0"), and the specialist
  // section stated none at all. A declared specialty is a tag on the Agent row (models/agent.py:
  // "short tags, used by manager routing"; conversation_service weights it highest when matching a
  // ticket) and it is NOT a Skill — skill_assignment_state's own contract says "declared
  // specialties are NOT skills: this projection never reads Agent.specialties", the publish body
  // carries requested_skill_domains only, and the server's `recognized_governed_domains` is
  // produced in one place and consumed by no product code. So "תחומים מפוקחים שזוהו בהצהרה:
  // FortiGate" named a governed domain that publishes nothing, gets no row in the outcome panel,
  // and — with no other intent requested — is never mentioned again, because publishIntentAfterCreate
  // does not call the endpoint at all. The section's only qualifier scoped its negative to
  // "לפני יצירה", which since 141 is exactly the window in which the skills section's domains DO
  // get applied, so it read as a promise about creation rather than a statement of the contract.
  //
  // State the consequence, for the confusable case only: a governed specialty that is NOT also a
  // requested domain. Compared on the SERVER's identity (canonicalSkillDomainLabel), never the
  // exact string, because "FortiGate" declared beside "fortigate" requested is ONE governed domain
  // to `_canonical_domain_label` — warning there would be this defect sign-flipped, telling the
  // operator that a domain the publish applies will not be published.
  const recognizedSpecialties = specialties.filter((s) => isGovernedDomain(s));
  const requestedCanonical = new Set(requestedDomains.map(canonicalSkillDomainLabel));
  const specialtiesNotRequested = recognizedSpecialties.filter(
    (s) => !requestedCanonical.has(canonicalSkillDomainLabel(s))
  );

  // Pass 50: the catalog's vocabulary, not the governed one. These are the domains for which the
  // employee page will show an Expert Pack by itself, derived from the declaration alone.
  const packDomains = declaredExpertPackDomains(specialties);

  // ── Pre-save readiness: only what is knowable before the employee exists ──
  const nameTrim = form.name.trim();
  const nameTaken = agents ? agents.some((a) => (a.name || "").trim() === nameTrim) : null;
  // Exact and case-sensitive, because `_assert_system_name_unused` tests `payload.name in
  // _SYSTEM_AGENT_NAMES` on the value this wizard sends (form.name.trim()). A looser fold here
  // would warn about a name the server treats as ordinary.
  const nameReservedSystem = isReservedSystemName(nameTrim);

  const readinessChecks = [
    {
      key: "name",
      label: "שם העובד",
      state: agentsFailedNow ? "unknown" : agentsLoading ? "info" : (nameTaken ? "warn" : "ok"),
      detail: [
        agentsFailedNow
          ? "לא ניתן לטעון את רשימת העובדים — לא ידוע אם השם בשימוש."
          : agentsLoading
          ? "טוען…"
          : nameTaken
          ? (nameReservedSystem
            ? "השם הזה שמור לסוכן מערכת של הפלטפורמה, וסוכן כזה כבר קיים — השרת ידחה את היצירה (409) והעובד לא ייווצר כלל. בחר שם אחר."
            : "קיים כבר עובד בשם זהה — שמות אינם ייחודיים, אך שם מזהה מומלץ.")
          : "לא נמצא עובד בשם זהה.",
        // The adoption half: disclosed, never degraded, and never stated as a refusal - with the
        // name free the create succeeds, and what follows is a consequence of the name itself.
        nameReservedSystem && nameTaken === false
          ? "השם הזה שמור לסוכן מערכת של הפלטפורמה: העובד שייווצר בשם הזה הוא זה שהפלטפורמה תאתר לפי השם ותפעיל בתפקיד המערכת הזה — עם הרמה, האוטונומיה, רשימת הכלים וסקופ הלקוחות שנבחרו כאן."
          : null,
        // Pass 45: and the adoption cannot be undone. Only on this arm - on the taken arm nothing
        // is created, so there is nothing to reverse, and on the did-not-load arm the row already
        // says the refusal is merely possible. The clause is interpolated from the same constant
        // the collecting step renders, so the two surfaces cannot drift (pass 25).
        nameReservedSystem && nameTaken === false ? RESERVED_NAME_IRREVERSIBLE_CLAUSE : null,
        nameReservedSystem && nameTaken === null
          ? "השם הזה שמור לסוכן מערכת של הפלטפורמה; אם סוכן כזה כבר קיים, השרת ידחה את היצירה והעובד לא ייווצר."
          : null,
      ].filter(Boolean).join(" "),
    },
    {
      key: "scope",
      label: "סקופ לקוחות",
      // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 20): the SHADOWING ARM. Four fetches
      // feed this surface, and every one of them except this row's routes a failed load to
      // `unknown` (name, tool_catalog/entitlement, provider/connections), which lands in the
      // verdict's EVIDENTIAL arm - whose sentence, "not all the data is available", is exactly
      // true of a failed fetch. This row sent `companiesFailed` to `warn`, the DETERMINATE
      // bucket, and tested it FIRST, so the wizard's own transport failure shadowed all three
      // arms that describe the draft.
      //
      // It shadowed arms that do not depend on it, because the customer list has no authority
      // over the scope: `create_agent` puts the submitted `client_id` straight onto the Agent
      // row and reads no company list, so form.client_id IS the scope and the list only NAMES
      // it. The server's own preview composes the same three arms and no fetch-failure arm at
      // all (services/employee_builder_v3.py `_persona` / `_readiness_checks`, both testing
      // `draft.client_id is None` first), and it reaches `all_customers` WITHOUT consulting the
      // customer evidence - a null client_id needs no lookup. Pinned across both languages in
      // backend/tests/test_employee_builder_v3.py.
      //
      // Measured consequences of testing the loader first: with every customer chosen - the
      // default, and the only value reachable without the list - the row refused to say "all
      // customers" and degraded the verdict to needs-attention over a determinate choice; and
      // with a customer chosen and the list still in hand (the reachable shape: query-core v5
      // keeps `data` on a background refetch error and only flags it invalidated, and
      // staleTime is 0 with refetchOnWindowFocus default, so one alt-tab is enough) the name
      // was in hand, discarded, and the row said a specific customer "cannot be selected"
      // while that exact id was about to be sent.
      //
      // Now the loader decides only the case that depends on it - a chosen customer the wizard
      // cannot find - which is missing evidence, so `unknown`, in the arm whose sentence is
      // true of it. The staleness is still disclosed, appended with .filter(Boolean).join(" ")
      // so all three sentences stay byte-identical when the list did load. The ladder is not
      // degraded for the two determinate cases: nothing is wrong with them and there is
      // nothing for the operator to do.
      state: form.client_id === null
        ? "info"
        : company
        ? "ok"
        : companiesFailed
        ? "unknown"
        : "warn",
      detail: [
        form.client_id === null
          ? "כל הלקוחות."
          : company
          ? `לקוח נבחר: ${company.name}.`
          : companiesFailed
          ? "לא ניתן לאמת את הלקוח שנבחר — אם אינו קיים עוד, היצירה תיכשל."
          : "הלקוח שנבחר אינו נמצא ברשימה — היצירה תיכשל עד לבחירת לקוח תקין.",
        companiesFailed
          ? "רשימת הלקוחות לא נטענה; הסקופ נקבע לפי הבחירה שבטופס ואינו תלוי בטעינתה."
          : null,
        form.client_id === null
          ? SCOPE_BOUNDARY_ROW_GLOBAL
          : SCOPE_BOUNDARY_ROW_SCOPED,
        SCOPE_IMMUTABLE_AFTER_CREATE,
      ].filter(Boolean).join(" "),
    },
    {
      key: "role",
      label: "תפקיד והנחיות",
      // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 28). This row read
      // `form.system_prompt` while the single creating action sends `composeSystemPrompt(form)`.
      // With nothing declared and any system selected the wizard authors the WHOLE operating
      // prompt - composeSystemPrompt's configured-tools line, not quoted here because pass 22's
      // one-composition pin counts that literal in the RAW source and a comment copy reds it.
      // handleNext sends that value, AgentCreate stores it on agents.system_prompt, and
      // manager_agent._build_agent_analyst passes it to
      // create_direct_llm_analyst, which PREPENDS it to the base safety contract on every
      // analysis run (DirectLLMAnalysisAdapter.__init__). So "runs will use the system default"
      // was false in exactly the state that produces it, and the only site in this file naming
      // the appended line was the >10k arm of draft_limits below - unreachable on any ordinary
      // draft. Pass 22 moved the composition out of handleNext for the BOUND and pass 21
      // measured the count understatement; neither asked whether this sentence was still true.
      // Disclosed, never degraded: the line exists because the operator selected the systems,
      // it is determinate, and there is nothing here to fix - the refusal to degrade is pinned
      // in backend/tests/test_employee_builder_v3.py. Both no-system arms stay byte-identical.
      state: promptText ? "ok" : "info",
      detail: [
        promptText
          ? `הוצהרו הוראות הפעלה (${promptText.length} תווים) — הן מנחות ריצות ואינן מעניקות הרשאה. ${PROMPT_RUNTIME_NOTE}`
          : promptLineAppended
          ? "לא הוצהרו הוראות הפעלה, אך האשף עצמו מרכיב אותן — ולכן הריצות לא ישתמשו בברירת המחדל של המערכת לבדה. טקסט התפקיד, כשיש, נשאר הנחיה בלבד."
          : "לא הוצהרו הוראות הפעלה — הריצות ישתמשו בברירת המחדל של המערכת. טקסט התפקיד, כשיש, נשאר הנחיה בלבד.",
        promptLineAppended
          ? `האשף מוסיף להוראות שורה אחת המונה את המערכות שנבחרו, כך שההוראות שיישלחו מונות ${bounds.sentPromptChars} תווים; היא נוספת לפני חוזה הבטיחות של המערכת בכל ריצת ניתוח, ואינה מעניקה הרשאה, כלי או אישור.`
          : null,
      ].filter(Boolean).join(" "),
    },
    {
      // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 22). Ten rows described the draft and
      // none of them described whether the server will ACCEPT it. Every bound AgentCreate enforces
      // on the persona/role text refuses the create outright, so each is a determinate fact this
      // surface holds in its hand one click before the irreversible action - and three of them were
      // named nowhere in this file (the per-specialty 40, the name 255, the composed prompt 10k),
      // while the fourth, the 20-item cap, was "mirrored" as a silent drop. `warn`, the determinate
      // bucket, for the same reason pass 19 moved the ungoverned skill domain there: this is not
      // missing evidence, it is a refusal already decided by a schema. The clean arm is the `ok`
      // sentence plus the secret-boundary statement, which is always present because it is a
      // standing property of these fields and not a finding about this draft.
      key: "draft_limits",
      label: "מגבלות שדות הטיוטה",
      state: boundsViolated ? "warn" : "ok",
      detail: [
        boundsViolated
          ? null
          : `כל שדות הזהות וההנחיות בתוך מגבלות השרת.`,
        bounds.nameTooLong
          ? `שם העובד חורג מ-${MAX_NAME_CHARS} תווים (${bounds.nameChars}) — השרת ידחה את הבקשה והעובד לא ייווצר.`
          : null,
        bounds.overlongSpecialties.length > 0
          ? `תחום התמחות מוגבל ל-${MAX_SPECIALTY_CHARS} תווים וחורגים ממנו: ${bounds.overlongSpecialties.join(", ")} — השרת ידחה את הבקשה והעובד לא ייווצר.`
          : null,
        bounds.promptTooLong
          ? `הוראות ההפעלה שיישלחו חורגות מ-${MAX_SYSTEM_PROMPT_CHARS} תווים (${bounds.sentPromptChars}, כולל שורת המערכות שהאשף מוסיף) — השרת ידחה את הבקשה והעובד לא ייווצר.`
          : null,
        bounds.droppedSpecialties.length > 0
          ? `הוצהרו יותר מ-${MAX_SPECIALTIES} תחומי התמחות, ואלה לא יישלחו ולא יישמרו על רשומת העובד: ${bounds.droppedSpecialties.join(", ")}.`
          : null,
        // "מזוהה" rather than a bare "סוד": the opening clause has to be true on its own, not
        // true only once the reach sentence after it narrows it. The reach sentence is what
        // defines which secrets are recognized.
        // Pass 47: the row is the last surface before the single creating action, so the one
        // consequence the unchecked-fields clause states (stored on the row) is finished here
        // with the one it stopped short of (sent to the model provider on every reply).
        `סוד מזוהה בשדות האישיות, הפתיח או הוראות ההפעלה נדחה בשרת והעובד לא נוצר — שדות אלה אינם מקום לסודות. ${SECRET_REFUSAL_REACH_NOTICE} ${DRAFT_UNCHECKED_SECRET_FIELDS_NOTICE} ${IDENTITY_MODEL_EGRESS_NOTICE} ${IDENTITY_MCP_BRIDGE_EGRESS_NOTICE}`,
      ].filter(Boolean).join(" "),
    },
    {
      key: "tool_catalog",
      label: "קטלוג הכלים",
      // Pass 24: `projection ? projection.registered : 0` rendered a settled, unqualified "0 tools
      // are registered in the catalog" for a catalog that was never read - entitlementProjection
      // returns null on a non-array body, which is neither loading nor an error, so both guards
      // above it are false. The file already answers this exact state correctly twelve lines down
      // in the preview body ("תחזית כלים: לא ידוע — קטלוג הכלים לא נטען." on `toolsFailedNow ||
      // !projection`); only this row turned it into a number, and a fabricated zero is what every
      // read contract in this repo refuses to emit for a slice it does not have.
      state: toolsFailedNow ? "unknown" : toolsLoading ? "info" : projection ? "info" : "unknown",
      detail: toolsFailedNow
        ? "לא נטען — מספר הכלים הרשומים אינו ידוע."
        : toolsLoading
        ? "טוען…"
        : projection
        ? `${projection.registered} כלים רשומים בקטלוג ברמת הפירוט הזו.`
        : "קטלוג הכלים הוחזר בצורה שאינה מוכרת — מספר הכלים הרשומים אינו ידוע.",
    },
    {
      key: "entitlement",
      label: "הרשאות כלים בפועל (ברמת הרשומה)",
      state: !projection ? (toolsFailedNow ? "unknown" : "info") : projection.reachable === 0 ? "warn" : "ok",
      detail: !projection
        ? toolsFailedNow
          ? "לא ידוע — הקטלוג לא נטען."
          : "טוען…"
        : projection.reachable === 0
        ? "0 כלים נגישים בטיוטה זו ברמה וברשימה שנבחרו — כדאי לחזור לשלב המגבלות."
        : [
            `${unrestricted ? "ללא הגבלת רשימה" : "עם רשימה מצומצמת"} — ${projection.entitled} כלים מורשים כעת מתוך ${projection.registered} רשומים (${projection.strongGated} נוספים נגישים רק באישור מוגבר).`,
            // The restriction the operator configured is partial, and this is the surface that has
            // to say so: the allowlist gate never withholds a read-only Registry tool.
            projection.listExempt > 0
              ? `הרשימה המצומצמת אינה מגבילה כלים לקריאה בלבד — ${projection.listExempt} מהם נגישים גם ללא סימון.`
              : null,
          ].filter(Boolean).join(" "),
    },
    {
      key: "model",
      label: "מדיניות מודל",
      // Being in the wizard's catalog is not being runnable: the model only reaches a run through
      // the deployment's provider, so a cross-vendor selection is needs-attention here and not ok.
      state: !model ? "unknown" : modelMisaligned ? "warn" : "ok",
      detail: !model
        ? "המודל שנבחר אינו מהקטלוג המוכר של האשף — התנהגותו בזמן ריצה אינה ידועה."
        : [
            `המודל נבחר מהקטלוג המוכר של האשף (${model.label}, ספק ${model.provider}).`,
            modelMisaligned
              ? `ספק ה-AI של המערכת הוא ${readiness.provider} — מודל של ספק אחר אינו נגיש בהרצה, והבחירה הזו תיכשל בקריאה הראשונה לספק.`
              : null,
          ].filter(Boolean).join(" "),
    },
    {
      key: "provider",
      label: "ספק AI במערכת",
      // pass 54: `unreadable` is missing evidence, not a determinate problem, so it belongs in
      // the EVIDENTIAL bucket -- the verdict arm whose own sentence ("not all the data is
      // available") is exactly true of it -- and not in `warn`, which asserts something the
      // operator could act on. Pass 20's rule, applied to the one state that still reached the
      // determinate bucket by falling through an else.
      state: healthFailedNow
        ? "unknown"
        : healthLoading
        ? "info"
        : readiness.state === "unreadable"
        ? "unknown"
        : readiness.state === "configured"
        ? (modelMisaligned ? "warn" : "ok")
        : "warn",
      detail: healthFailedNow
        ? "לא ידוע — סטטוס החיבורים לא נטען."
        : healthLoading
        ? "טוען…"
        : readiness.state === "unreadable"
        ? PROVIDER_STATUS_UNREADABLE_NOTICE
        : readiness.state === "configured"
        ? [
            `ספק ה-AI מוכן (${readiness.provider || "לא צוין"}).`,
            modelMisaligned
              ? `המודל שנבחר שייך לספק ${model.provider} ולא לספק המערכת — הספק נלקח מהגדרת המערכת ולא מרשומת העובד, ולכן הרצות העובד ייכשלו ולא יעברו למודל חלופי.`
              : "הרצות העובד ישתמשו בשם המודל שנבחר מול ספק זה; כרטיס בעדיפות גבוהה עשוי להינתב למודל ההחלפה של המערכת.",
          ].filter(Boolean).join(" ")
        : "ספק ה-AI אינו מוכן (הגדרה או מפתח חסרים) — הרצות העובד לא ישתמשו במודל שנבחר עד להשלמת ההגדרה.",
    },
    {
      key: "connections",
      label: "מערכות מבוקשות",
      state: connectionsState === "none_requested"
        ? "info"
        : connectionsState === "unknown"
        ? "unknown"
        : connectionsState === "not_configured"
        ? "warn"
        : "ok",
      // Two independent truths, and before 141 only the first was stated: whether each requested
      // system is CONFIGURED in this deployment (the ladder below), and whether publishing it
      // CREATES a per-employee connection record (SYSTEM_PUBLISH_SCOPE). A configured
      // platform-managed system is `ok` on the first and produces nothing on the second. The
      // scope clause is appended rather than substituted, and is empty when every requested
      // system is `supported`, so the configuration sentences stay byte-identical in that case.
      // The state ladder is deliberately NOT degraded by scope: platform_managed is a correct,
      // determinate, by-design outcome reported truthfully after the publish, so flagging it as
      // needs-attention would be the over-degrading direction pass 13 warned about.
      detail: [
        connectionsState === "none_requested"
          ? "לא בוקשו מערכות סביבה."
          : connectionsState === "unknown"
          ? "לא ידוע — סטטוס המערכות לא נטען או שאינו מזוהה."
          : connectionsState === "not_configured"
          ? `${requestedTools.filter((t) => t.status.state === "not_configured").length} מתוך ${requestedTools.length} מערכות מבוקשות אינן מוגדרות במערכת.`
          : "כל המערכות המבוקשות מוגדרות במערכת.",
        noRecordTools.length > 0
          ? `${noRecordTools.length} מתוך ${requestedTools.length} המערכות המבוקשות לא תיווצר עבורן רשומת חיבור פר-עובד (מנוהלות פלטפורמית או אינן מוכרות); עבור ${connectionCreatingTools.length} תיווצר רשומת חיבור ללא פרטי גישה.`
          : null,
      ]
        .filter(Boolean)
        .join(" "),
    },
    {
      key: "skills",
      label: "מיומנויות מבוקשות",
      // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 18): the DURABILITY axis. The promise
      // "נרשם כרשומת מועמדת ברמה 0" was rendered at THREE pre-create sites (the skills step's
      // notice, this detail and the preview-skills footer) and once post-create, and only the
      // post-create one — PublishOutcomeSection's publish-skill-durability notice — carried the
      // store it lands in. Pre-create arm count on that axis was 0 of 3. Measured in
      // backend/tests/test_employee_builder_v3.py
      // (test_the_two_halves_of_one_publish_land_in_stores_of_different_durability): the skill
      // half goes through skill_assignment_state's process-local module global into
      // skill_registry.SkillRegistry ("In-memory contract registry. Not a singleton, not
      // persisted"), no mapped table holds a Skill at all, and a fresh process has lost it —
      // while the system half of the SAME publish is a committed AgentConnection row that
      // survives. So the operator decided to request a governed domain on a screen describing a
      // durable registration and read the truth only after the single irreversible action. Same
      // shape as the publish-scope axis this task closed earlier: a literally-true sentence
      // answering a question the operator was not about to bet on.
      //
      // Labelling only, and the ladder is deliberately NOT degraded: in-memory registration is
      // what the §28 contract does today, it is determinate, and it is not a failure the operator
      // can fix from here (persistence is a separate future task). A probe pins that refusal so a
      // later pass cannot quietly turn this into "warn" without reddening a test. Unreachable
      // half, stated so it is not re-chased: docker-compose runs ONE uvicorn worker with no
      // --workers, so the cross-process-invisibility half is not reachable in this deployment;
      // the restart/redeploy half is, on every prodigy-deploy.
      //
      // An unadded draft is needs-attention, not information: it is the one input the single
      // creating action discards, and the operator can still fix it from here.
      //
      // Pass 19, the AGGREGATION. A requested domain outside the governed vocabulary used to
      // be `unknown`, so `unknownCount` carried it into the verdict's EVIDENTIAL bucket and
      // the badge the operator acts on read "partial information - not all the data is
      // available" with every fetch resolved. It is not unknown: publish_builder_skill_intent
      // answers `unsupported` on `domain not in SKILL_DOMAINS` BEFORE anything is constructed
      // or registered (pinned in backend/tests/test_employee_builder_v3.py), and
      // GOVERNED_SKILL_DOMAINS == SKILL_DOMAINS as sets is pinned from this file off disk. The
      // refusal is therefore DETERMINATE, and this very row already grades the weaker case -
      // a draft typed and never added, same consequence, less certain - as `warn`. So the
      // certain one gets the same severity, and the detail names the outcome panel's own
      // `unsupported` label instead of saying the mapping is not known.
      state: pendingSkillDraft
        ? "warn"
        : requestedDomains.length === 0
        ? "info"
        : requestedDomains.some((d) => !isGovernedDomain(d))
        ? "warn"
        : "info",
      detail: [
        requestedDomains.length === 0
          ? "לא בוקשו תחומי מיומנות; גם ללא בקשה, היצירה אינה משייכת רשומת מיומנות."
          : requestedDomains.some((d) => !isGovernedDomain(d))
          ? `${requestedDomains.filter((d) => !isGovernedDomain(d)).length} מתוך ${requestedDomains.length} תחומים מבוקשים מחוץ למילון המפוקח — הפרסום דוחה אותם לפני כל ניסיון ומדווח עליהם „${PUBLISH_SKILL_STATE_META.unsupported.label}”, כלומר לא תיווצר עבורם רשומת מועמדת. הבקשה עצמה אינה נעלמת ומדווחת פר-תחום.`
          : `${requestedDomains.length} תחומים מפוקחים נרשמו ככוונה — עם סיום האשף הם מפורסמים כרשומות מועמדות ברמה 0 (ללא הרשאה); בקשות שאינן נתמכות ידווחו במפורש.`,
        // The sentence above states WHAT gets registered; this one states WHERE, because the two
        // halves of the same publish land in stores of different durability and only the
        // post-creation panel used to say so. Keyed on "at least one governed domain" so it also
        // reaches the partly-ungoverned arm above (which mentions only the unrecognized ones),
        // and so the detail stays byte-identical when nothing governed was requested.
        //
        // Pass 27: the second half of that sentence had NO arm on whether a connection record is
        // created at all. `connectionCreatingTools` (above, from SYSTEM_PUBLISH_SCOPE) is the
        // authority this row needed: two of the three offered systems are platform-managed and
        // create none, and the default draft requests no system at all — so on the common shape
        // this row named a durable store for a record the publish will not create, which is the
        // one sentence telling the operator that anything from the publish survives a restart.
        // The connection-created arm is byte-identical; the other arm states the determinate
        // negative instead.
        requestedDomains.some((d) => isGovernedDomain(d))
          ? [
              "הרשומה נרשמת במרשם ההקצאות של השירות ולא בבסיס הנתונים: היא מוצגת בעמוד המיומנויות של העובד, ושורדת הפעלה מחדש של השירות רק כאשר מאגר המצב של השירות זמין בשרת",
              connectionCreatingTools.length > 0
                ? ", בעוד רשומת החיבור של מערכת מבוקשת נשמרת בבסיס הנתונים."
                : ". לא נבחרה מערכת שנוצרת עבורה רשומת חיבור, כך שהפרסום לא ישמור דבר בבסיס הנתונים.",
            ].join("")
          : null,
        requestedDomains.some((d) => isGovernedDomain(d)) ? SKILL_RECORD_NO_REMOVAL_PATH : null,
        pendingSkillDraft
          ? `הוקלד תחום שלא נוסף לבקשה — הוא לא יפורסם ולא ידווח. חזור לשלב המיומנויות והוסף אותו או נקה את השדה.`
          : null,
      ].filter(Boolean).join(" "),
    },
    {
      // Pass 25: the row for the one column the creating payload cannot set. Every other row
      // describes a field the operator chose; this one describes what POST /api/agents/ does
      // with a field it does not accept -- Agent.is_enabled defaults to False and AgentCreate has
      // no such field, so the wizard cannot create an activated employee and no draft input can
      // change that. Determinate and by design (fail-safe), so it is `info` and the verdict is
      // untouched -- the same treatment pass 24 gave the reserved-name disclosure. The three
      // consequences named are the measured gates: tickets.py (assignment), agent_runs.py (run
      // start / retry / system check), conversation_service.py (joining a conversation), and
      // employee_workspace.effective_status for the team-visible badge; 32 is_enabled gate sites
      // in the backend. The last sentence is the applied-but-inert distinction the publish panel
      // below cannot make: the records ARE created, and the employee still will not act on them.
      key: "activation",
      label: "הפעלת העובד לאחר היצירה",
      state: "info",
      detail: `העובד נוצר במצב מושבת ואין באשף אפשרות ליצור אותו מופעל: עד שיופעל מדף העובד הוא לא ישובץ לפנייה, לא יריץ ניתוח, לא יצטרף לשיחה, וכל מסך צוות יציג אותו כמנותק. הרשומות שהאשף מחיל מיד לאחר היצירה נוצרות גם ללא הפעלה, אך העובד לא יפעל לפיהן עד ההפעלה. ${EMPLOYEE_ROW_NO_REMOVAL_PATH}, ולכן עובד שלא יופעל נשאר קיים במצב מושבת.`,
    },
    {
      key: "employee",
      label: "מיומנות והרשאות של העובד עצמו",
      state: "info",
      // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 28): one predicate over a CONJOINED
      // SUBJECT, true of the first subject and false of the second. "Measured skill and actual
      // permissions ... derive from run data after creation" is right about proficiency -
      // growth_profile reads AgentRun history through skills_evaluator's deterministic counters,
      // and employee_builder_v3._SKILLS_NOTE says a governed skill starts at level 0 and moves
      // only through human-reviewed evaluations of run evidence - and wrong about permissions:
      // autonomy_policy.check_entitlement takes (action_type, permission_tier, crisis_mode,
      // allowed_tools) and employee_workspace.entitled_tools_for takes (tier, allowed_tools),
      // with no run input at either. The employee's actual permissions are therefore decided by
      // the tier and tool list chosen in this wizard's own limits step and apply from creation;
      // growth_profile's header calls that section "a description of the current policy, not a
      // second policy", and skill_lens states outright that a Skill grants nothing. So the one
      // sentence telling the operator where their employee's permissions come from pointed at
      // run evidence instead of at the choice they are one click from committing - the
      // under-reporting direction of pass 14 - and it contradicted the `entitlement` row above,
      // which renders how many tools are permitted NOW. Carry-forward, outside FILES_OWNED:
      // services/employee_builder_v3.py::_readiness_checks ships the same sentence in English,
      // so this is pass 24's faithful mirror and the blind spot was shared.
      detail: `מיומנות נמדדת טרם קיימת — היא נגזרת מנתוני ריצות לאחר היצירה כציון מחושב, ועולה בלי הערכה אנושית. רמת רשומת מיומנות היא מדד נפרד. ${SKILL_LEVEL_GATE_SHORT} הרשאות בפועל אינן נגזרות מנתוני ריצות: הן נקבעות בשער המדיניות מהרמה ומרשימת הכלים שנבחרו כאן, חלות מרגע היצירה, ובמצב חירום מצטמצמות עוד. מיומנות או רשומת מועמדת אינן מעניקות כלי, הרשאה או אישור.`,
    },
  ];

  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 19): the verdict FOLD. The thirty-four
  // state arms of the ten rows above collapse into one of three sentences, and the fold's only
  // inputs are these two counts plus `evidenceLoading` - so the middle arm asserts a CAUSE
  // ("not all the data is available") about every `unknown` it is handed. Measured arm by arm,
  // that cause is true for the five rows whose `unknown` really is a failed fetch (name,
  // tool_catalog, entitlement, provider, connections-not-loaded) - six since pass 20, which
  // moved `scope`'s failed-list case out of `warn` and into this bucket, where its cause is
  // the sentence this arm already states - and false for three:
  // `skills` with an ungoverned requested domain - reachable in two clicks, now graded `warn`
  // above because it is a determinate refusal and not missing evidence - plus `model`'s
  // `!model` arm (unreachable: both writers of form.model stay inside MODEL_OPTIONS) and
  // `connections`' unrecognized arm (unreachable: envToolStates maps over ENV_TOOLS itself).
  // The two unreachable ones are recorded so the next pass does not re-chase them; if either
  // ever becomes reachable it needs its own arm here rather than this bucket. The precedence is
  // deliberate: a certain problem outranks an uncertain one, so `warn` wins over unknown and
  // over still-loading.
  const warnCount = readinessChecks.filter((c) => c.state === "warn").length;
  const unknownCount = readinessChecks.filter((c) => c.state === "unknown").length;
  const evidenceLoading = agentsLoading || healthLoading || toolsLoading || (!connectorTypes && !connectorsFailedNow);
  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 21): the STALE VERDICT. The three
  // sentences this fold produces are the words the operator acts on, and all three speak one
  // axis — the quality of the DRAFT. The axis none of them spoke is the one only this surface
  // can know: what happened to the single creating action. Measured over the four states of
  // that axis: NOT ATTEMPTED → the three sentences, correct; IN FLIGHT → "מוכן ליצירה" while
  // the button itself already reads "יוצר...", which claims nothing about a decision because
  // none has been made; SUCCEEDED → an explained zero, but NOT for the reason published here for
  // twenty-one passes: measured, the overlay is `fixed inset-0 z-50 bg-black/40` with no
  // `pointer-events-none`, so what it buys is that nothing behind it is CLICKABLE -- a 40% scrim
  // dims the badge, it does not hide it, and "is not readable at all" was an overstatement. The
  // arm is still not warranted, for a reason that is measurable: the dialog's own heading states
  // the succeeded fact ("העובד נוצר בהצלחה!", 1 rendered site) directly in front of the stale badge, so
  // screen does not leave the question unanswered; and REFUSED → the badge read
  // "מוכן ליצירה", in green, on the same screen as the red sentence stating that the creation
  // failed. Arm count on the attempt axis: 0 of 3 pre-existing arms. Same shape as pass 18's
  // durability disclosure with the window reversed — there a true sentence was missing before
  // the irreversible action, here a verdict computed before it kept asserting readiness after
  // the action had been decided the other way.
  //
  // The server cannot own this arm, which is why its absence survived twenty passes:
  // compose_builder_preview_v3 takes (draft, evidence) only, is preview_only and grants
  // nothing, and _verdict folds the ten checks with no attempt input at all — so `ready` there
  // is a statement about the draft and never a promise that POST /api/agents/ will accept it.
  // Pinned across both languages in backend/tests/test_employee_builder_v3.py, which also
  // reads this file off disk so the two folds are held to agreeing on the three draft states
  // and differing by exactly this arm. The inverse of pass 20: there the mirror ADDED an arm
  // its source cannot have and put it in the determinate bucket; here the one arm only the
  // mirror can know was absent.
  //
  // Keyed on the LAST attempt rather than on "creation failed", because createMutation.isError
  // survives an edit — measured: going back, changing the draft and returning leaves both the
  // error paragraph and this badge rendered — and clears on the next mutate, so the sentence is
  // true in both states. Highest precedence, by the rule the rest of the ladder already
  // follows (a certain problem outranks an uncertain one): a refusal that has already happened
  // is the most certain fact on the screen, and unlike the other three it is not about the
  // draft the rows describe. The prop defaults to false, so every render that does not pass it
  // is byte-identical.
  // Pass 34: pass 21 enumerated this axis as FOUR states (not attempted / in flight /
  // succeeded / refused) and gave the fourth an arm. "Refused" is two states, not one: the
  // application answered and refused, or nothing answered at all -- and only the second can
  // sit over a created employee, because create_agent flushes inside the request and get_db
  // commits after the route returns. Calling that one "failed" is pass 25's conflated cause
  // on the one word the operator acts on, so it gets its own arm, above the refusal, and the
  // notice under the navigation says what to do about it. The prop defaults to false, so
  // every render that does not pass it is byte-identical.
  const verdict = createUnconfirmed
    ? { label: "לא התקבלה תשובה על ניסיון היצירה — לא ניתן לדעת אם העובד נוצר", cls: "bg-red-100 text-red-700 border-red-200" }
    : createFailed
    ? { label: "ניסיון היצירה האחרון נכשל — הסיכום מתאר את הטיוטה, לא את תוצאת הבקשה", cls: "bg-red-100 text-red-700 border-red-200" }
    : warnCount > 0
    ? { label: "דורש תשומת לב לפני יצירה", cls: "bg-amber-100 text-amber-700 border-amber-200" }
    : unknownCount > 0 || evidenceLoading
    ? { label: "מידע חלקי — לא כל הנתונים זמינים", cls: "bg-gray-100 text-gray-600 border-gray-200" }
    : { label: "מוכן ליצירה", cls: "bg-green-100 text-green-700 border-green-200" };

  return (
    <div data-testid="preview-step" className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">תצוגה מקדימה וסיכום מוכנות — לפני יצירה</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          שמונת התחומים המרכיבים את העובד, לקריאה בלבד, לפני השמירה היחידה באשף.
        </p>
      </div>

      <div data-testid="preview-readonly-banner" role="note" className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700">
        תצוגה לקריאה בלבד — אינה יוצרת רשומות, הרשאות, חיבורים או שיוכי מיומנויות, ואינה מאשרת דבר.
        העובד נוצר רק בלחיצה על „צור עובד ✓”, במצב מושבת, ובדיוק לפי מה שמוצג כאן.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PreviewSection tone="indigo" testId="preview-persona" title="פרסונה" state="מוצהר בטופס" editStep={2} onEditStep={onEditStep}>
          <div className="flex items-center gap-3">
            <EmployeeAvatar
              name={form.name}
              avatarKey={form.avatar_key}
              avatarColor={form.avatar_color}
              status={form.availability_status}
              size="md"
              showStatus
            />
            <dl className="text-sm space-y-1 min-w-0">
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">שם</dt>
                <dd className="font-medium text-gray-800 break-words">{form.name || "—"}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">תפקיד</dt>
                <dd className="text-gray-700">{declaredRoleTitle || "לא הוגדר תפקיד"}</dd>
              </div>
              {preset && (
                <div className="flex gap-2">
                  <dt className="text-gray-500 w-20 shrink-0">תבנית</dt>
                  <dd className="text-gray-700">{preset.icon} {preset.label}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="text-gray-500 w-20 shrink-0">היקף</dt>
                <dd className="text-gray-700">
                  {/* DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: "no company matched" is not
                      "all customers". `company` is undefined both when the operator chose every
                      customer (client_id === null) and when a chosen customer is absent from the
                      loaded list, and collapsing the two reported a customer-SCOPED draft as
                      global — the widening direction — on the confirmation surface, while the
                      readiness row below said the opposite about the same state ("the chosen
                      customer is not in the list — creation will fail"). Two sections of one
                      screen cannot both be right. */}
                  {/* Pass 20: the same four arms in the same order as the readiness row above,
                      so the two sections cannot disagree. `companiesFailed` is consulted last,
                      for the one case that depends on it: before, it shadowed even the
                      all-customers arm, which the server composes with no customer evidence at
                      all, and it reported a customer-SCOPED draft as "unknown". */}
                  {form.client_id === null
                    ? "כל הלקוחות"
                    : company
                    ? `לקוח: ${company.name}`
                    : companiesFailed
                    ? "לקוח ספציפי — לא אומת מול רשימת הלקוחות"
                    : "לקוח ספציפי שאינו ברשימה שנטענה"}
                </dd>
              </div>
            </dl>
          </div>
          {/* Pass 53: on the one arm where the row above now says nothing was declared. Keyed on
              the SENT value, so it and the `לא הוגדר תפקיד` arm are the same state by
              construction and cannot drift apart; the clause never renders beside a declared role
              (the false-positive direction is probe N3). */}
          {!declaredRoleTitle && (
            <p className="text-[11px] text-gray-500" data-testid="preview-persona-role-unset">
              {[
                ROLE_TITLE_UNSET_LEAD,
                preset ? ROLE_TITLE_UNSET_TEMPLATE_CLAUSE : null,
                ROLE_TITLE_UNSET_RANKING_CLAUSE,
              ].filter(Boolean).join(" ")}
            </p>
          )}
          {form.description && (
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 w-20 shrink-0">תיאור</span>
              <span className="text-gray-700">{form.description}</span>
            </div>
          )}
          {form.personality && (
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 w-20 shrink-0">אישיות</span>
              <span className="text-gray-700">{form.personality}</span>
            </div>
          )}
          {form.intro_text && (
            <div className="flex gap-2 text-sm">
              <span className="text-gray-500 w-20 shrink-0">פתיח</span>
              <span className="text-gray-600 italic">„{form.intro_text}”</span>
            </div>
          )}
        </PreviewSection>

        <PreviewSection tone="amber" testId="preview-role" title="תפקיד והנחיות הפעלה" state="מוצהר בטופס" editStep={1} onEditStep={onEditStep}>
          {promptText ? (
            <>
              <p className="text-[11px] text-gray-400">הוראות הפעלה — {promptText.length} תווים</p>
              <p dir="ltr" className="text-xs text-gray-600 bg-white/70 border border-amber-100 rounded p-2 whitespace-pre-wrap break-words text-left">
                {promptText.slice(0, PROMPT_EXCERPT_LENGTH)}{promptText.length > PROMPT_EXCERPT_LENGTH ? "…" : ""}
              </p>
            </>
          ) : promptLineAppended ? (
            <p className="text-xs text-gray-400 italic">לא הוצהרו הוראות הפעלה, אך האשף מרכיב אותן בעצמו — ולכן הריצות לא ישתמשו בברירת המחדל של המערכת לבדה.</p>
          ) : (
            // pass 28: the same false promise as the readiness row, on the same state and the
            // same screen -- and this section renders only the DECLARED excerpt, so the line
            // the wizard adds is unrepresented in both arms. Its own arm, byte-identical when
            // no system is selected.
            <p className="text-xs text-gray-400 italic">לא הוצהרו הוראות הפעלה — הריצות ישתמשו בברירת המחדל של המערכת.</p>
          )}
          {promptLineAppended && (
            <p className="text-[11px] text-gray-500">להוראות שיישלחו מתווספת שורה אחת המונה את המערכות שנבחרו, שאינה חלק מהטקסט המוצהר; היא נוספת לפני חוזה הבטיחות של המערכת בכל ריצת ניתוח.</p>
          )}
          <p className="text-[11px] text-gray-500">
            הוראות ותפקיד הם הנחיה בלבד — אינם מעניקים כלי, רמה, חיבור או אישור.
          </p>
          {/* pass 33: the two sentences above are about GRANTING and are both true. This one is
              the axis neither speaks - whether the instructions are CARRIED OUT at all. Keyed on
              promptText exactly like the readiness row's clause: with nothing declared there is no
              instruction to report as unperformed (and the line the wizard appends for the
              selected systems instructs nothing), so both no-prompt arms stay byte-identical.
              An unconditional render here asserted the axis over a draft that declared none --
              the sign-flipped twin, caught by this change's own test before it shipped. */}
          {promptText && (
            <p className="text-[11px] text-gray-500" data-testid="preview-role-prompt-runtime">
              {PROMPT_RUNTIME_NOTE}
            </p>
          )}
        </PreviewSection>

        <PreviewSection
          tone="violet"
          testId="preview-specialist"
          title="חבילת מומחה"
          state={specialties.length ? "הוצהרה" : "לא הוצהרה"}
          editStep={2}
          onEditStep={onEditStep}
        >
          {specialties.length ? (
            <ul className="flex flex-wrap gap-1">
              {specialties.map((s) => (
                <li key={s} className="text-[11px] rounded-full bg-white border border-violet-200 px-2 py-0.5 text-violet-700">{s}</li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 italic">לא הוצהרו תחומי התמחות.</p>
          )}
          <p className="text-[11px] text-gray-500">
            תחומים מפוקחים שזוהו בהצהרה: {recognizedSpecialties.length > 0 ? recognizedSpecialties.join(", ") : "לא זוהו"}.
            {" "}הזיהוי תיאורי בלבד ואינו הופך הצהרה לבקשת מיומנות — בקשות מיומנות נרשמות בשלב המיומנויות בלבד.
          </p>
          {specialtiesNotRequested.length > 0 && (
            <p role="note" data-testid="preview-specialty-not-a-skill-request" className="text-[11px] text-amber-700">
              תחומים מפוקחים שהוצהרו כהתמחות בלבד ואינם בין המיומנויות המבוקשות: <span dir="ltr">{specialtiesNotRequested.join(", ")}</span>. הצהרה נשמרת כתגית על רשומת העובד — לשיוך פניות ולתצוגה — ואינה מפורסמת כרשומת מיומנות, לא תקבל שורת מצב בדוח הפרסום ולא תהפוך למיומנות מאושרת. אם הכוונה היא בקשת מיומנות מפוקחת, חזור לשלב המיומנויות ובקש אותם שם.
            </p>
          )}
          {packDomains.length > 0 && (
            <p role="note" data-testid="preview-specialist-pack-derived" className="text-[11px] text-amber-700">
              קטלוג החבילות מזהה בהצהרה את התחומים: <span dir="ltr">{packDomains.map((d) => EXPERT_PACK_DOMAIN_LABELS[d]).join(", ")}</span> — ולכן עמוד העובד יציג עבורם חבילת מומחה מעצמו, מיד לאחר היצירה. זיהוי זה נפרד מזיהוי התחומים המפוקחים שלמעלה: אף אחד מארבעת התחומים המפוקחים אינו יוצר חבילה, ואף אחד מהתחומים שקטלוג החבילות מזהה אינו תחום מיומנות מפוקח.
            </p>
          )}
          <p className="text-[11px] text-gray-400" data-testid="preview-specialist-pack-mechanism">
            הצהרת התמחות היא כוונת פריסה, לא הוכחת מוכנות — מיומנות או הרשאה אינן משויכות לפני יצירה, וגם היצירה והפרסום שאחריה אינם משייכים אותן. {EXPERT_PACK_DERIVATION_NOTICE}
          </p>
        </PreviewSection>

        <PreviewSection
          tone="blue"
          testId="preview-skills"
          title="מיומנויות מבוקשות"
          state={requestedDomains.length ? "בקשות נרשמו" : "לא בוקשו"}
          editStep={4}
          onEditStep={onEditStep}
        >
          {requestedDomains.length ? (
            <ul className="space-y-1">
              {requestedDomains.map((domain, index) => {
                // Joined by the domain itself, never by position: the preview list and the
                // forecast must not be able to describe two different requests.
                const forecast = publishForecast.skills.find((item) => item.key === domain)
                  || builderSkillForecast(domain);
                const dispositionMeta = PUBLISH_FORECAST_META[forecast.disposition];
                const approvalMeta = forecast.approval
                  ? PUBLISH_FORECAST_META[forecast.approval]
                  : null;
                return (
                  <li key={domain} className="flex items-center gap-2 text-xs flex-wrap">
                    <span dir="ltr" className="text-gray-700">{domain}</span>
                    {/* The controlled-vocabulary classification is kept beside the forecast:
                        it answers "is this word in the governed dictionary", which is a
                        different question from "what will the publish do with it". */}
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${
                      isGovernedDomain(domain) ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {isGovernedDomain(domain) ? "במילון המפוקח" : "מחוץ למילון — לא ייתמך, לא ייווצר דבר"}
                    </span>
                    <span data-testid={`preview-forecast-skill-${index}`} className="flex items-center gap-1">
                      <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${dispositionMeta.cls}`}>
                        {dispositionMeta.label}
                      </span>
                      {approvalMeta && (
                        <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${approvalMeta.cls}`}>
                          {approvalMeta.label}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-gray-500 basis-full">{forecast.detail}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 italic">לא בוקשו תחומי מיומנות.</p>
          )}
          {pendingSkillDraft && (
            <p role="alert" data-testid="preview-skill-draft-dropped" className="text-[11px] text-amber-700">
              הוקלד תחום שלא נוסף לבקשה: <span dir="ltr">{pendingSkillDraft}</span> — הוא אינו חלק מהבקשה, לא יפורסם ולא ידווח.
            </p>
          )}
          <p className="text-[11px] text-gray-500">
            רשומות מיומנות מדודות: טרם קיימות (אין).
            עם סיום האשף, תחום מפוקח מבוקש נרשם כרשומת מועמדת ברמה 0, ותחום מחוץ למילון מדווח כלא נתמך.
            {" "}{SKILL_LEVEL_GATE_NOTICE}
          </p>
          {/* Pass 27: "שני חצאי אותו פרסום" is only true when the draft actually requests a
              system the publish materializes. With none selected — the default, and the shape
              every fixture here sends — this footer asserted a second, durable half that does
              not exist. Same arm as the readiness row above, from the same authority. */}
          {requestedDomains.some((d) => isGovernedDomain(d)) && (
            <p data-testid="preview-skill-durability" className="text-[11px] text-amber-700">
              רשומת מועמדת נרשמת במרשם ההקצאות של השירות ואינה נשמרת בבסיס הנתונים — היא מוצגת
              בעמוד המיומנויות של העובד, ושורדת הפעלה מחדש של השירות רק כאשר מאגר המצב
              של השירות זמין בשרת.{" "}
              {connectionCreatingTools.length > 0
                ? "רשומת החיבור של מערכת מבוקשת, לעומת זאת, נשמרת בבסיס הנתונים — שני חצאי אותו פרסום נשמרים במקומות שונים."
                : "לא נבחרה מערכת שנוצרת עבורה רשומת חיבור, כך שהפרסום לא ישמור דבר בבסיס הנתונים."}
              {" "}<span data-testid="preview-skill-removal-path">{SKILL_RECORD_NO_REMOVAL_PATH}</span>
            </p>
          )}
        </PreviewSection>

        <PreviewSection tone="cyan" testId="preview-model" title="מדיניות מודל" state="מוצהר בטופס" editStep={5} onEditStep={onEditStep}>
          <dl className="text-sm space-y-1">
            <div className="flex gap-2">
              <dt className="text-gray-500 w-20 shrink-0">מודל</dt>
              <dd className="text-gray-700 font-mono text-xs" dir="ltr">{form.model}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500 w-20 shrink-0">ספק</dt>
              <dd className="text-gray-700">{model?.provider || "לא מהקטלוג המוכר של האשף"}</dd>
            </div>
          </dl>
          <p className="text-[11px] text-gray-500">
            {healthFailedNow
              ? "מוכנות ספק ה-AI: לא ידוע — הסטטוס לא נטען."
              : healthLoading
              ? "בודק את מוכנות ספק ה-AI…"
              : readiness.state === "unreadable"
              ? PROVIDER_STATUS_UNREADABLE_NOTICE
              : readiness.state === "configured"
              ? `מוכנות ספק ה-AI: מוכן (${readiness.provider || "לא צוין"}).`
              : "מוכנות ספק ה-AI: לא מוכן — הרצות לא ישתמשו במודל שנבחר עד להשלמת ההגדרה."}
          </p>
          {/* Both providers were already printed on this card -- the model's, two lines above, and
              the deployment's, one line above -- and nothing compared them. */}
          {modelMisaligned && (
            <p role="alert" data-testid="preview-model-provider-mismatch" className="text-[11px] text-amber-700">
              אי-התאמת ספק: המודל שנבחר הוא של {model.provider} וספק המערכת הוא {readiness.provider} — ההרצה לוקחת את הספק מהגדרת המערכת ואת שם המודל מרשומת העובד, ולכן הצמד הזה ייכשל בקריאה הראשונה לספק ולא יעבור למודל חלופי.
            </p>
          )}
          <p className="text-[11px] text-gray-400">
            אימות המודל מול מרשם המודלים בצד השרת אינו זמין בגרסה זו — ראו „לא נבדק לפני יצירה” בסיכום.
          </p>
        </PreviewSection>

        <PreviewSection
          tone="teal"
          testId="preview-connections"
          title="מערכות וחיבורים"
          state={
            connectionsState === "none_requested" ? "לא בוקשו"
            : connectionsState === "all_configured" ? "מוגדרות"
            : connectionsState === "not_configured" ? "לא מוגדרות"
            : "לא ידוע"
          }
          editStep={6}
          onEditStep={onEditStep}
        >
          {requestedTools.length ? (
            <ul className="space-y-1">
              {requestedTools.map(({ tool, status, publishScope }) => {
                const meta = ENV_STATUS_META[status.state] || ENV_STATUS_META.unknown;
                const scopeMeta = SYSTEM_PUBLISH_SCOPE_META[publishScope];
                // `requestedTools` follows the pick-list order while the forecast follows the
                // selected keys' order; join by id so the two orders cannot mismatch.
                const forecast = publishForecast.systems.find((item) => item.key === tool.id)
                  || builderSystemForecast(
                    tool.id,
                    status.state === "configured" ? true : status.state === "not_configured" ? false : null
                  );
                const dispositionMeta = PUBLISH_FORECAST_META[forecast.disposition];
                const approvalMeta = forecast.approval
                  ? PUBLISH_FORECAST_META[forecast.approval]
                  : null;
                return (
                  <li key={tool.id} className="flex items-center gap-2 text-xs flex-wrap">
                    <span>{tool.icon} {tool.label}</span>
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
                    {status.detail && <span className="text-[10px] text-gray-400">({status.detail})</span>}
                    <span
                      data-testid={`preview-publish-scope-${tool.id}`}
                      className={`text-[10px] rounded-full px-1.5 py-0.5 ${scopeMeta.cls}`}
                    >
                      {scopeMeta.label}
                    </span>
                    <span data-testid={`preview-forecast-system-${tool.id}`} className="flex items-center gap-1">
                      <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${dispositionMeta.cls}`}>
                        {dispositionMeta.label}
                      </span>
                      {approvalMeta && (
                        <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${approvalMeta.cls}`}>
                          {approvalMeta.label}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-gray-500 basis-full">{forecast.detail}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-xs text-gray-400 italic">לא בוקשו מערכות סביבה.</p>
          )}
          <p className="text-[11px] text-gray-500">
            {connectorsFailedNow
              ? "קטלוג הקישוריות: לא ידוע — לא נטען."
              : implementedConnectors
              ? `קטלוג הקישוריות: ${connectorTypes.length} ספקים רשומים, ${implementedConnectors.length} ממומשים.`
              : connectorTypes
              ? "קטלוג הקישוריות: לא ידוע — הוחזר בצורה שאינה מוכרת."
              : "קטלוג הקישוריות: טוען…"}
          </p>
          <p className="text-[11px] text-gray-400">
            הבקשה מוסיפה הנחיות שימוש בלבד ולא נשמר מפתח. עם היצירה הבקשה תפורסם במסלול המבוקר —
            מערכת נתמכת תקבל רשומת חיבור ללא פרטי גישה, והתוצאה המדויקת לכל בקשה תוצג מיד לאחר היצירה.
            התיוג לצד כל מערכת למעלה מראה מראש אילו מהמערכות שבחרת נתמכות ואילו מנוהלות פלטפורמית
            ולא תיווצר עבורן רשומת חיבור.
          </p>
        </PreviewSection>

        <PreviewSection tone="rose" testId="preview-limits" title="מגבלות" state="נרשמות ביצירה" editStep={7} onEditStep={onEditStep}>
          <dl className="text-sm space-y-1">
            <div className="flex gap-2 items-center">
              <dt className="text-gray-500 w-24 shrink-0">רמת הרשאה</dt>
              <dd className="text-gray-700">
                {tier && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full mr-1 ${
                    tier.value === "tier_1" ? "bg-green-100 text-green-700" :
                    tier.value === "tier_2" ? "bg-amber-100 text-amber-700" :
                    "bg-red-100 text-red-700"
                  }`}>{tier.badge}</span>
                )}
                {tier?.label || form.permission_tier}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500 w-24 shrink-0">רשימת כלים</dt>
              <dd className="text-gray-700 text-xs">
                {unrestricted
                  ? "ללא הגבלת רשימה — כל כלי ברמה זו"
                  : `רשימה מצומצמת: ${(form.allowed_tools || []).length} כלים`}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500 w-24 shrink-0">אוטונומיה</dt>
              <dd className="text-gray-700">{autonomy?.label || form.autonomy_level}</dd>
            </div>
          </dl>
          {/* The row above states WHICH policy was picked; this states what that pick changes. The
              label alone used to promise approval for every action, and this is the last screen
              before the single irreversible one. */}
          <div data-testid="preview-autonomy-effect" className="space-y-0.5">
            <p className="text-[11px] text-gray-500">{AUTONOMY_EQUIVALENCE_NOTE}</p>
            <p className="text-[11px] text-gray-500">
              {toolsFailedNow
                ? AUTONOMY_EFFECT_UNKNOWN
                : !projection
                ? "תחזית כלים: טוען…"
                : autonomyEffectSentence(projection)}
            </p>
          </div>
          <p className="text-[11px] text-gray-500">
            {/* pass 54: three states, two arms. `!projection` is true while the catalog is in
                flight AND when it settles in a shape entitlementProjection cannot read, and this
                paragraph called both of them "did not load" -- while the `tool_catalog` row above
                separates all three (pass 24) and the autonomy paragraph three lines up says
                "loading" about the very same pair. The loading arm reuses that paragraph's own
                wording and the unreadable arm the row's, so no site states a cause it has not
                measured. */}
            {toolsFailedNow
              ? "תחזית כלים: לא ידוע — קטלוג הכלים לא נטען."
              : toolsLoading
              ? "תחזית כלים: טוען…"
              : !projection
              ? "תחזית כלים: לא ידוע — קטלוג הכלים הוחזר בצורה שאינה מוכרת."
              : `תחזית כלים ברמה זו: ${projection.entitled} מורשים כעת, ${projection.strongGated} באישור מוגבר, ${projection.blocked} חסומים מתוך ${projection.registered} רשומים.`}
          </p>
          {projection && projection.listExempt > 0 && !toolsFailedNow && (
            <p className="text-[11px] text-amber-700" data-testid="preview-allowlist-exempt">
              הרשימה המצומצמת שנבחרה מגבילה כלי שינוי-מצב בלבד — {projection.listExempt} כלים לקריאה בלבד נשארים נגישים לעובד גם ללא סימון.
            </p>
          )}
          <p className="text-[11px] text-gray-400">
            אין נתיב הענקה בתצוגה זו; כלי שינוי-מצב נשארים תחומי-אישור גם כשהם נגישים.
          </p>
        </PreviewSection>

        <PreviewSection tone="emerald" testId="preview-readiness" title="סיכום מוכנות" state={null}>
          <div className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${verdict.cls}`} data-testid="readiness-verdict">
            {verdict.label}
          </div>
          {(publishForecast.skills.length > 0 || publishForecast.systems.length > 0) && (
            <p data-testid="preview-publish-forecast-summary" className="text-[11px] text-gray-600">
              {`תחזית הפרסום: ${publishForecast.counts.created} ייווצרו, ${publishForecast.counts.requestedOnly} רק מבוקשים ללא רשומה לעובד, ${publishForecast.counts.pendingApproval} ממתינים לאישור או להגדרה, ${publishForecast.counts.refused} נדחים. יצירה והמתנה לאישור נספרות בנפרד — פריט יכול להיכלל בשתיהן.`}
            </p>
          )}
          <ul className="space-y-2 mt-1">
            {readinessChecks.map((c) => (
              <ReadinessRow key={c.key} state={c.state} label={c.label} detail={c.detail} />
            ))}
          </ul>
          {!canOpenEmployeePage && (
            <p role="note" data-testid="preview-operator-access" className="text-[11px] text-amber-700">
              {EMPLOYEE_PAGE_NO_ACCESS_NOTICE}
            </p>
          )}
          <div role="note" data-testid="preview-unverified" className="rounded-lg border border-gray-200 bg-white/60 p-2 mt-1">
            <p className="text-[11px] font-medium text-gray-600 mb-1">לא נבדק לפני יצירה (אין ראיה — לא מוצג כהצלחה)</p>
            <ul className="text-[11px] text-gray-500 space-y-0.5 list-disc pr-4">
              <li>רישום המודל במרשם המודלים בצד השרת — נבדק רק כשהשרת חושף נקודת קצה לכך, ואינו זמין בגרסה זו.</li>
              {/* The routing the created employee actually gets is a server contract
                  (model_provider_routing.build_assignment_contract, returned as `provider_model` on
                  GET /api/agents/{id}): the provider is the deployment's, a HIGH-priority ticket is
                  routed to ANALYSIS_MODEL_HIGH_PRIORITY, and the fallback list is explicit. None of
                  the three is readable before creation, so none of them is asserted here. */}
              <li>ניתוב המודל בזמן ריצה — מרשומת העובד נלקח שם המודל בלבד; הספק נקבע בהגדרת המערכת, וכרטיס בעדיפות גבוהה מנותב למודל ההחלפה של המערכת כשהוא מוגדר. הניתוב בפועל נקרא מחוזה provider_model בדף העובד לאחר היצירה.</li>
              <li>שיוך מיומנויות ורמות מדודות — אינם קיימים לפני יצירה. {SKILL_LEVEL_GATE_SHORT} (§28)</li>
              <li>בדיקת חיבור חיה למערכות — מתבצעת מדף העובד לאחר יצירת החיבור בפועל.</li>
              {/* Pass 28: this item carried the SAME conjoined claim as the employee row,
                  inside the list of things the screen cannot know before creation -- so the
                  permission half was presented as a deferral when autonomy_policy.
                  check_entitlement decides it from the tier and allowlist alone and the
                  entitlement row above renders the answer. The measured-proficiency half is
                  genuinely unverifiable here and stays. Surviving copy of a correction, the
                  shape this task keeps hitting: grep the sentence repo-wide. */}
              <li>מיומנות מדודה של העובד — נגזרת מנתוני ריצות לאחר היצירה. ההרשאות בפועל אינן ממתינות לריצות: הן נקבעות מהרמה ומרשימת הכלים שנבחרו כאן, וסיכום המוכנות שלמעלה מונה אותן.</li>
            </ul>
          </div>
        </PreviewSection>
      </div>
    </div>
  );
}

// ─── Post-creation: governed publish of the wizard's intent ──────────────────

// The wizard's skill/system intent is replayed through POST /api/agents/{id}/builder-publish-v3
// after creation. Every requested item always has a row here — the rows are built from the
// request, not from the response — so an in-flight or failed publish can never make a request
// look like it vanished: `pending`, `failed` and `unreported` label the states a missing
// response cannot know, and the applied / already_present / platform_managed / refused /
// unsupported states come from the governed outcome itself.
//
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141: a row is joined to its outcome by ORDER AND
// ARITY ("one outcome per requested item, in request order" — the response's own documented
// contract, pinned in backend/tests/test_employee_builder_v3.py), with the echoed `requested`
// key used only as the primary, unambiguous match. The key alone is not a join column: the
// governed backend passes that echo through secret redaction on its unsupported arms, which
// this wizard can reach because the skill step takes a free-text domain — so a key-only join
// dropped exactly the rows classified `unsupported` and showed them as "not yet applied".
const PUBLISH_SKILL_STATE_META = {
  applied:         { label: "הוחל — נרשמה רשומת מועמדת ברמה 0 (ללא הרשאה)", cls: "bg-green-100 text-green-700" },
  already_present: { label: "כבר היה קיים — לא נוצרה כפילות",               cls: "bg-blue-100 text-blue-700" },
  unsupported:     { label: "לא נתמך — לא נוצר דבר",                        cls: "bg-amber-100 text-amber-700" },
  pending:         { label: "ממתין — טרם הוחל",                             cls: "bg-gray-100 text-gray-500" },
  failed:          { label: "ההחלה נכשלה — לא הוחל",                        cls: "bg-red-100 text-red-700" },
  refused_unverified: { label: "הפרסום נדחה — לא ניתן לאשר אם נרשמה רשומה", cls: "bg-red-50 text-red-700" },
  unconfirmed:     { label: "לא התקבלה תוצאה — לא ניתן לאשר אם הוחל",       cls: "bg-red-50 text-red-700" },
  unreported:      { label: "לא דווחה תוצאה — לא הוחל",                     cls: "bg-red-50 text-red-700" },
  unknown:         { label: "מצב לא מוכר — לא ניתן לאשר החלה",           cls: "bg-red-50 text-red-700" },
};

const PUBLISH_CONNECTION_STATE_META = {
  applied:          { label: "נוצר חיבור לעובד — ללא פרטי גישה",        cls: "bg-green-100 text-green-700" },
  already_present:  { label: "החיבור כבר היה קיים",                     cls: "bg-blue-100 text-blue-700" },
  platform_managed: { label: "מנוהל פלטפורמית — אין חיבור פר-עובד",     cls: "bg-gray-100 text-gray-600" },
  refused:          { label: "נדחה בפרסום קודם — לא נוצר חיבור, וניסיון חוזר של אותה גרסה לא יפעיל", cls: "bg-red-100 text-red-700" },
  unsupported:      { label: "לא נתמך — לא נוצר דבר",                   cls: "bg-amber-100 text-amber-700" },
  pending:          { label: "ממתין — טרם הוחל",                        cls: "bg-gray-100 text-gray-500" },
  failed:           { label: "ההחלה נכשלה — לא הוחל",                   cls: "bg-red-100 text-red-700" },
  unconfirmed:      { label: "לא התקבלה תוצאה — לא ניתן לאשר אם נוצר חיבור", cls: "bg-red-50 text-red-700" },
  unreported:       { label: "לא דווחה תוצאה — לא נוצר חיבור",          cls: "bg-red-50 text-red-700" },
  unknown:          { label: "מצב לא מוכר — לא ניתן לאשר חיבור",      cls: "bg-red-50 text-red-700" },
};

// PROD-BUILDER-060: what the persisted record's state MEANS, the same five-state vocabulary the
// governed publish returns in `intent_state` (backend/app/schemas/employee_builder_v3.py). The
// write state above answers "what did publication do"; this answers "what is the employee's
// canonical assignment/connection state now". Neither upgrades the other: an applied candidate
// Skill is still approval_pending, and a created connection with unset credentials is still
// approval_pending rather than access granted.
export const PUBLISH_INTENT_STATE_META = {
  requested:        { label: "מבוקש — טרם נוצרה רשומה קנונית",              cls: "bg-gray-100 text-gray-600" },
  eligible:         { label: "כשיר — הרשומה הקנונית קיימת ואינה דורשת פעולה", cls: "bg-green-100 text-green-700" },
  approval_pending: { label: "ממתין לאישור או להגדרה — הרשומה אינה פעילה",  cls: "bg-amber-100 text-amber-700" },
  blocked:          { label: "חסום — המצב הקנוני מונע שימוש",               cls: "bg-red-100 text-red-700" },
  unsupported:      { label: "לא נתמך — אין חוזה קנוני",                    cls: "bg-amber-100 text-amber-700" },
  unreported:       { label: "מצב קנוני לא דווח — לא ניתן לאשר",            cls: "bg-red-50 text-red-700" },
};

// The canonical product contract each item reaches. `none` is an answer, not a missing field.
export const PUBLISH_CANONICAL_CONTRACT_META = {
  agent_role_text:     "טקסט תפקיד בעובד",
  skill_assignment:    "שיוך מיומנות",
  agent_connection:    "חיבור פר-עובד",
  platform_connection: "חיבור פלטפורמי",
  none:                "ללא חוזה קנוני",
};

// The agent-connection system type a wizard key resolves to. Only `ms365` is supported today
// (SYSTEM_PUBLISH_SCOPE); the other two keys are platform-managed and have no per-employee row.
// The publish outcome's own `system_type` is preferred whenever it is present, so this map never
// overrides the server's answer.
const ENV_TOOL_SYSTEM_TYPES = { ms365: "m365_graph" };

const expectedSystemType = (key, outcome) =>
  (outcome && outcome.system_type) || ENV_TOOL_SYSTEM_TYPES[key] || null;

// Canonical lookups are the SERVER's identity: the same fold the publish/registry uses. They
// return null rather than guessing, and callers report null as absence — never success.
export function canonicalSkillRecord(skills, domain) {
  if (!Array.isArray(skills)) return null;
  const folded = canonicalSkillDomainLabel(domain);
  return skills.find((skill) => canonicalSkillDomainLabel(skill?.domain) === folded) || null;
}

export function canonicalConnectionRecord(connections, systemType) {
  if (!Array.isArray(connections) || !systemType) return null;
  return connections.find((row) => row?.system_type === systemType) || null;
}

// Statuses a reverse proxy in front of the API answers with WITHOUT the application having
// answered. nginx/nginx.conf proxies /api/ with no proxy_read_timeout, i.e. nginx's 60s default,
// while builder_publish_v3 takes pg_advisory_xact_lock, which blocks indefinitely behind a
// concurrent publish for the same employee — so a 504 over a publish that then commits is
// reachable, not hypothetical. A body from one of these is the gateway's, so the application's
// rollback guarantee does not cover it.
const PUBLISH_GATEWAY_STATUSES = [408, 502, 503, 504];

// Statuses THIS route can only have answered with before its body could register anything, so
// they are the only ones on which a determinate "the skill was not applied" is safe. Measured
// at HEAD: the route's single pre-loop `raise` is the agent lookup's 404 and the route body
// raises no other HTTPException at all; 401/403 come from Depends(_manage) /
// require_agent_permission and 422 from RequestValidationError, all resolved before the body.
// Everything else answered means the body was entered and the skill loop — which runs first and
// unconditionally — may already have registered a record. Deliberately a closed list of SAFE
// statuses rather than a list of unsafe ones: an unrecognized or absent status falls through to
// the indeterminate arm, never to a false determinate negative.
//
// The accepted cost, stated so it is not re-chased: a 500 raised BEFORE the skill loop (the
// agent select, or advisory_xact_lock failing) registered nothing, and this rule still reports
// that row as unverifiable. That is the over-statement direction — an uncertainty where there is
// certainty — and the client cannot tell the two 500s apart, because the only party that knows
// whether the loop ran is the route. Closing it properly means the route reporting the rows it
// registered on its error path, i.e. a new member of BuilderPublishV3SkillOutcome's `state`
// Literal in app/schemas/employee_builder_v3.py, which is not in this task's FILES_OWNED.
// Unblock condition: ownership of that schema. Under-reporting an application was the live
// defect; over-reporting an uncertainty is the safe direction and is deliberate.
// (The one in-body producer of a
// status on this list would be app/main.py's DBAPIError handler, which also answers 422 — it is
// scoped to SQLSTATE 22001 / NUL over the columns this route writes, and the connection row's
// only text column is `display_name`, taken from the connector registry, so no operator text
// reaches a bounded column here. Pinned in the other language.)
const PUBLISH_PRE_BODY_REFUSAL_STATUSES = [401, 403, 404, 422];

// True when the answer itself proves the route refused before its body ran. Conservative in the
// one direction that matters: no response, or a status this list does not hold, is NOT a proof.
export function publishRefusedBeforeTheRouteBody(error) {
  const response = error?.response;
  if (!response) return false;
  return PUBLISH_PRE_BODY_REFUSAL_STATUSES.includes(response.status);
}

// What the panel says when the application never answered. It states the uncertainty instead of
// a determinate negative, and then states the one thing that makes the uncertainty actionable:
// the publish is idempotent at both halves by contract (a replayed publish answers
// `already_present` and writes nothing new — BuilderPublishV3SkillOutcome /
// BuilderPublishV3ConnectionOutcome, pinned in backend/tests/test_employee_builder_v3.py), so a
// retry cannot double-create and its report is the real state.
// Pass 38. What the panel can say when the APPLICATION answered with a refusal but the skill
// half was never inside the transaction that refusal rolled back. Three things, in the order the
// operator needs them: the asymmetry, the one place the answer actually exists, and what a retry
// does — including the arm on which the retry CANNOT succeed, because _STATE_LOAD_ERROR is set
// once per process and nothing clears it (1 assignment site, measured), so every replay
// recomputes `already_present` and then discards it by raising. The replay label is interpolated
// rather than quoted so the promise and the row cannot drift.
// The generic fallback banner asserts a determinate negative about EVERY request below it, and
// on the arm above that is false for the skill rows. Its first clause is byte-identical; only
// the clause that spoke for the rows is replaced, by a deferral to the rows themselves — which
// after this pass carry the per-surface truth. The determinate banner stays reachable and stays
// true on a systems-only publish, where the rolled-back AgentConnection row is the whole of what
// was requested; it is the ONLY shape that reaches it with a skill requested, because every
// pre-body status answers with a string `detail` (401/403/404 from the route, and a 422 flattened
// to a string by services/api.js) and so takes the server-detail arm instead of this fallback.
const PUBLISH_FAILED_BANNER_SKILL_UNVERIFIED =
  "החלת הבקשות נכשלה — ראה להלן, לכל בקשה, מה ניתן לאשר.";

const PUBLISH_SKILL_REFUSAL_UNVERIFIED_NOTICE =
  "השרת דחה את הפרסום, אך הדחייה אינה מבטלת רשומת מיומנות: רשומת חיבור פר-עובד נוצרת בתוך " +
  "הטרנזקציה של הבקשה ומתבטלת עם השגיאה, ואילו רשומת המיומנות נרשמת במרשם ההקצאות של השירות — " +
  "מחוץ לטרנזקציה — ואין מסלול שמבטל אותה. " +
  "לכן לגבי התחומים שלהלן שבמילון המפוקח לא ניתן לדעת מכאן אם נרשמו; בדוק בעמוד המיומנויות של העובד — " +
  "רשומה שנרשמה מוצגת שם. " +
  `ניסיון חוזר אינו יוצר כפילות, ואם יצליח ידווח „${PUBLISH_SKILL_STATE_META.already_present.label}” ` +
  "על תחום שנרשם; אם הסיבה היא מאגר המצב של השירות, הוא ייכשל שוב עד שהמאגר יתוקן בשרת.";

const PUBLISH_UNCONFIRMED_NOTICE =
  "לא התקבלה תשובה מהשרת — לא ניתן לדעת אם הבקשות שלהלן הוחלו. " +
  "ניסיון חוזר בטוח ואינו יוצר כפילות: פרסום חוזר מדווח את המצב האמיתי של כל בקשה.";

// The same uncertainty one action EARLIER, where it loses BOTH halves of the intent instead
// of one. DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 34): pass 26 built everything
// above for the publish, and nobody asked the same question of POST /api/agents/ -- the
// action the publish wholly depends on. create_agent (backend/app/routers/agents.py) does
// db.add + await db.flush() and never commits; app/database.py's get_db commits AFTER the
// route returns. So an error with no `response` at all, or one of PUBLISH_GATEWAY_STATUSES,
// sits over an Agent row (and its activity_log row) that may already be durable -- while
// publishIntentAfterCreate runs only from createMutation's onSuccess, so there is no
// ScheduleDialog, no PublishOutcomeSection and not one row per requested item: both halves
// of the intent are dropped with nothing reported.
//
// Two measured facts make this the OPPOSITE of the notice above rather than a copy of it:
//   * a replayed publish is idempotent by contract (backend/tests/test_employee_builder_v3
//     .py::test_replayed_publish_is_idempotent_and_writes_nothing_new), while `agents.name`
//     carries no uniqueness constraint in the model or in any migration -- so a retry here
//     can create a SECOND employee, and the publish's "a retry is safe" sentence would be
//     false if it were reused;
//   * `agents.is_enabled` defaults to False, so an employee that WAS created performs no
//     work (no ticket assignment, no run, no conversation) -- which is the bound that keeps
//     this a disclosure rather than a degradation.
// Both are pinned in the other language, in the direction that can start lying.
const CREATE_UNCONFIRMED_NOTICE =
  "לא התקבלה תשובה מהשרת — לא ניתן לדעת אם העובד נוצר. " +
  "בשונה מהפרסום, ניסיון חוזר אינו בטוח: שם עובד אינו חייב להיות ייחודי, ולחיצה נוספת עלולה ליצור עובד שני. " +
  "בדוק קודם במסך „הצוות הדיגיטלי” אם העובד קיים. " +
  "אם הוא נוצר — הוא במצב מושבת ואינו מבצע עבודה, והמיומנויות והמערכות שביקשת לא הוחלו ולא דווחה עליהן תוצאה, " +
  "כי הפרסום רץ רק לאחר יצירה שתשובתה התקבלה. ואם נוצר: " + EMPLOYEE_ROW_NO_REMOVAL_PATH + ".";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 77), the UNARMED ERRAND IN A PINNED
// SET. Pass 70 censused the OPERATOR's axis: the wizard admits can("agents_manage") while every
// destination it names after creation reads agents_view, and require_agent_permission
// special-cases the ADMIN role for agents_manage and for nothing else. Its own backend pin
// (test_the_destinations_the_wizard_names_are_all_gated_on_agents_view) resolves the gate of
// NINE destination routes and its comment names three groups -- "the employee page, the
// roster, and the skills surfaces". Its disclosure names TWO of them.
//
// Measured at this tip, over comment-stripped source: the roster screen is named at exactly 1
// code site in the whole wizard -- the sentence directly above -- and in 0 of
// EMPLOYEE_PAGE_NO_ACCESS_NOTICE's three clauses (which name the employee page twice and the
// skills page once). The notice region itself carries 0 arms on the access axis: 0 each of
// `agents_view`, EMPLOYEE_PAGE_VIEW_GRANT, BUILDER_GATE_GRANT, canOpenEmployeePage and the two
// Hebrew permission needles, against file-wide code controls of 1 / 4 / 2 / 9 / 45 / 2 -- so
// the file has the vocabulary and had applied none of it here.
//
// Why this is the arm where it costs: the notice renders only when the create went UNANSWERED,
// so an Agent row may already be durable, a retry is NOT safe (agents.name carries no
// uniqueness) and the row it may create has no removal path at either layer. The one check
// that prevents a permanent duplicate is the errand this sentence gives -- and GET /api/agents/
// is Depends(_view), Layout.jsx gates /agents on agentPermission: "agents_view" (so it is not
// in the nav for this actor at all, while /employee-builder is adminOnly with no
// agentPermission), and AgentsPage's roster query takes its isError arm on the 403
// require_agent_permission raises (detail is a string that is not "Not authenticated", so
// services/api.js does not force a logout). The operator is left with the one control the
// screen offers: the same button.
//
// Closed by labelling only, and SLICED rather than retyped: the screen's name comes out of the
// sentence above and the unavailability phrase out of pass 70's own COST clause, so the access
// axis cannot be worded two ways on one screen and the roster phrase stays at 1 literal site.
// Both grant names are interpolated from pass 70's exported constants, so the clause cannot
// drift from the gates it describes. The remedy is NOT repeated here: pass 70's
// EMPLOYEE_PAGE_NO_ACCESS_REMEDY renders on the same screen under the same predicate, and a
// second copy is the defect pass 63's probe table exists to catch.
//
// The composer's default is the CAUTIOUS arm (pass 2/62: default a new gate to the member it
// is safe to be wrong about) -- showing the qualifier to an operator who does hold the grant
// is imprecise in the alarming direction, which pass 27 makes the safe one. The predicate is
// licensed by pass 75's POSITIONAL LICENCE pin: can() is false for every permission until the
// payload lands, and what keeps this from being a determinate refusal over an unarrived
// payload is that the permissionsLoading / permissionsFailed early returns precede
// `const canOpenEmployeePage`. That ordering is pinned once, by pass 75; not re-asserted here.
const CREATE_UNCONFIRMED_ROSTER_SCREEN =
  CREATE_UNCONFIRMED_NOTICE.split("„")[1].split("”")[0];
const CREATE_UNCONFIRMED_ACCESS_UNAVAILABLE =
  EMPLOYEE_PAGE_NO_ACCESS_COST.split("— ")[2].split(",")[0];
export const CREATE_UNCONFIRMED_ROSTER_GRANT_CLAUSE =
  `גם מסך „${CREATE_UNCONFIRMED_ROSTER_SCREEN}” דורש ${EMPLOYEE_PAGE_VIEW_GRANT} ולא ` +
  `${BUILDER_GATE_GRANT} — ולכן הבדיקה הזאת ${CREATE_UNCONFIRMED_ACCESS_UNAVAILABLE}.`;
export function createUnconfirmedNotice(canOpenEmployeePage = false) {
  return [
    CREATE_UNCONFIRMED_NOTICE,
    canOpenEmployeePage ? "" : CREATE_UNCONFIRMED_ROSTER_GRANT_CLAUSE,
  ].filter(Boolean).join(" ");
}

// The same uncertainty one action LATER, on the third and last write this screen performs, and
// the only one of the three whose row is already committed when the response is composed.
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 35): pass 26 armed the publish and pass
// 34 the create -- whose own lesson was "when a disclosure is built for step 2 of a two-step
// action, ask whether step 1 has it". Nobody asked it of step 3. Measured:
//   * routers/scheduled_checks.py::create_check holds exactly one `await db.commit()`, and it
//     runs BEFORE the return -- unlike the publish and the create, which both rely on get_db
//     committing after the route returns. So the row is durable at a strictly earlier point and
//     any later failure (refresh, serialization, a lost response, a gateway status) sits over it;
//   * `scheduled_checks` carries 0 unique columns, 0 UniqueConstraint and 0 unique indexes, and
//     create_check does 0 ScheduledCheck lookups and raises no 409 -- there is no duplicate
//     guard anywhere, so a second press creates a second row;
//   * the one guard on the platform against the harm a duplicate causes is keyed on the check's
//     own id at BOTH layers (the advisory lock and the in-flight AgentRun query), with no
//     agent-scoped term -- and its own comment names that harm as "a duplicate ticket + AgentRun
//     + paid LLM call each time". Two rows do not share an id, so neither suppresses the other.
// Hence the opposite of the publish's sentence, like pass 34's and for the same reason: a retry
// here is NOT safe. All three facts are pinned in the other language, in the direction that can
// start lying.
// Pass 36 (this session, reviewing pass 35's own delivery before pushing it): the clause above
// asserting what a duplicate COSTS had 0 arms on the activation axis, and the axis decides it.
// `agents.is_enabled` lives on AgentUpdate and not on AgentCreate (schemas/agent.py:96 vs
// :77-89) and defaults False (models/agent.py:34), so `activated` is false in every state this
// dialog can reach -- and scheduler._fire_scheduled_check's first act is
// `if not agent or not agent.is_enabled: _bump_next(check); commit; return None`. So in the only
// reachable state the two checks open NO ticket and run NO analysis: pass 35's own section 41.5
// measured that bound ("the harm begins at activation ... not immediately") and the shipped
// string carried none of it, while this same dialog states it twice twenty lines below (the
// `activated` ternary and `firingNotice`'s named skip causes). Worse than an overstatement: the
// sentence named a symptom -- separate tickets, hourly -- that cannot appear yet, so an operator
// who verifies it the way it invites finds nothing and concludes no duplicate exists, over two
// rows that are really there. Split per arm; the uncertainty and the remedy are the same in both
// states, only the consequence differs. The enabled arm is byte-identical to pass 35's clause
// except for its leading connective, and is unreachable today -- kept because it is the true one
// if a create path ever sets is_enabled, with the default pinned in the other language.
// Pass 55 split the SCHEDULE_UNCONFIRMED_OPENING of pass 35 into its CAUSE and the
// arm-independent MECHANISM below. Pass 36 wrote the lesson on this very family (the PIN
// can be the conflation, after it had to split SCHEDULE_NOCANCEL_CLAUSE for the same
// reason) and it applies one arm further out: the retry/no-cancel mechanism is a property
// of the TABLE and of the dedup key the server uses, not of which failure produced the
// uncertainty, so BOTH uncertainty arms need it and only the cause sentence differs. The
// split is byte-neutral: this constant, a space, and SCHEDULE_RETRY_UNSAFE_MECHANISM equal
// the bytes pass 35 shipped, asserted by EQUALITY in the other language against the text
// that pass pinned rather than against this file.
const SCHEDULE_UNCONFIRMED_CAUSE =
  "לא התקבלה תשובה מהשרת — לא ניתן לדעת אם הבדיקה המתוזמנת נוצרה.";
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 55), the COMMITTED PREFIX. The arm
// above covers the two members of this axis that share the unanswered predicate. The
// comment of pass 35, forty lines up, measured FOUR of them (any later failure: refresh,
// serialization, a lost response, a gateway status) and the other two ANSWER with a status,
// so publishApplicationAnswered is true for them, scheduleUnanswered is false, and the only
// thing the dialog rendered was the generic sentence. Measured at HEAD, not read:
//   . routers/scheduled_checks.py::create_check holds its single `await db.commit()` and
//     then TWO more statements, `await db.refresh(check)` (a fresh SELECT round trip) and
//     `return _to_response(check, agent.name)` plus the response_model serialization. So
//     the row is durable before anything that composes the answer runs, and that is unique
//     to this write: create_agent and builder_publish_v3 both hold 0 commits and rely on
//     get_db committing after the route returns.
//   . app/database.py::get_db rolls back on an exception, and a rollback after that commit
//     reaches nothing, so an answered error carries no guarantee here at all.
//   . every post-commit failure answers 500: the DBAPIError handler in app/main.py
//     re-raises anything outside SQLSTATE 22001/22021/22P05, and a ResponseValidationError
//     is a 500. 500 is neither a gateway status nor a member of
//     PUBLISH_PRE_BODY_REFUSAL_STATUSES.
// So the discriminator is the one the publish panel already uses, and it was read at 1 of
// the 3 write surfaces on this screen: only a status this route can ONLY have answered with
// before its body ran proves nothing was written. For create_check those are exactly
// 401/403/404/422 (require_admin, the feature flag, the interval, the agent lookup,
// RequestValidationError), the same closed list, reused rather than copied. Its absence at
// the create step is CORRECT and stays, because create_agent holds no commit of its own.
//
// Accepted over-statement, stated so it is not re-chased: a 500 raised BEFORE the commit,
// including the commit itself failing, wrote nothing durable and still takes this arm. The
// client cannot tell the two 500s apart, since only the route knows whether it got past its
// commit, and over-stating an uncertainty is the safe direction (pass 38 took the same
// trade for the skill half). Unblock condition: the route reporting which side of the
// commit it failed on, i.e. app/routers/scheduled_checks.py, which is not in FILES_OWNED.
const SCHEDULE_COMMITTED_ROW_CAUSE =
  "השרת החזיר שגיאה, אך הבדיקה נשמרת בבסיס הנתונים לפני שהתשובה נבנית — ושגיאה שנוצרה אחרי השמירה משאירה את הבדיקה קיימת. לכן לא ניתן לדעת כאן אם הבדיקה המתוזמנת נוצרה.";
// Arm-independent, and the reason the split above was worth making: it is true of both
// uncertainty arms because it describes the table and the dedup key the server uses.
const SCHEDULE_RETRY_UNSAFE_MECHANISM =
  "בשונה מהפרסום, ניסיון חוזר אינו בטוח: לבדיקה מתוזמנת אין אילוץ ייחודיות, ולחיצה נוספת עלולה ליצור בדיקה שנייה. " +
  "שתי בדיקות אינן מבטלות זו את זו: מניעת הכפילות בשרת מזהה ריצה פעילה לפי מזהה הבדיקה עצמו.";
const SCHEDULE_DUPLICATE_COST_ACTIVE =
  "לכן כל אחת מהן תפתח פנייה נפרדת ותריץ ניתוח AI נפרד בכל מרווח — והמרווח הקצר ביותר שניתן לבחור כאן הוא שעה.";
const SCHEDULE_DUPLICATE_COST_DISABLED =
  "כל עוד העובד מושבת, אף אחת מהן לא תפתח פנייה ולא תריץ ניתוח: כל מועד שיגיע יידלג ומועד הריצה הבא ומועד ההרצה האחרונה יתקדמו, " +
  "ולכן חיפוש פניות לא יגלה אם נוצרה בדיקה כפולה. מרגע שיופעל מדף העובד, " +
  "כל אחת מהן תפתח פנייה נפרדת ותריץ ניתוח AI נפרד בכל מרווח — והמרווח הקצר ביותר שניתן לבחור כאן הוא שעה.";
const SCHEDULE_UNCONFIRMED_WHERE =
  "בדוק קודם במסך „בדיקות מתוזמנות” אם הבדיקה קיימת.";
// One composer for both uncertainty arms, so the mechanism, the activation-dependent cost
// and the place to check can never diverge between them. Only the cause is passed in.
function scheduleUncertaintyNotice(cause, activated) {
  return [
    cause,
    SCHEDULE_RETRY_UNSAFE_MECHANISM,
    activated ? SCHEDULE_DUPLICATE_COST_ACTIVE : SCHEDULE_DUPLICATE_COST_DISABLED,
    SCHEDULE_UNCONFIRMED_WHERE,
  ].filter(Boolean).join(" ");
}
function scheduleUnconfirmedNotice(activated) {
  return scheduleUncertaintyNotice(SCHEDULE_UNCONFIRMED_CAUSE, activated);
}
function scheduleCommittedRowNotice(activated) {
  return scheduleUncertaintyNotice(SCHEDULE_COMMITTED_ROW_CAUSE, activated);
}

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 40), the OUTER GATE. The notice below
// states what a created check WILL DO, on the succeeded path, and it enumerates the causes of
// a skipped due time as a closed two-member list: a disabled employee, or a previous run of
// the same check still active. Measured: those are exactly
// scheduler._fire_scheduled_check's two early returns, and both do `_bump_next(check)` +
// commit, so the notice is a FAITHFUL mirror of that function and says truly that such a due
// time `only advances the next run time`. The blind spot is one stack frame up, in the caller.
//
// scheduler's poll loop wraps the whole due-check query in
// `if dispatch_allowed:` where `dispatch_allowed = await _new_work_dispatch_allowed(db)` =
// `_flag_enabled(db) and not _crisis_mode_active(db)`. Under either gate
// `_fire_scheduled_check` is never called at all, so `_bump_next` never runs; and `_bump_next`
// (scheduler.py, `check.next_run_at = now + timedelta(...)`) is the scheduler's ONLY writer of
// `next_run_at`, the other writers being the admin router's own create/update. So the
// occurrence is skipped AND nothing advances, which is the opposite timestamp behaviour from
// both named causes: the row stays overdue, and because _POLL_INTERVAL_SECONDS is 60 the first
// poll after the suspension ends processes it, once, re-basing the cadence to that moment.
//
// Reachable with the gate ALREADY CLOSED at the moment the promise is made:
// routers/scheduled_checks.py::create_check gates on `_flag_enabled` only and holds zero
// crisis references (the router's only crisis mentions are its import and `run_now`, whose own
// comment says `The scheduler's own dispatch of due checks is already gated on Crisis Mode`).
// So a 201 and this dialog are reachable while Crisis Mode is active, and the flag half can
// only close afterwards because the create would otherwise have been refused 403.
//
// And the operator reading the promise is authorized to cause the state that voids it:
// `ai_crisis_mode_enabled` is the identical SystemSettings key in the scheduler and in
// routers/agents.py, whose activate_crisis_mode_endpoint depends on
// `_manage = require_agent_permission("agents_manage")` -- the same permission this wizard
// gates itself on. Arm count on the dispatch axis before this: 0 operator-visible sites
// (sweep of the wizard for crisis / suspension / feature-flag vocabulary returns 3 hits, all
// SOURCE COMMENTS about crisis as a check_action/check_entitlement parameter, a different
// axis), against controls of 11 activation sites and 5 due-time sites.
//
// Stated, never inferred (pass 16's rule): neither gate is in any payload this wizard holds,
// so the clause qualifies the promise instead of computing it, and it promises no ticket on
// resume -- `ובכפוף לתנאים שלמעלה` keeps the activation axis above in force, which is pass 36's
// defect (a cost sentence with 0 arms on the axis deciding whether the cost happens) not
// repeated. Rendered unconditionally because the gate is a property of the scheduler, not of
// this employee, so pass 20's shadowing arm is not introduced either.
//
// NOT duplicated at the other two sites carrying `מועד הריצה הבא` (the `activated` ternary's
// disabled arm and SCHEDULE_DUPLICATE_COST_DISABLED), so pass 41 does not re-chase it: the
// firing notice renders UNCONDITIONALLY and immediately below the ternary, and the unconfirmed
// notice renders below both, so this clause is on screen in every state those two sentences
// reach. Three copies of one qualifier is the drift this chain keeps finding.
// Pass 74 (the UNEARNED INFERENCE). Pass 40 wrote this clause as the one statement on this
// screen that the scheduler can stop dispatching entirely, and gave its two gates different
// epistemic status: the feature flag KNOWN ON, Crisis Mode unknown. The flag half was an
// inference -- `ולכן היא פעילה כעת` -- whose premise is a 201 from the
// admin router's own check-creation, the one request on this screen that has NOT been made
// when the sentence is read. firingNotice renders unconditionally the moment the created
// employee is in hand, and the schedule create only happens if the operator clicks the button
// below it; on success the dialog navigates away, so the clause could never be READ in the one
// state where its own derivation holds. Arm count on has-the-flag-been-tested-yet: 0.
//
// Measured, and the same-symbol control is in the module that creates the employee: the owned
// agents router IMPORTS the pilot-flag helper from the agent-runs router and calls it at 5
// sites, all five browser_work -- and at 0 sites inside either of the two writes this wizard
// performs before this dialog opens. The app mounts that router with no router-level
// dependencies, so those two carry only their own manage-permission and session dependencies.
// The flag also fails CLOSED: both helpers return False for a MISSING system_settings row and
// the settings read reports the two pilot flags as false when unset, which the settings screen
// mirrors as a false default -- so flag-off is the shipped default until an admin turns it on,
// not an exotic configuration. With it off the agent-runs router refuses at 23 route sites and
// the scheduler's new-work dispatch gate is false, so the employee does nothing at all.
// Direction of harm: pass 20's dangerous direction, and pass 50's rule -- the REASSURING half
// was the false one, and it told the operator which of the two gates to worry about by naming
// the wrong one.
//
// Qualified, never computed (pass 16): the wizard reads this flag at 0 sites and the one route
// that reports it runs on the settings-admin role -- a different gate from the one this wizard
// admits on (pass 70), so it is in no payload this screen holds. The sibling that DOES read it
// names the state explicitly (the live system map's known-off flag), which is the authority
// this wording borrows. The creation of the check is now named as what TESTS the flag, so the
// operator has a reachable signal, and the closing `no way to know from this screen` now
// covers both gates rather than only Crisis Mode. Still unconditional, for pass 40's reason:
// it is a property of the scheduler, not of this employee's activation state.
const SCHEDULE_PLATFORM_SUSPENSION_CLAUSE =
  "שתי הסיבות לדילוג שלמעלה הן ברמת העובד והבדיקה הזו; מעליהן יש עצירה רוחבית של כל השיגור: " +
  "השרת אינו משגר עבודה חדשה כלל כשתכונת AI Technician מכובה או כשמצב משבר פעיל, " +
  "ואז מועד שמגיע אינו מעובד כלל ומועד הריצה הבא אינו מתקדם — " +
  "כך שהבדיקה אינה מדלגת על מועד אחרי מועד אלא נשארת באיחור ומעובדת פעם אחת, " +
  "בתוך כדקה מתום העצירה ובכפוף לתנאים שלמעלה, והמרווח נמדד משם. " +
  "תכונת AI Technician חייבת להיות פעילה כדי ליצור את הבדיקה, אך יצירת העובד שכבר בוצעה אינה נבדקת מולה — " +
  "ולכן יצירת הבדיקה הזו היא מה שיבדוק אותה, ועד אז אין כאן עדות שהיא פעילה. " +
  "מצב משבר אינו נבדק באף אחת משתי היצירות ויכול להיות פעיל כבר ברגע זה — ואין במסך הזה דרך לדעת.";


// True only when the APPLICATION answered this error: a response object exists (so something
// answered) and its status is not one a proxy produces on the application's behalf. Deliberately
// conservative — an unparseable/absent status counts as answered only because `error.response`
// itself exists, and anything with no response at all is never treated as a determinate refusal.
// Called at three sites: PublishOutcomeSection's rowState, the create step's unconfirmed
// notice, and ScheduleDialog's (pass 35). Nothing in it is publish-specific -- it reads only
// `error.response.status` -- and it keeps the `publish` name because the panel's call line is
// pinned verbatim in the backend suite. Reused rather than copied deliberately: a second
// status list beside this one is the drift this chain keeps finding (two copies of one rule,
// one of them edited).
export function publishApplicationAnswered(error) {
  const response = error?.response;
  if (!response) return false;
  return !PUBLISH_GATEWAY_STATUSES.includes(response.status);
}

// The exact detail literal FastAPI's HTTPBearer answers a request with NO Authorization header
// with (403, not 401 — bearer_scheme = HTTPBearer() in backend/app/middleware/auth.py, whose
// auto_error default raises it). Kept as a constant because services/api.js matches the same
// literal and the two copies are pinned equal in the backend suite.
const SESSION_ENDED_FORBIDDEN_DETAIL = "Not authenticated";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 37). The two error shapes on which the
// SHARED response interceptor in services/api.js removes `access_token`, clears sessionStorage
// and sets `window.location.href = "/"` — i.e. the only two on which this screen stops
// existing. They are NOT a second did-the-write-land axis: `publishApplicationAnswered` returns
// TRUE for both (a response exists and 401/403 are not gateway statuses), which is correct —
// the auth check is a FastAPI dependency, so it is resolved BEFORE the route body and nothing
// was written. That correctness is exactly what hid this: all three write sites therefore took
// their determinate arm, printed the auth layer's own message as though it answered the
// request ("טוקן לא תקין", or the untranslated "Not authenticated"), labelled every requested
// item with a determinate refusal and offered a retry — over a tab that is being signed out,
// so the retry cannot be authenticated and the report is being navigated away from.
//
// Arm count on the session axis before this: 0 of 3 (a precise sweep of the wizard for session
// / expiry / sign-out vocabulary returns 0 sites; the three `מנותק` hits are the EMPLOYEE's
// offline status, against a control of 3 gateway-uncertainty sites). And the wizard persists
// nothing — 0 localStorage/sessionStorage writes in this file — so the draft dies with the tab.
//
// Reachable without any race: the page keeps five React Query subscriptions live while the
// schedule dialog (and the publish report inside it) is open, index.js sets `staleTime: 0` and
// leaves `refetchOnWindowFocus` at its default, so one window focus after the session ended
// fires the interceptor with no action by the operator at all. Mirrors services/api.js
// deliberately rather than importing from it: a second status list beside that one is the drift
// this chain keeps finding, so the two are pinned EQUAL in the other language instead.
export function requestEndedTheSession(error) {
  const response = error?.response;
  if (!response) return false;
  if (response.status === 401) return true;
  return response.status === 403 && response.data?.detail === SESSION_ENDED_FORBIDDEN_DETAIL;
}

const SESSION_ENDED_OPENING =
  "ההתחברות הסתיימה והלשונית הזו מתנתקת כעת וחוזרת למסך הכניסה. " +
  "ההודעה שלמעלה הגיעה משכבת ההזדהות ואינה תשובה על הבקשה עצמה.";
// True at all three sites for the same structural reason, and pinned in the other language:
// the auth dependency is resolved before the endpoint runs, so no route body executed.
const SESSION_ENDED_NOTHING_WRITTEN =
  "בדיקת ההזדהות רצה לפני גוף הבקשה, ולכן הבקשה נדחתה בלי שנכתב דבר.";
const SESSION_ENDED_CREATE_TAIL =
  "העובד לא נוצר, וטיוטת האשף אינה נשמרת בשום מקום — התחברות מחדש תפתח אשף ריק ויהיה צורך למלא אותו מחדש.";
// The publish tail is the only one whose write is preceded by a SUCCEEDED one: this panel
// renders inside ScheduleDialog, which renders only when `createdAgent` is set, so the
// employee certainly exists here while none of its requested intent does. And the wizard
// does not replay the publish once this screen is gone: `publishMutation.mutate` has
// exactly two call sites, the create's `onSuccess` and the retry button below, and leaving
// the dialog navigates away and drops `publishIntent` with the component. That is the
// silent drop this task exists to prevent, so the tail names it rather than apologising.
//
// Not stated here, deliberately: the worse version of this cannot be armed at all. If the
// publish SUCCEEDS and the report renders, a later 401 (a background refetch of any of the
// five queries still subscribed behind this dialog) evicts the page while `publish.isError`
// is false, so no arm of this component can fire. That matters because an applied request
// leaves a record a later screen can show (the AgentConnection row; the candidate Skill in
// the workspace's assigned-Skills section) while a REFUSED one is computed per call and
// stored by nothing at all, making this panel the only place an `unsupported` verdict is
// ever stated. Putting that sentence on THIS arm would attach a true clause to the wrong
// failure: here the server never evaluated the requests, so the report holds no verdict to
// lose.
const SESSION_ENDED_PUBLISH_TAIL =
  "אף בקשה מהבקשות שלהלן לא הוחלה, וכפתור „נסה שוב” לא יצליח עד להתחברות מחדש. " +
  "העובד עצמו כבר נוצר, אך המיומנויות והמערכות שביקשת לא הוחלו — והאשף אינו מריץ את הפרסום שוב לאחר שהמסך הזה נסגר.";
const SESSION_ENDED_SCHEDULE_TAIL = "הבדיקה המתוזמנת לא נוצרה.";

// The three buttons the publish report sits between, as constants rather than three JSX
// literals, so a sentence naming a button cannot outlive that button's label. The rendered text
// is unchanged at all three sites.
//
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 39), the EXPIRING REMEDY. Pass 26 gave the
// unanswered publish its own state and a notice whose second sentence IS the remedy ("a retry is
// safe and creates no duplicates: a repeated publish reports the true state of each request"),
// and pass 38 did the same for the rollback-exempt skill half ("a retry creates no duplicate,
// and if it succeeds it will report already_present"). Both name the retry button below.
//
// Measured at HEAD, not read: `agentsApi.builderPublish` has exactly ONE product consumer in
// frontend/src (this file; same-symbol control `agentsApi.create` has two), `publishMutation
// .mutate` has exactly two product call sites (the create's onSuccess and that button),
// `publish_builder_skill_intent` has exactly one caller in the whole backend and its own module
// docstring calls itself "this module's ONLY writer", and skillsApi is two GETs. So for the skill
// half this button is the only publisher the product has. `publishIntent` and `createdAgent` are
// each assigned at one site and never cleared, this file writes 0 bytes of localStorage or
// sessionStorage, and PublishOutcomeSection is rendered at exactly one site, so the report and
// the retry die with the component. Both exits are live the instant the publish settles (pinned
// by "an in-flight publish keeps both exits and Escape closed until the outcome is on screen"),
// and they are presented as answers to the scheduled-check question above them.
//
// The fact was already stated, at exactly ONE site: SESSION_ENDED_PUBLISH_TAIL, i.e. the single
// arm where that same tail also says the retry "will not succeed until you re-login". So the one
// sentence naming the cost of leaving rendered only where the remedy was already dead, and on
// the two arms that offer a WORKING retry the arm count on that axis was 0.
//
// Disclosed, never degraded: closing the exits after a settled failure would trap the operator
// on a modal with no way out, and the existing test asserting they open ("the operator was
// informed, so leaving is no longer blocked") is right about the gating. What was missing is
// what taking them costs.
//
// Deliberately NOT armed, so pass 40 does not re-chase it: a 200 carrying `unsupported` rows
// loses the same report on the same exits, and pass 37 measured that this panel is the only
// place an `unsupported` verdict is ever stated. That case has no remedy to lose (nothing
// failed, and a replay answers the same refusal), and the operator has just read the verdict, so
// a "not saved" line there would put a warning on a publish that behaved exactly as described.
export const PUBLISH_RETRY_LABEL = "נסה שוב";
export const SCHEDULE_SUBMIT_LABEL = "צור בדיקה מתוזמנת";
export const SKIP_LABEL = "דלג";

// Every clause is scoped to one subject. The last one is skill-only on purpose: a requested
// SYSTEM can be added by hand from the employee page, and recommending that would be
// recommending a duplicate (POST /api/agents/{id}/connections has no duplicate-system_type guard
// and agent_connections no uniqueness), so the sentence claims nothing about connections.
export const PUBLISH_RETRY_SCREEN_BOUND_NOTICE =
  `כפתור „${PUBLISH_RETRY_LABEL}” שלמעלה הוא המקום היחיד שבו האשף מריץ את הפרסום המבוקר הזה, והוא קיים רק במסך הזה: ` +
  `יציאה — ב„${SCHEDULE_SUBMIT_LABEL}” או ב„${SKIP_LABEL}” — מסיימת אותו, והדוח הזה אינו נשמר בשום מקום ולא יוצג שוב. ` +
  "מיומנות מבוקשת אינה ניתנת לפרסום ממסך אחר.";

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 41), the ABANDONED WRITE. This dialog
// closes BOTH exits and Escape while the PUBLISH is in flight -- the write it only REPORTS --
// and the comment above `publishInFlight` says why: "so no request can be navigated away from
// before its applied/pending/unsupported state was shown". The write this dialog PERFORMS had
// none of it. Census of its own in-flight state: `scheduleMutation.isPending` renders at 2
// sites, both on the submit button (its `disabled` term and its "יוצר..." label) -- 0 prose arms
// and 0 closed exits, against 5 `publishInFlight` sites (Escape, submit, skip) plus the banner
// sentence that TELLS the operator the screen is held.
//
// Leaving is not a cancellation, measured rather than assumed: services/api.js passes no
// `signal`, no `AbortController`, no `timeout` and no `CancelToken` (0 each, in an owned file);
// @tanstack/query-core 5.100.14's `Mutation.removeObserver` only drops the observer and
// schedules gc while the `#retryer` keeps running, and `execute` awaits `this.options.onSuccess`
// regardless of observers; and routers/scheduled_checks.py::create_check holds its single
// `await db.commit()` BEFORE it returns (pinned since pass 35 -- unlike the publish and the
// create, which both rely on get_db committing after the route returns). So pressing the button
// named SKIP_LABEL in this window creates the check anyway.
//
// A failure in that window is reported nowhere: this mutation declares no `onError`, index.js's
// QueryClient carries no `MutationCache` config and no mutation defaults, api.js raises no
// notification, and all three of this dialog's failure renderings (the generic sentence,
// `schedule-unconfirmed-notice`, `schedule-session-ended-notice`) exist at 1 site each, inside a
// dialog the exit unmounts. Fixture-shape census: 11 `scheduledChecksApi.create` fixtures (5
// resolved, 6 rejected) and 0 pending, against 2 pending `agentsApi.create` fixtures -- so no
// test had ever been in this state.
//
// Closed by labelling, with both exits left open (pass 39's rule: closing them traps the
// operator, here for as long as nginx's 60s proxy read timeout). The third clause names where
// the answer does arrive, because both exits navigate to the employee page, whose
// ScheduledChecksSection polls `refetchInterval: 30000` -- cited from that file and pinned
// against it in the other language, never guessed. For a granted-`agents_manage` non-admin that
// section renders its own 403 arm rather than an empty list, and `create_check` refuses that
// actor first, so the pointer is honest in both roles.
export const SCHEDULE_CHECKS_REFRESH_SECONDS = 30;
export const SCHEDULE_INFLIGHT_NO_CANCEL =
  `הבקשה נשלחה לשרת. יציאה מהמסך כעת — ב„${SKIP_LABEL}” או במקש Escape — אינה מבטלת אותה: ` +
  "אין ביטול בצד הלקוח, והשרת כותב את הבדיקה לפני שהוא משיב, ולכן הבדיקה עשויה להיווצר גם אם תעזוב.";
export const SCHEDULE_INFLIGHT_ONLY_HERE =
  "הודעת הכשל, אם היצירה תיכשל, מוצגת רק במסך הזה — אם תעזוב לפני שתתקבל תשובה, לא יוצג דיווח על הכשל בשום מסך.";
export const SCHEDULE_INFLIGHT_WHERE =
  `הבדיקה — אם נוצרה — תופיע ברשימת „בדיקות מתוזמנות” בדף העובד, שמתרעננת מעצמה כל ${SCHEDULE_CHECKS_REFRESH_SECONDS} שניות.`;
export const SCHEDULE_INFLIGHT_NOTICE = [
  SCHEDULE_INFLIGHT_NO_CANCEL,
  SCHEDULE_INFLIGHT_ONLY_HERE,
  SCHEDULE_INFLIGHT_WHERE,
].filter(Boolean).join(" ");

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 42): the UNFROZEN DRAFT. Pass 41 ran one
// census -- "for each mutation a component holds, list which of its controls are gated on THAT
// mutation's pending state, and compare across the component's mutations" -- on ScheduleDialog's
// two writes. The PAGE also holds two, and the same census on them is the inverse finding:
// `publishInFlight` renders at 5 code sites and closes THREE controls (submit, skip, Escape) plus
// the pending banner, for the write this page only reports, while `createMutation.isPending`
// renders at exactly 2 sites -- both on the submit button, its `disabled` term and its "יוצר..."
// label -- for the write this page PERFORMS. 0 prose arms and 0 other closed controls, against a
// control of 8 SCHEDULE_INFLIGHT_* / 2 PUBLISH_UNCONFIRMED_NOTICE / 2 CREATE_UNCONFIRMED_NOTICE.
//
// What that window costs is not the attempt (pass 21 measured the verdict's attempt axis and
// rightly called in-flight honest: nothing is decided, and the button says so). It is the
// operator's requested INTENT, because `handleNext` snapshots it -- `const intent = {...}` occurs
// once, is closed over by the onSuccess callback, reaches setPublishIntent once, and
// PublishOutcomeSection builds one row per item of THAT object. The draft that composes it stays
// fully editable for the whole duration: the back button's only `disabled` term is `step === 1`,
// PreviewSection's edit button has NO disabled term and the preview carries 7 editStep links, and
// `form.requested_skill_domains` has 4 live writers in Step3Skills alone. So a domain added in
// that window is in the draft, promised by the draft's own surfaces ("נרשם כרשומת מועמדת ברמה 0"),
// and reaches no publisher at all: not the create payload, not the snapshot, not a row in the
// panel -- not even `pending`, which is the one state this task's acceptance says must be
// distinguishable. The retry cannot recover it either (`publish.mutate(publish.variables)` replays
// the snapshot verbatim), and with nothing requested AT submit `hasIntent` is false, so the panel
// does not render and the dropped item is never mentioned on any screen.
//
// Reachability is not a millisecond race. `_assert_system_name_unused` takes
// `advisory_xact_lock` for each of the three reserved platform names -- which is the name
// AgentsPage's own placeholder suggests ("לפיילוט: AI Technician Pilot", pass 24) -- and that lock
// blocks indefinitely behind a concurrent create or auto-create of the same name, bounded only by
// nginx's 60s `proxy_read_timeout` default on /api/ (pass 26). api.js carries 0
// `signal`/`AbortController`/`CancelToken`/`timeout` against a control of 81 `api.post(`
// (pass 41's figure, re-measured), so there is no client-side cancel to offer.
//
// Closed by labelling only, in both directions and on both halves. No `disabled` term is added:
// holding the operator on a screen for up to 60s is what pass 41 declined to do for the same
// reason, and a clamp would lose the text rather than disclose it (pass 30).
export const CREATE_INFLIGHT_NO_CANCEL =
  "הבקשה נשלחה לשרת. אין ביטול בצד הלקוח, והשרת מקבל את הטיוטה כפי שהייתה ברגע הלחיצה.";
export const CREATE_INFLIGHT_INTENT_FROZEN =
  "מיומנות או מערכת שתתווסף לטיוטה מכאן והלאה אינה חלק מהבקשה שנשלחה: היא לא תפורסם ולא תקבל שורה בדוח ההחלה.";
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 59), the ONE-WAY FREEZE. The clause
// above names ONE direction of a two-direction axis, and it is the direction in which the
// freeze costs nothing. Un-selecting is live in the same window and on the same controls: the
// two steps that COLLECT this intent hold none of the page's create state (measured over both
// regions against their own form./setForm controls), the back exit's only gating term is the
// first step, and the preview's skills section carries an edit link to the skills step whose
// button has no gating term at all — so that step is ONE click away while the request hangs,
// bounded only by nginx's 60s default, since api.js sets no timeout and takes no abort signal.
//
// What the omitted direction costs is the mirror of the clause above: the item stays in the
// snapshot and IS published, so an operator who un-checks a governed domain in order to stop
// it gets the record anyway. Reversal paths measured per half rather than assumed (pass 45),
// because they differ — a connection record is removable (the owned router declares
// DELETE /{agent_id}/connections/{connection_id} and the remove call has exactly 1 product
// consumer, the employee page, which is where both of this dialog's exits land), while the
// skill half has no removal path at four layers: SkillRegistry exposes 10 public methods and
// none of them removes a record, the registry and assignment-state modules hold 0
// unregister/remove/delete/revoke sites, the skills router is two GETs, and the skills client
// is 2 GETs with 0 writers.
//
// Bounded rather than inflated: the published record grants nothing (its grants flag is a
// false Literal, level 0, status candidate, credential and customer columns NULL, all pinned
// at both ends) and pass 51 measured that it can never leave level 0 — so the harm is a
// permanent, non-granting record the operator actively tried to prevent, never an authority
// grant. Closed by labelling only: no control gains a gating term, because holding the
// operator for as long as the request can hang is what passes 41/42 declined for this reason.
//
// Tensed for the arm it renders on. This notice renders only while the create is in flight,
// where there may be no employee row and may never be one, so the publication half is
// CONDITIONAL on the creation rather than asserted — pass 57 committed exactly that mistake
// in the pass that named it, and only reading the rendered arm aloud finds it.
export const CREATE_INFLIGHT_INTENT_WITHDRAWN =
  "מיומנות או מערכת שתוסר מהטיוטה מכאן והלאה נשארת בבקשה שנשלחה, ואם העובד ייווצר היא תפורסם ותקבל שורה בדוח ההחלה.";
// Per half, because the remedy is not the same on both: agentConnectionsApi.create has exactly 1
// product consumer (AgentDetailPage, the employee's own page), while skillsApi is 2 GETs and 0
// writers and agentsApi.builderPublish has 1 product consumer -- this wizard -- which replays only
// what was sent. So a system is recoverable later and a skill request is not.
//
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 61), the UNREACHABLE ERRAND. Pass 60 ran
// pass 45's reversal-path census on the two WITHDRAWN clauses and armed them on whether the
// publish creates a record for the item's CLASS. The handover named this constant first in the
// population it opened, and it is the same axis on the one disclosure that offers an ACTION
// rather than describing a record -- and the clause above justifies the system half by counting
// agentConnectionsApi.create's product CALL SITES. A call-site census proves the action EXISTS;
// it says nothing about which class of subject the action ADMITS. Measured here, the system half
// was false for 2 of the 3 ids ENV_TOOLS offers, at three independent layers:
//
//   - the type layer: AgentConnectionCreate.system_type is the AgentSystemType enum, whose
//     members are exactly m365_graph / eset_protect / acronis_backup. Neither openclaw nor
//     teamviewer is a member, and neither connector definition declares a `system_type` at all
//     (both are agent_connection_backed=False, scope=PLATFORM), so POST
//     /api/agents/{id}/connections is refused on the body before the route runs.
//   - the surface layer: AgentDetailPage:1105 builds the connection form's option list as
//     `connectorTypes?.filter((t) => t.agent_connection_backed)`, so neither id is offered on
//     the page this sentence sends the operator to.
//   - the semantic layer: the publish creates nothing for them either -- SYSTEM_PUBLISH_SCOPE
//     maps both to `platform_managed`, whose resolver detail is "one deployment-wide
//     configuration, not a per-employee connection; nothing to create" -- so the freeze cost
//     the operator no connection record, and the honest statement is that there is nothing to
//     add rather than that it can be added later.
//
// The reassuring half is the one that was false (pass 50), and it was false in both directions
// at once: it sent the operator on an errand that cannot be completed, AND it implied a loss
// that did not occur. The authority was already in the file at exactly the granularity pass 60
// used -- SYSTEM_PUBLISH_SCOPE, pinned per id against resolve_environment_tool_key -- and the
// `supported` label is interpolated rather than retyped so the words before the click and the
// badge at the systems step cannot drift (pass 19).
//
// Not filtered per item, qualified instead, and the reason is the arm this renders on: the
// notice is a static constant shown while the create is in flight, and the item it is about is
// one the operator has not added yet, so there is no id to key on. Both class clauses therefore
// render together; each is decided by one property of the subject (agent_connection_backed,
// which is the same property resolve_environment_tool_key reads), so neither clause's decider
// is outside its own condition (pass 53).
//
// The SKILL half is left byte-identical and is TRUE for every class: no skill request has a
// later publisher whether it is governed or not (skillsApi 2 GETs / 0 writers, SkillRegistry
// exposes no removal or external add path, builderPublish replays only the snapshot). For an
// UNGOVERNED domain nothing was lost either, so denying a remedy there is imprecise in the
// alarming direction, which pass 27's rule makes the safe one for a freeze warning -- the same
// reasoning pass 60 recorded for CREATE_INFLIGHT_INTENT_WITHDRAWN, which also stays
// byte-identical here. Stated so pass 62 does not re-chase either.
export const CREATE_INFLIGHT_REMEDY_SYSTEM_RECORDED =
  `מערכת שעבורה ${SYSTEM_PUBLISH_SCOPE_META.supported.label} אפשר להוסיף אחר כך מדף העובד שייווצר;`;
export const CREATE_INFLIGHT_REMEDY_SYSTEM_NO_RECORD =
  "מערכת מנוהלת פלטפורמית אין מה להוסיף שם: הפרסום עצמו אינו יוצר לה רשומת חיבור פר-עובד, והיא אינה מוצעת כסוג חיבור בדף העובד;";
export const CREATE_INFLIGHT_REMEDY_SKILL =
  "בקשת מיומנות אין לאן להוסיף — האשף הזה הוא המפרסם היחיד שלה בממשק, והוא מפרסם רק את מה שנשלח.";
export const CREATE_INFLIGHT_REMEDY = [
  CREATE_INFLIGHT_REMEDY_SYSTEM_RECORDED,
  CREATE_INFLIGHT_REMEDY_SYSTEM_NO_RECORD,
  CREATE_INFLIGHT_REMEDY_SKILL,
].join(" ");
export const CREATE_INFLIGHT_NOTICE = [
  CREATE_INFLIGHT_NO_CANCEL,
  CREATE_INFLIGHT_INTENT_FROZEN,
  CREATE_INFLIGHT_INTENT_WITHDRAWN,
  CREATE_INFLIGHT_REMEDY,
].filter(Boolean).join(" ");

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 57): the FROZEN-DRAFT PROMISE. Pass 42 ran
// the census "for each mutation a component holds, list which of its controls are gated on THAT
// mutation's pending state" on this page and closed the window for the INTENT snapshot. The
// population nobody ran is the complement: the create payload. `handleNext` composes it once at
// the click and hands it to `createMutation.mutate`, and the request carries 15 fields; the
// snapshot pass 42 reconciles carries 2, and they are not payload fields at all. So the payload's
// 15 fields have 0 drift arms, while `AgentResponse` carries 14 of them (every one but
// `system_prompt`) and `createdAgent` is read for exactly 3 of its 22 fields (`id`, `is_enabled`,
// `name`).
//
// Measured, the gap is structural rather than a wording slip: the seven collecting steps receive
// NONE of the create's state. `createMutation` / `createdAgent` / `isPending` / `createFailed` /
// `createUnconfirmed` / `publishInFlight` each occur 0 times inside all seven regions, against
// in-region controls of 34 `form.` and 26 `setForm` - so no step can qualify its own promise even
// where it makes one. Creation-tense census over those regions, comment-stripped: 8 occurrences
// on 5 lines in 4 steps. Three of the five are still true post-submit or are covered elsewhere
// (Step3Skills' governed-domain chip is exactly what CREATE_INFLIGHT_INTENT_FROZEN and the drift
// notice already report; Step5Systems' sentence is about live connection testing, not a draft
// value). The two that are not:
//
//   1. Step6Limits' own subtitle said, unconditionally, that the values on screen are
//      "המגבלות שיירשמו ביצירה" - the limits that WILL BE RECORDED at creation. It is the only
//      sentence in the wizard asserting that, and it sits on the one step whose single click
//      rewrites two authority fields at once (the tier card writes `permission_tier` and clears
//      the restricted tool list, pass 52). One back-click from the preview reaches it: the back
//      button's only `disabled` term is `step === 1`, the tier card's only one is `tier.disabled`
//      (tier_3), and "next" is the only control pass 42's census found gated. So submit at
//      tier_2, click back once, click "קריאה בלבד", and the employee is created tier_2 (26 tools,
//      11 of them state-changing, pass 31) under a screen showing tier_1 pressed and its own
//      sentence promising that is what gets recorded. The operator's least-privilege click is the
//      one that triggers it, which is pass 20's dangerous direction.
//   2. StepIdentity's two bound alerts state a determinate server outcome ("השרת ידחה את הבקשה
//      והעובד לא ייווצר" / "לא יישלחו ולא יישמרו") about the CURRENT draft. A violation typed
//      into the box after the click is not in the sent request, so the alarming sentence is false
//      and the dialog on top says the employee was created.
//
// Reachability is not a race, for the same reason pass 42 published: `_assert_system_name_unused`
// takes `advisory_xact_lock` per reserved platform name - the name AgentsPage's own placeholder
// suggests - and that blocks indefinitely, bounded only by nginx's 60s default, since the `/api/`
// block in nginx/nginx.conf declares no `proxy_read_timeout` (re-measured: 0 occurrences under
// nginx/). api.js carries no abort signal or timeout, so there is no cancel to offer.
//
// Closed by labelling only. No `disabled` term is added (pass 41/42's decision: holding the
// operator for up to 60s is worse), no readiness row moves and no verdict arm reads any of this.
// The sent values come from `createMutation.variables`, which is the exact payload and is the one
// authority that also covers `system_prompt`; the tier label is INTERPOLATED out of
// PERMISSION_TIER_OPTIONS so the sentence cannot drift from the card it describes.
//
// Declared here, beside the family it belongs to, and read only during render - so these consts
// are initialised by the time Step6Limits and StepIdentity (declared above) evaluate them, the
// same rule rolePresetDraftOverwrites documents for EMPTY_FORM.
export const LIMITS_SUBTITLE_DRAFT =
  "הגדר את רמת ההרשאה, רשימת הכלים ומדיניות האישור — אלה המגבלות שיירשמו ביצירה";
// The same sentence with its one false clause replaced rather than qualified: a reader who stops
// after the first half must not be left with the promise (pass 47).
export const LIMITS_SUBTITLE_SUBMITTED =
  "הגדר את רמת ההרשאה, רשימת הכלים ומדיניות האישור — אלה המגבלות שבטיוטה, ולא אלה שנשלחו בבקשה";

export const LIMITS_SUBMITTED_OPENING =
  "הבקשה נשלחה לשרת עם המגבלות שהיו בטיוטה ברגע הלחיצה.";
export const LIMITS_SUBMITTED_SENT_UNRESTRICTED = "גישה לכל הכלים שהרמה מתירה";
export const limitsSubmittedSentRestricted = (count) =>
  `רשימת כלים מצומצמת — ${count} כלים מסומנים`;
export const limitsSubmittedSentClause = (tier, tools) =>
  `נשלחו: רמת הרשאה ${tier}; ${tools}.`;
export const LIMITS_SUBMITTED_FROZEN =
  "שינוי שייעשה כאן מכאן והלאה אינו חלק מהבקשה שנשלחה, ולכן אינו משפיע על העובד.";
// The remedy exists and is where both dialog exits land: AgentUpdate carries `permission_tier`
// and `allowed_tools`, and AgentDetailPage's edit modal offers both (a tier select and the same
// per-tool selector). Measured before the sentence was written, per pass 50.
export const LIMITS_SUBMITTED_REMEDY =
  "אפשר לשנות את רמת ההרשאה ואת רשימת הכלים אחר כך מדף העובד.";

// Every sentence here is tense-neutral across BOTH arms this notice renders on, which is the
// correction reading them aloud produced: `draftSubmitted` is true while the request is still
// in flight, and a first draft said the limits "recorded on the employee row" ARE the draft's
// at the click - presupposing a durable row over a create that may still be refused, which is
// this task's own defect class (section 32) in new prose. Verify each sentence against its own
// arm's state, not against the feature (section 43).
//
// The determinate clause is omitted, never guessed, when the sent payload is not in hand: the
// three sentences around it are true without it, so the notice states less rather than something
// false. That arm is unreachable from this page (the payload is `mutate`'s own argument and this
// notice renders only once `mutate` has been called); it exists for a contract-shape change in
// the query library, which is the class pass 2 recorded.
export function limitsSubmittedNotice(sentPayload) {
  const sent = sentPayload || null;
  const tier = sent
    ? PERMISSION_TIER_OPTIONS.find((option) => option.value === sent.permission_tier)
    : null;
  const tools = !sent
    ? ""
    : isUnrestrictedAllowlist(sent.allowed_tools)
    ? LIMITS_SUBMITTED_SENT_UNRESTRICTED
    : limitsSubmittedSentRestricted(String(sent.allowed_tools.length));
  return [
    LIMITS_SUBMITTED_OPENING,
    tier ? limitsSubmittedSentClause(tier.label, tools) : "",
    LIMITS_SUBMITTED_FROZEN,
    LIMITS_SUBMITTED_REMEDY,
  ].filter(Boolean).join(" ");
}

// One clause for both identity bound alerts. Pinned on the constant rather than on either render,
// because an assertion against a rendered paragraph is satisfied by its sibling clause (pass 46).
export const IDENTITY_BOUNDS_SUBMITTED_CLAUSE =
  "הבקשה כבר נשלחה, והמגבלות חלות על הטיוטה כפי שהייתה ברגע הלחיצה — חריגה שנוספת כאן מכאן והלאה אינה חלק מהבקשה שנשלחה ואינה משנה את תוצאת היצירה.";

// The after-the-fact half: once the employee exists, the two sets are both knowable from state the
// dialog already holds, so the drop is named per ITEM rather than described in general. Skills are
// compared by the SERVER's identity (canonicalSkillDomainLabel is `_canonical_domain_label`'s fold:
// NFKC, lower, whitespace-collapse) -- pass 23's lesson, because `FortiGate` typed after submit
// beside `fortigate` sent at submit is ONE domain to the publish and warning about it would be this
// defect sign-flipped. Systems are compared by exact id, which is all ENV_TOOLS can produce.
export function requestedIntentDrift(draft, published) {
  const draftSkills = (draft && draft.requested_skill_domains) || [];
  const pubSkills = (published && published.requested_skill_domains) || [];
  const draftSystems = (draft && draft.requested_environment_tools) || [];
  const pubSystems = (published && published.requested_environment_tools) || [];
  const foldedDraft = new Set(draftSkills.map(canonicalSkillDomainLabel));
  const foldedPub = new Set(pubSkills.map(canonicalSkillDomainLabel));
  return {
    addedSkills: draftSkills.filter((d) => !foldedPub.has(canonicalSkillDomainLabel(d))),
    withdrawnSkills: pubSkills.filter((d) => !foldedDraft.has(canonicalSkillDomainLabel(d))),
    addedSystems: draftSystems.filter((id) => !pubSystems.includes(id)),
    withdrawnSystems: pubSystems.filter((id) => !draftSystems.includes(id)),
  };
}

const envToolLabel = (id) => {
  const found = ENV_TOOLS.find((tool) => tool.id === id);
  return found ? found.label : id;
};

// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 58), the UNCONDITIONAL APPLICATION.
// Pass 42 built this notice for the create's in-flight window and gave its opening ONE arm:
// it asserts that the request that was sent is the one that WAS APPLIED. The notice renders in
// three states on that axis and the assertion is true in one of them. Arm count before this
// pass: 0, and the tell is structural in exactly pass 57's shape -- createIntentDriftNotice
// took (draft, published) and received NOTHING of the publish, so it could not qualify its own
// claim, against a control of 15 publish reads inside PublishOutcomeSection.
//
//   - a 200 is in hand: true, and the per-item outcome is in the panel below. Byte-identical.
//   - the publish was issued and has not successfully settled (in flight, or any of the four
//     error arms): nothing is applied yet or nothing was applied, and the panel's rows carry
//     the per-item truth. The old sentence sat two lines above the panel's own determinate
//     banner -- the generic fallback arm of `publish-error-reason`, which states that the
//     requests below have not yet been applied -- and contradicted it. Described rather than
//     quoted: that sentence is asserted by an exact count in the backend suite, and a product
//     comment repeating it reads one high (pass 30's collision, pass 38's rule -- reword the
//     comment, never the pin).
//   - no publish was issued at all (`hasIntent` false, so PublishOutcomeSection does not
//     render): nothing was applied and there is no report to defer to. This is the shape pass
//     42 put the notice OUTSIDE the panel FOR -- its own test asserts
//     `expect(agentsApi.builderPublish).not.toHaveBeenCalled()` -- so the one state the notice
//     exists to cover was the state its opening was most plainly false in.
//
// Fixture-shape census (pass 34's method): all 6 page-level `builderPublish` fixtures in the
// owned publish suite are `mockResolvedValue` and 0 are rejected or deferred, and `draftIntent`
// is passed by 0 direct renders -- so the (drift x publish-not-applied) combination was
// reachable by no test in the repository. A string census cannot see this; only a per-state
// fixture can (pass 9).
export const CREATE_DRIFT_LEAD =
  "הטיוטה שונתה אחרי שהבקשה נשלחה,";
// The applied arm, kept as its own constant so the recomposition below is provably byte-equal
// to the single literal pass 42 shipped (pass 55: split byte-neutrally and prove it by
// EQUALITY against the text the prior pass pinned, never against the product).
export const CREATE_DRIFT_APPLIED_CLAUSE =
  "והבקשה שנשלחה היא זו שהוחלה.";
export const CREATE_DRIFT_OPENING = `${CREATE_DRIFT_LEAD} ${CREATE_DRIFT_APPLIED_CLAUSE}`;
// Issued, not successfully settled. States which set governs and defers the application
// itself to the rows, which since passes 26/38 carry pending / failed / unconfirmed /
// refused_unverified per surface -- the deferral shape pass 38 used for the banner, rather
// than a second copy of the rows' own reasoning here.
export const CREATE_DRIFT_SUBMITTED_CLAUSE =
  "והבקשה שנשלחה היא הקובעת — מה הוחל בפועל מדווח בדוח ההחלה שלהלן, לכל בקשה.";
// No publish request was issued, so this arm has no panel to defer to and has to be
// self-contained. Determinate and TRUE: `publishIntentAfterCreate` calls `publishMutation`
// only when the snapshot carries an item, so `issued` below is exactly that call's condition.
export const CREATE_DRIFT_NOT_PUBLISHED_CLAUSE =
  "והבקשה שנשלחה לא כללה בקשת מיומנות או מערכת, ולכן לא נשלחה בקשת פרסום, לא הוחל דבר, ואין דוח החלה במסך הזה.";
export const createDriftAddedClause = (items) =>
  `נוסף לטיוטה ולא נכלל בבקשה: ${items} — לא פורסם, אין לו שורה בדוח ההחלה, ולא הוחל על העובד.`;
export const createDriftWithdrawnClause = (items) =>
  `הוסר מהטיוטה אחרי השליחה אך נכלל בבקשה: ${items} — הוא נשלח לפרסום, והשורה שלו בדוח מדווחת את מצבו בפועל.`;
// Which of the three openings above this render earns. Keyed on `publish.data` -- the SAME
// predicate PublishOutcomeSection's own `settled` uses -- so the opening and the rows below it
// cannot disagree about whether a 200 is in hand; a second, different predicate beside that one
// is the drift this chain keeps finding. `isSuccess` is the honest predicate for a settled
// publish with a falsy body (pass 12), but that shape is unreachable through `response_model`
// and the 15 hand-built publish fixtures in the owned suite carry no `isSuccess` key at all, so
// keying on it would take a not-applied arm over a success fixture. `issued` is read off the
// SNAPSHOT rather than off a flag, because the snapshot IS what decides whether the publish was
// called, and it is already an argument here.
export function createDriftOpening(published, publish) {
  const sent = published || {};
  const issued =
    (sent.requested_skill_domains || []).length > 0 ||
    (sent.requested_environment_tools || []).length > 0;
  if (!issued) return `${CREATE_DRIFT_LEAD} ${CREATE_DRIFT_NOT_PUBLISHED_CLAUSE}`;
  if (publish && publish.data) return CREATE_DRIFT_OPENING;
  return `${CREATE_DRIFT_LEAD} ${CREATE_DRIFT_SUBMITTED_CLAUSE}`;
}
// `publish` defaults to null, which selects a NOT-applied arm: never the optimistic member of
// the pair (pass 2's closing lesson, applied when writing the default rather than a pass
// later). Every pre-existing two-argument call therefore keeps producing a true sentence.
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 59). The withdrawn clause above is
// complete and TRUE about what the un-select cost — pass 42 established that it is not a drop
// and that the row below is truthful, and its own test says so — and it has 0 arms on whether
// the operator can undo it. That is the census pass 45 established (for every disclosed
// consequence, census its reversal paths as their own population) and nobody ran on this
// clause; the inherited reason "it is NOT a drop" is what stopped anyone looking further.
//
// Stated per HALF, because the halves differ and one sentence for both would be false for one
// (pass 42's own rule, reused as an input): the connection record is removable from the
// employee page, where both of this dialog's exits land, and the skill record has no removal
// path in the product at all. Each clause's decider is in its own condition (pass 53), and
// each is true read ALONE (pass 47): the relative clause carries the conditional, so neither
// sentence claims that a record exists — which matters because this notice renders on every
// publish state, including the four error arms where nothing may have been written.
//
// Kept OUT of createDriftWithdrawnClause rather than welded into it: that constant is one
// item list for both halves, so a reversal sentence inside it could not be per-half, and
// pass 36/55 both had to split a constant that welded an arm-independent mechanism to an
// arm-dependent cause. Here the split is the design.
// Precise about ACTIONS, not about persistence: a storeless host loses the record on restart
// (pass 18/37 measured that durability is conditional on the state store), so "cannot be
// removed" would be false in the one direction the operator does not control. The claim is that
// no action removes it, and it names the two layers this pass measured.
export const WITHDRAWN_SKILL_IRREVERSIBLE =
  "לרשומת מיומנות שנרשמה אין מסלול הסרה — לא בממשק ולא בשירות.";
export const WITHDRAWN_CONNECTION_REVERSIBLE =
  "רשומת חיבור שנוצרה אפשר להסיר אחר כך מדף העובד.";
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 66), the UNREMOVED COPY. Pass 65 handed
// over the RESIDUE of an abandoned employee: the connection record is removable and the skill
// record is not, so removing the one by hand leaves the other and the Agent row behind. Run
// here, that half is a NEGATIVE result -- both component facts are already stated (the skill
// record's absent removal path at 4 sites, the employee row's at 2 since pass 65). The defect
// is one question over, and it is about what the one removal the platform permits actually
// LEAVES.
//
// The single creating action writes the operator's SYSTEM selection into TWO durable places,
// from one input, in one block of handleNext: the per-employee connection record for a
// `supported` id, and a line listing the selected systems that composeSystemPrompt appends to
// the operating instructions it sends (described rather than quoted -- that literal is asserted
// by an exact count in the backend suite over RAW source, and a comment copy reds it: pass
// 22's pin, pass 38's rule). Measured live rather than inherited:
//
//   * delete_agent_connection is 684 chars, holds one db.delete of the connection row, and 0
//     occurrences each of system_prompt and of the Agent model -- so the one removal the
//     product offers touches the connection row and nothing else.
//   * agents.system_prompt is read by 3 modules under the backend app; manager_agent passes it
//     to create_direct_llm_analyst, whose adapter PREPENDS it to the base safety contract on
//     every analysis run. That half was already armed at 2 sites (pass 28/34) -- for what the
//     line DOES, never for the fact that it is a second copy of the selection.
//   * the remedy exists and the destination is the one this screen already names: AgentUpdate
//     carries system_prompt, update_agent applies it, and the employee page's edit modal seeds
//     that field and sends it.
//
// Arms on the residue axis before this pass: 0. Absence sweep over this file for a sentence
// naming the operating instructions AND a removal: 0, against same-concept controls of 23
// code lines carrying the instructions word, 9 naming the employee page as a destination, 12
// naming what happens after creation and 10 naming the systems -- so the file has every word
// and had applied none of them here (pass 53's same-concept control, which is what makes the
// zero mean something).
//
// Class-INDEPENDENT, which is why it is a third member rather than a condition on either
// existing one. composeSystemPrompt maps over form.tools with no scope filter and all three
// ENV_TOOLS entries declare a promptLabel, so a `platform_managed` id reaches the prompt line
// exactly like a `supported` one -- while pass 60 correctly gated both RECORD sentences on the
// class, because a record is what the class decides. So the gate here is the withdrawn systems
// list itself, and pass 60's blanket assertion that no arm in this function may read that
// length is re-pointed per claim rather than deleted (pass 56): the two record sentences still
// may not, and this one must.
//
// Which half needed no arm, measured rather than assumed: the SKILL half has exactly one
// landing place. composeSystemPrompt reads form.tools and holds 0 occurrences of the requested
// skill domains, and the payload carries no skills field at all, so a withdrawn domain leaves
// nothing outside the registry record whose absent removal path is already stated.
//
// Every clause's decider is in its own condition (pass 53): the line certainly exists because
// this arm renders only when the SENT snapshot carried a system, and the snapshot and the
// prompt are composed from the same form.tools in the same block; the row certainly exists
// because this notice renders inside ScheduleDialog, which renders only once the create has
// returned a row; and the runtime and remedy clauses are static properties of the platform.
// The two phrases are SLICED out of the role row's own sentence rather than retyped, and the
// backend asserts the composed constant still contains both, so the two cannot drift.
//
// Declined, with the reason, so a later pass does not fill it: the durability notice's
// connection-created arm. Its subject is where the record LANDED, it offers no removal, and
// pass 64 measured that the missing removability statement there is a withheld REASSURANCE.
// A residue clause belongs where the removal is offered, which is here and nowhere else on
// this screen.
export const WITHDRAWN_SYSTEM_PROMPT_LINE_RESIDUE =
  "שורה אחת המונה את המערכות שנבחרו נשלחה גם להוראות ההפעלה של העובד, ואין רשומה שהסרתה מוציאה אותה משם: היא נשארת על רשומת העובד ומגיעה למודל לפני חוזה הבטיחות של המערכת בכל ריצת ניתוח, וכדי להסיר אותה יש לערוך את הוראות ההפעלה מדף העובד.";
// DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 60), the UNGUARDED EXISTENCE CLAIM --
// pass 27's finding re-committed one notice over. Each sentence above is the reversal path
// for the item named in the clause it follows, and each presupposes that the publish created
// a record for that item. Neither was armed on whether it does, and for BOTH halves a
// reachable withdrawn value creates nothing at all:
//
//   - a system: of the three ids ENV_TOOLS offers, TWO (openclaw, teamviewer) resolve
//     `platform_managed`, and the resolver's own detail for that state is that there is no
//     connection record to create (measured here against the real registry, not inherited).
//     So the removal remedy was offered for a record that does not exist, and the operator
//     who goes to the employee page to remove it finds nothing there.
//   - a skill: the skills step takes FREE TEXT, so a domain outside the governed vocabulary
//     is reachable, and the publish answers `unsupported` before anything is constructed or
//     registered. So an operator who un-checked one was told the registered record is
//     permanent, about a record that was never created and never could be -- a false alarm
//     asserting an irreversible consequence that did not occur.
//
// The relative clause in each sentence carries the did-the-write-LAND conditional, which is
// pass 26's axis and is why both are safe on the four error arms. Neither carries the
// does-this-item's-CLASS-create-a-record axis, which is pass 21's, is decided before the
// request is sent, and is already computed in this file twice over: `isGovernedDomain`
// (folded through the server's own fold) and `SYSTEM_PUBLISH_SCOPE` (pinned per id against
// resolve_environment_tool_key). The authority was already in the tree, and so was the
// proof: test_unsupported_and_platform_managed_intent_create_nothing publishes an ungoverned
// domain beside openclaw and asserts that neither leaves a record.
//
// Nothing new is said for the complement, deliberately rather than as a second gap: the
// withdrawn item clause directly above already defers to the row, and that row carries the
// `platform_managed` or `unsupported` label, each of which states the no-record outcome in
// the panel's own vocabulary. Deferring to the rows is pass 38's own prescription, and it
// keeps this correction at zero new operator-visible strings.
//
// An unclassified system id falls through BOTH arms rather than into the reversible one: it
// is the rolling-deploy path SYSTEM_PUBLISH_SCOPE_META's `unknown` entry exists for, nothing
// determinate is known about it, and pass 26 refused a determinate claim on an indeterminate
// outcome.
export function createDriftWithdrawnReversalClause(drift) {
  const recordedSkills = drift.withdrawnSkills.filter(isGovernedDomain);
  const recordedSystems = drift.withdrawnSystems.filter(
    (id) => SYSTEM_PUBLISH_SCOPE[id] === "supported",
  );
  return [
    recordedSkills.length > 0 ? WITHDRAWN_SKILL_IRREVERSIBLE : "",
    recordedSystems.length > 0 ? WITHDRAWN_CONNECTION_REVERSIBLE : "",
    drift.withdrawnSystems.length > 0 ? WITHDRAWN_SYSTEM_PROMPT_LINE_RESIDUE : "",
  ].filter(Boolean).join(" ");
}
export function createIntentDriftNotice(draft, published, publish = null) {
  const drift = requestedIntentDrift(draft, published);
  const added = [...drift.addedSkills, ...drift.addedSystems.map(envToolLabel)];
  const withdrawn = [...drift.withdrawnSkills, ...drift.withdrawnSystems.map(envToolLabel)];
  if (added.length === 0 && withdrawn.length === 0) return "";
  return [
    createDriftOpening(published, publish),
    added.length > 0 ? createDriftAddedClause(added.join(", ")) : "",
    withdrawn.length > 0 ? createDriftWithdrawnClause(withdrawn.join(", ")) : "",
    createDriftWithdrawnReversalClause(drift),
  ].filter(Boolean).join(" ");
}

function sessionEndedNotice(tail) {
  return [SESSION_ENDED_OPENING, SESSION_ENDED_NOTHING_WRITTEN, tail].filter(Boolean).join(" ");
}

// A state the row-state function cannot name is NOT "not yet applied": `rowState` only ever
// returns a key these maps hold, so the fallback below fires on exactly one thing — an
// `outcome.state` this bundle does not recognize (the backend Literal gained a state, or a
// rolling deploy left this bundle older than the API). Falling back to `pending` there would
// re-open the same operator lie this task closed: a governed verdict read as still-coming.
// One outcome per requested item, aligned to the request. The echoed `requested` key matches
// first (unambiguous, and order-independent for a well-behaved response); any row the key
// cannot resolve takes the outcome at its own position, but only when the response carries
// exactly one outcome per request, and only if that outcome was not already claimed by a key
// match. An outcome is never reused and never guessed at: a row that neither rule resolves
// stays null, and null is reported as a non-success state, never as applied.
export function alignPublishOutcomes(requested, outcomes) {
  const delivered = Array.isArray(outcomes) ? outcomes : [];
  const byKey = new Map();
  delivered.forEach((outcome) => {
    if (!byKey.has(outcome?.requested)) byKey.set(outcome?.requested, outcome);
  });

  const claimed = new Set();
  const aligned = requested.map((key) => {
    const hit = byKey.get(key);
    if (hit && !claimed.has(hit)) {
      claimed.add(hit);
      return hit;
    }
    return null;
  });
  if (delivered.length === requested.length) {
    aligned.forEach((outcome, index) => {
      if (outcome === null && !claimed.has(delivered[index])) {
        aligned[index] = delivered[index];
        claimed.add(delivered[index]);
      }
    });
  }
  return aligned;
}

// PROD-BUILDER-060: the post-publish canonical read-back. The outcome rows above state what the
// publish REPORTED; this section asks the employee's own canonical read routes what records
// actually exist NOW, and renders the answer even when it contradicts the publish. A failed or
// forbidden read is an explicit unknown, never a success. The two routes are read-only and gated
// agents_view; nothing here writes, resolves a credential or grants authority.
function CanonicalPublishState({ agentId, intent, publish, skillOutcomes, connectionOutcomes }) {
  const requestedSkills = intent?.requested_skill_domains || [];
  const requestedSystems = intent?.requested_environment_tools || [];
  const settled = Boolean(publish && !publish.isPending && (publish.data || publish.isError));
  const skillsQuery = useQuery({
    queryKey: ["builder-canonical-skills", agentId],
    queryFn: () => agentsApi.skillAssignmentState(agentId),
    enabled: Boolean(settled && agentId && requestedSkills.length > 0),
    retry: false,
    staleTime: 0,
  });
  const connectionsQuery = useQuery({
    queryKey: ["builder-canonical-connections", agentId],
    queryFn: () => agentsApi.connectionReadiness(agentId),
    enabled: Boolean(settled && agentId && requestedSystems.length > 0),
    retry: false,
    staleTime: 0,
  });

  if (!settled || (requestedSkills.length === 0 && requestedSystems.length === 0)) return null;

  const unreadable = "לא ניתן לאמת מול השרת — קריאת המצב הקנוני נכשלה או אינה מורשית, ולכן המצב לא ידוע.";

  return (
    <div
      data-testid="publish-canonical-state"
      dir="rtl"
      className="rounded-lg border border-gray-200 bg-white p-2 space-y-1.5"
    >
      <p className="text-[11px] font-medium text-gray-700">המצב הקנוני של העובד — נקרא מהשרת לאחר הפרסום</p>
      <p className="text-[10px] text-gray-500">
        הנתונים שלהלן נקראו מעמוד המיומנויות והחיבורים הקנוני של העובד, ולא הוסקו מהצלחת הפרסום.
        אי-התאמה בין הדיווח לשורה כאן היא מידע, לא תקלה להסתרה.
      </p>

      {requestedSkills.map((domain, index) => {
        const record = canonicalSkillRecord(skillsQuery.data?.skills, domain);
        // One text node per line, and the domain never stands alone: the row above already
        // owns that exact token, and a second bare copy would make every operator-facing row
        // selector ambiguous. The line is also read as one RTL sentence around the LTR id.
        const stateText = skillsQuery.isPending
          ? "בודק במרשם המיומנויות…"
          : skillsQuery.isError
          ? unreadable
          : record
          ? `נמצאה רשומת מיומנות קנונית: סטטוס ${record.status || "לא דווח"}, רמה ${record.current_level}, מזהה ${record.skill_id}`
          : "לא נמצאה רשומת מיומנות קנונית לעובד.";
        return (
          <p key={domain} data-testid={`publish-canonical-skill-${index}`} className="text-[11px] text-gray-600" dir="rtl">
            {`מצב קנוני — ${domain}: ${stateText}`}
          </p>
        );
      })}

      {requestedSystems.map((key, index) => {
        const outcome = connectionOutcomes[index];
        const expected = expectedSystemType(key, outcome);
        const record = canonicalConnectionRecord(connectionsQuery.data?.connections, expected);
        const tool = ENV_TOOLS.find((t) => t.id === key);
        const reportedNoRecord =
          outcome?.state === "platform_managed" ||
          outcome?.state === "unsupported" ||
          outcome?.state === "refused";
        const label = tool ? `${tool.icon} ${tool.label}` : key;
        const stateText = connectionsQuery.isPending
          ? "בודק ברשימת החיבורים…"
          : connectionsQuery.isError
          ? unreadable
          : reportedNoRecord
          ? "לא נוצרה רשומת חיבור לעובד — כפי שדווח לעיל."
          : !expected
          ? "לא ידוע איזה סוג חיבור צפוי — לא ניתן לאמת מול השרת."
          : record
          ? `נמצאה רשומת חיבור קנונית (${record.system_type}); ${
              record.configuration_complete
                ? "ההגדרה הושלמה"
                : `ההגדרה חסרה: ${(record.missing_required_fields || []).join(", ") || "שדות נדרשים"}`
            }.`
          : "לא נמצאה רשומת חיבור קנונית — ייתכן שהפרסום לא הוחל.";
        return (
          <p key={key} data-testid={`publish-canonical-system-${index}`} className="text-[11px] text-gray-600" dir="rtl">
            {`מצב קנוני — ${label}: ${stateText}`}
          </p>
        );
      })}
    </div>
  );
}

function PublishOutcomeSection({ agentId, intent, publish }) {
  const requestedSkills = intent.requested_skill_domains || [];
  const requestedSystems = intent.requested_environment_tools || [];
  const skillOutcomes = alignPublishOutcomes(requestedSkills, publish.data?.skills);
  const connectionOutcomes = alignPublishOutcomes(requestedSystems, publish.data?.connections);

  // A row state without a delivered outcome is never success: it is `failed` when the
  // APPLICATION answered with an error (so nothing was applied), `unconfirmed` when the request
  // errored without the application ever answering (so whether it was applied is not knowable
  // here), `unreported` when a response did arrive but carried no outcome this row could be
  // aligned to (nothing was applied, and nothing is still coming), and `pending` only while the
  // publish is genuinely still in flight.
  //
  // Pass 26: `failed` used to be the single arm for every `isError`, and its label asserts a
  // determinate negative ("לא הוחל" / "לא נוצר חיבור") that the client cannot always know. The
  // publish commits in app.database.get_db AFTER the route returns, and the commit is never
  // conditional on the client receiving the response — so a transport failure or a gateway
  // status sits over a possibly-durable AgentConnection row and a registered candidate Skill
  // record. Only an answer from the application itself carries the rollback guarantee: every
  // error path out of builder_publish_v3 leaves get_db's `except` arm, which rolls the
  // transaction back (pinned in backend/tests/test_employee_builder_v3.py).
  const answered = publishApplicationAnswered(publish.error);
  // Mutually exclusive with `!answered` by construction (401/403 are not gateway statuses) and
  // asserted so: a future gateway-list change cannot make both notices render at once.
  const sessionEnded = publish.isError && requestEndedTheSession(publish.error);
  const settled = Boolean(publish.data);
  // Pass 38, the ROLLBACK-EXEMPT HALF. `answered ? "failed" : "unconfirmed"` was ONE arm for
  // both surfaces, and the determinate arm's only authority is get_db's rollback — the owned
  // backend pin says exactly that ("the wizard's determinate 'nothing was applied' wording on
  // an answered error has no other authority"). The skill half is not in that transaction.
  // publish_builder_skill_intent registers through skill_assignment_state's process-global
  // registry and snapshots it to a FILE, and that module holds 0 rollback / 0 unregister sites,
  // so an answered error rolls the AgentConnection row back and leaves the Skill record exactly
  // where it is — which its own SkillStateStoreError docstring states by design ("The in-process
  // record is left as it is — nothing is silently rolled back").
  //
  // Measured at HEAD, not read: with the store present and its snapshot unreadable, the publish
  // raises SkillStateStoreError and `for_employee` still returns the record; after 173
  // skill_lens and skill_history read the SAME registry object, so the employee's skills page
  // shows it while this panel took its determinate `failed` arm. The same holds with the store
  // writable and the write failing, for EVERY governed domain in the request (the persist is
  // attempted once, after the whole loop), and when the store works the record is additionally
  // durable in the snapshot — so a later error in the connection half leaves a durably applied
  // Skill reported as not applied. The direction of the harm is under-reporting an application:
  // the operator is told the domain was not applied and the record is already there.
  //
  // So the skill rows take the determinate negative only when the ANSWER proves the body never
  // ran. The connection rows keep it unconditionally and byte-identically: that half IS the
  // transaction get_db rolls back.
  const skillRefusalDeterminate = answered && publishRefusedBeforeTheRouteBody(publish.error);
  // Single-sourced: the banner, the notice and the row labels must never disagree about
  // whether this publish's skill half can be spoken for. A second copy of this condition
  // beside it is the drift this chain keeps finding.
  // DIGEMP-FINISH-BUILDER-PUBLISH-INTEGRATION-141 (pass 62), the UNARMED REQUEST
  // DIRECTION. Pass 60 armed the WITHDRAWN direction of this screen's
  // registration-permanence claim on the class axis: createDriftWithdrawnReversalClause
  // filters its skill half by isGovernedDomain, because an ungoverned domain registers
  // nothing and so has no record to call permanent. The REQUESTED direction, in this
  // component, carried no class term at all -- it was keyed on the requested list's
  // LENGTH. Of this file's 10 product uses of isGovernedDomain, 0 were inside
  // PublishOutcomeSection.
  //
  // What that one predicate drives, measured: the banner (it REPLACES the determinate
  // "nothing was applied" sentence with a per-request deferral), the pass-38 notice
  // (which states that a Skill record is registered outside the request's transaction
  // and that no path cancels it), and every skill row's label. So an operator who
  // requested only free-text domains outside the governed vocabulary and hit an answered
  // 5xx was told a possibly permanent, unremovable Skill record may exist for each of
  // them, sent to the employee's skills page to look for it, and denied the determinate
  // negative that was TRUE -- a false alarm asserting an irreversible consequence that
  // did not occur, which is pass 60's own finding one surface over.
  //
  // The server says it outright: publish_builder_skill_intent answers a domain outside
  // SKILL_DOMAINS before anything is constructed, and its single persist call is guarded
  // on some outcome being applied or already-present -- so with no governed domain
  // requested nothing is registered, nothing is snapshotted, and the skill half cannot
  // even be the source of the error. Pinned in the other language.
  //
  // isGovernedDomain is the SERVER's fold (canonicalSkillDomainLabel is
  // _canonical_domain_label, and the vocabulary is pinned set-equal to SKILL_DOMAINS off
  // disk), so this adds no second vocabulary. Per-ROW on the labels rather than one
  // filter, because the isolating case is MIXED: a governed domain's row must keep the
  // uncertain arm while an ungoverned domain's row beside it takes the determinate one.
  // The third parameter defaults to the UNCERTAIN member, because under-reporting an
  // application is the harm this arm exists to avoid (the comment above names that
  // direction), so a caller that omits the term keeps today's behaviour.
  //
  // Fixture-shape census, decisive again: of the 16 hand-built isError publish objects in
  // the owned suite, 13 request governed domains only and 3 request no skill at all --
  // 0 mixed and 0 ungoverned-only, so every test of this arm was of the one class that
  // CAN register a record.
  const governedRequestedSkills = requestedSkills.filter(isGovernedDomain);
  const skillHalfUnverified =
    publish.isError && answered && !skillRefusalDeterminate && governedRequestedSkills.length > 0;
  const rowState = (outcome, surface, recordable = true) => {
    if (publish.isError) {
      if (!answered) return "unconfirmed";
      return surface === "skill" && !skillRefusalDeterminate && recordable
        ? "refused_unverified"
        : "failed";
    }
    if (outcome) return outcome.state;
    return settled ? "unreported" : "pending";
  };

  // A Skill reported `applied`/`already_present` is real, and it is NOT where the
  // connection half of the same publish is. Peer DIGEMP-FINISH-SKILL-DURABILITY-173 closed
  // the divergence this comment used to describe: skill_lens.get_skill_registry and
  // skill_history.get_skill_registry now both delegate to
  // skill_assignment_state.get_skill_registry, so all three return the SAME object
  // (re-measured here with `is`, not read off the commit message) and the employee's skills
  // page does show the record. What remains, and what this notice states, is that the record
  // lands in the service's assignment registry plus a FILE snapshot rather than in any mapped
  // table — so its survival is conditional on the state store being present on the host, while
  // the connection half of the same publish is a committed row. That asymmetry is also why the
  // rows above need their own answered-error arm: a transaction rollback reaches one half and
  // not the other.
  const skillRecordedInMemory = skillOutcomes.some(
    (outcome) => outcome?.state === "applied" || outcome?.state === "already_present",
  );

  // Pass 27: the contrast clause of the notice above — the only sentence on this screen saying
  // that ANY half of this publish survives a restart — had no arm on whether a connection row
  // was created. Two of the three systems this wizard offers resolve to `platform_managed`
  // (services/employee_builder_v3.resolve_environment_tool_key: "there is no connection record
  // to create"), and a skills-only publish requests none at all — so it asserted a durable
  // connection record directly above a row whose own label reads
  // `PUBLISH_CONNECTION_STATE_META.platform_managed`, i.e. "אין חיבור פר-עובד".
  //
  // Three arms, not two, because "nothing was created" is only knowable for a DETERMINATE
  // no-record outcome: `platform_managed` and `unsupported` both return before anything is
  // constructed, while a row with no delivered outcome at all (arity mismatch -> `unreported`)
  // is exactly the state pass 26 refused to describe with a determinate negative. An empty
  // `every` is true, which is the zero-systems case and is correct: no system requested, no
  // connection row. The connection-created arm is byte-identical to what shipped.
  const connectionRecordDurable = connectionOutcomes.some(
    (outcome) => outcome?.state === "applied" || outcome?.state === "already_present",
  );
  const connectionRecordAbsent =
    !connectionRecordDurable &&
    connectionOutcomes.every(
      (outcome) => outcome?.state === "platform_managed" || outcome?.state === "unsupported",
    );

  return (
    <>
    <div data-testid="publish-outcomes" className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-xs font-medium text-gray-700">החלת הבקשות מהאשף על העובד החדש</p>
      <p className="text-[11px] text-gray-500">
        הבקשות נשלחו למסלול הפרסום המבוקר — רשומות מועמדות בלבד, ללא הענקת הרשאה וללא שמירת פרטי גישה.
        כל בקשה מוצגת עם מצבה המדויק.
      </p>

      {/* Pass 27 read this as prose and left it unchanged: "לא ניתן לעזוב את המסך" is a claim
          about enforcement, and the enforcement exists — ScheduleDialog is modal (an
          `aria-modal` overlay, `fixed inset-0 z-50`, so nothing behind it is clickable), both
          of its exits are `disabled` while the publish is in flight and its Escape handler is
          gated, all pinned by "an in-flight publish keeps both exits and Escape closed...".
          Only browser-level navigation (back/reload/close) remains, which no SPA blocks without
          a `beforeunload` handler — `frontend/src` registers none (the sole occurrence of that
          word in the tree is this comment) — and that path is the same lost-answer shape pass 26
          labelled, not an unkept promise of this sentence. */}
      {publish.isPending && (
        <p role="status" data-testid="publish-pending" className="text-[11px] text-gray-500">
          מחיל את הבקשות… ניתן להמתין — לא ניתן לעזוב את המסך עד שתוצג התוצאה.
        </p>
      )}

      {publish.isError && (
        <div role="alert" data-testid="publish-error" className="rounded border border-red-200 bg-red-50 p-2 space-y-1.5">
          {/* The unanswered arm is tested FIRST and does not fall through to the server-detail
              arm, because on this arm there is no server detail to prefer: with no `response`
              there is no body at all, and with a gateway status the body is the proxy's, not the
              application's. The answered arm below is unchanged, byte for byte. */}
          <p className="text-[11px] text-red-700" data-testid="publish-error-reason">
            {!answered
              ? PUBLISH_UNCONFIRMED_NOTICE
              : typeof publish.error?.response?.data?.detail === "string"
                ? publish.error.response.data.detail
                : skillHalfUnverified
                  ? PUBLISH_FAILED_BANNER_SKILL_UNVERIFIED
                  : "החלת הבקשות נכשלה — הבקשות שלהלן טרם הוחלו."}
          </p>
          <button
            type="button"
            data-testid="publish-retry"
            onClick={() => publish.mutate(publish.variables)}
            className="border border-red-300 text-red-700 px-2 py-1 rounded text-[11px] hover:bg-red-100"
          >
            {PUBLISH_RETRY_LABEL}
          </button>
          {/* Beside the banner above rather than instead of it: the banner's sentence is true of
              the connection half, which get_db really does roll back, and its wording is pinned.
              What it cannot say is that the skill half was never in that transaction. Mutually
              exclusive with the session notice below BY CONSTRUCTION — 401 and 403 are both
              members of PUBLISH_PRE_BODY_REFUSAL_STATUSES, so a signed-out refusal is
              determinate and this arm cannot fire on it — and asserted so in both languages. */}
          {skillHalfUnverified && (
            <p
              data-testid="publish-skill-refusal-unverified"
              role="note"
              className="text-[11px] text-red-700"
            >
              {PUBLISH_SKILL_REFUSAL_UNVERIFIED_NOTICE}
            </p>
          )}
          {/* The cost of the two exits below, beside the remedy rather than instead of it. Not
              rendered on the session arm: SESSION_ENDED_PUBLISH_TAIL already names it there, and
              there the retry cannot succeed at all, so telling the operator to retry before
              leaving would be false. */}
          {!sessionEnded && (
            <p
              data-testid="publish-retry-screen-bound"
              role="note"
              className="text-[11px] text-red-700"
            >
              {PUBLISH_RETRY_SCREEN_BOUND_NOTICE}
            </p>
          )}
          {/* Beside the determinate wording above, not instead of it: "לא הוחל" is TRUE here
              (the auth dependency refused before the route body), and the row labels are left
              byte-identical for the same reason. What the panel could not say is that the
              message above is the auth layer's rather than an answer about these requests,
              that the tab is being signed out, and that the refusals it is showing are stored
              nowhere. */}
          {sessionEnded && (
            <p
              data-testid="publish-session-ended-notice"
              role="note"
              className="text-[11px] text-red-700"
            >
              {sessionEndedNotice(SESSION_ENDED_PUBLISH_TAIL)}
            </p>
          )}
        </div>
      )}

      {requestedSkills.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-gray-600 mb-1">מיומנויות מבוקשות</p>
          {skillRecordedInMemory && (
            <p
              data-testid="publish-skill-durability"
              className="text-[10px] text-amber-700 mb-1"
            >
              {[
                "רשומת המיומנות נרשמה במרשם ההקצאות של השירות ואינה נשמרת בבסיס הנתונים — היא מוצגת בעמוד המיומנויות של העובד, ושורדת הפעלה מחדש של השירות רק כאשר מאגר המצב של השירות זמין בשרת.",
                connectionRecordDurable
                  ? "רשומת החיבור, לעומת זאת, נשמרת בבסיס הנתונים."
                  : connectionRecordAbsent
                  ? "בפרסום הזה לא נוצרה רשומת חיבור, כך שאף חלק מהבקשות שלהלן לא נשמר בבסיס הנתונים."
                  : "",
                SKILL_RECORD_NO_REMOVAL_PATH,
              ].filter(Boolean).join(" ")}
            </p>
          )}
          <ul className="space-y-1">
            {requestedSkills.map((domain, index) => {
              const outcome = skillOutcomes[index];
              const meta = PUBLISH_SKILL_STATE_META[rowState(outcome, "skill", isGovernedDomain(domain))] || PUBLISH_SKILL_STATE_META.unknown;
              const intentMeta = PUBLISH_INTENT_STATE_META[outcome?.intent_state] || PUBLISH_INTENT_STATE_META.unreported;
              const contractLabel = PUBLISH_CANONICAL_CONTRACT_META[outcome?.canonical_contract] || null;
              return (
                <li key={domain} className="flex items-start gap-2 text-xs flex-wrap">
                  <span dir="ltr" className="text-gray-700">{domain}</span>
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
                  {outcome && (
                    <span data-testid={`publish-skill-intent-${index}`} className="flex items-center gap-1 flex-wrap">
                      <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${intentMeta.cls}`}>{intentMeta.label}</span>
                      {contractLabel && (
                        <span className="text-[10px] text-gray-500">חוזה: {contractLabel}</span>
                      )}
                    </span>
                  )}
                  {outcome?.detail && (
                    <span dir="ltr" className="text-[10px] text-gray-400 basis-full text-left">{outcome.detail}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {requestedSystems.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-gray-600 mb-1">מערכות מבוקשות</p>
          <ul className="space-y-1">
            {requestedSystems.map((key, index) => {
              const outcome = connectionOutcomes[index];
              const meta = PUBLISH_CONNECTION_STATE_META[rowState(outcome, "connection")] || PUBLISH_CONNECTION_STATE_META.unknown;
              const intentMeta = PUBLISH_INTENT_STATE_META[outcome?.intent_state] || PUBLISH_INTENT_STATE_META.unreported;
              const contractLabel = PUBLISH_CANONICAL_CONTRACT_META[outcome?.canonical_contract] || null;
              const tool = ENV_TOOLS.find((t) => t.id === key);
              return (
                <li key={key} className="flex items-start gap-2 text-xs flex-wrap">
                  <span className="text-gray-700">{tool ? `${tool.icon} ${tool.label}` : key}</span>
                  <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${meta.cls}`}>{meta.label}</span>
                  {outcome && (
                    <span data-testid={`publish-connection-intent-${index}`} className="flex items-center gap-1 flex-wrap">
                      <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${intentMeta.cls}`}>{intentMeta.label}</span>
                      {contractLabel && (
                        <span className="text-[10px] text-gray-500">חוזה: {contractLabel}</span>
                      )}
                    </span>
                  )}
                  {outcome?.credentials_pending && (
                    <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-700">
                      ממתין להגדרת פרטי גישה
                    </span>
                  )}
                  {outcome?.connector_implemented === false && (
                    <span className="text-[10px] rounded-full px-1.5 py-0.5 bg-gray-100 text-gray-600">
                      תבנית בלבד — אין מתאם חי בגרסה זו
                    </span>
                  )}
                  {outcome?.credentials_pending && outcome.required_fields?.length > 0 && (
                    <span className="text-[10px] text-gray-500 basis-full">
                      שדות להגדרה: <span dir="ltr">{outcome.required_fields.join(", ")}</span>
                    </span>
                  )}
                  {outcome?.detail && (
                    <span dir="ltr" className="text-[10px] text-gray-400 basis-full text-left">{outcome.detail}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
    {/* A sibling surface, not a row of the outcome list: the list reports what THIS publish
        answered, while this block reports what the server's canonical read now holds. Keeping
        them in one container let a canonical record line repeat the request label that the
        outcome row above already owns, which made every label query ambiguous. */}
    <CanonicalPublishState
      agentId={agentId || publish.data?.agent_id}
      intent={intent}
      publish={publish}
      skillOutcomes={skillOutcomes}
      connectionOutcomes={connectionOutcomes}
    />
    </>
  );
}

// ─── Post-creation: Schedule dialog ──────────────────────────────────────────

const INTERVAL_OPTIONS = [
  { value: 1,   label: "כל שעה" },
  { value: 6,   label: "כל 6 שעות" },
  { value: 12,  label: "כל 12 שעות" },
  { value: 24,  label: "יומי (24ש׳)" },
  { value: 48,  label: "כל יומיים" },
  { value: 168, label: "שבועי" },
];
// Pass 72 (the CAPPED CADENCE). Pass 71 handed over this file's one closed option set with no
// per-VALUE consequence pass of its own: passes 32/36/40 measured INTERVAL_OPTIONS' set equality
// with the route's _ALLOWED_INTERVALS, the duplicate-row cost and the firing notice's two skip
// causes, and nobody asked whether the six members cost DIFFERENTLY. They do, and not in the
// direction the labels read. `intervalHours` is COMPARED at 0 sites in this file (3 code sites:
// the useState, the sent field and the select's value), so the axis had 0 arms by construction.
//
// Measured against the code that decides: a scheduled check's run is created PENDING and
// manager_agent's own contract is `start -> ANALYZING -> AWAITING_TECHNICIAN_REVIEW` -- it does
// NOT close itself when the analysis finishes. That status is one of the 10 members of
// scheduler._ACTIVE_RUN_STATUSES, which is exactly the set the firing path's in-flight guard
// reads (1 site, keyed on AgentRun.context["scheduled_check_id"]): while a run of this check sits
// there, every due time takes `_bump_next(check); commit; return None` -- no ticket, no run, no
// paid call. The only exits are a technician's review and _expire_stale_pending_runs, which fails
// the run _PENDING_REVIEW_EXPIRY_HOURS (48) hours after its created_at and runs OUTSIDE the
// dispatch_allowed gate pass 40 armed, so the bound holds even under a platform suspension.
//
// So the second skip cause can last longer than the interval for the four options below that
// window, and the frequency this select offers is a MINIMUM GAP, not a rate: at 1/6/12/24 hours
// an unreviewed run throttles every one of them down to the same single analysis per review
// cycle. The harm is under-delivered coverage on the operator's own least-privilege-of-attention
// choice (pass 20's dangerous direction), and the notice already named the CAUSE at 1 site
// (`שעדיין פעילה`) while every duration needle read 0 in this region AND file-wide
// (expiry/at-most/upper-bound/AWAITING_TECHNICIAN_REVIEW, against in-region controls of 2/2/1/3).
// The one near-miss is L1462's `אישור טכנאי` -- the autonomy axis, about an action the
// employee PROPOSES, not about a finished run blocking the next due time.
//
// Unconditional, like the rest of firingNotice and like pass 40's suspension clause: it is a
// property of the scheduler and of this option set, not of this employee's activation state, so
// pass 20's shadowing arm is not repeated. Pass 36's rule is satisfied the other way round -- the
// force of this clause is a LIMIT on coverage, not a cost that the disabled state makes
// unreachable. The hours come from the constant below (pinned == scheduler's own value in the
// other language) and the count is DERIVED from INTERVAL_OPTIONS, so neither figure can drift if
// the option set or the expiry window changes.
const SCHEDULE_REVIEW_EXPIRY_HOURS = 48;
const SCHEDULE_THROTTLED_INTERVAL_COUNT = INTERVAL_OPTIONS.filter(
  (o) => o.value < SCHEDULE_REVIEW_EXPIRY_HOURS
).length;
const SCHEDULE_REVIEW_THROTTLE_CLAUSE =
  "ריצה של בדיקה מתוזמנת אינה נסגרת מעצמה בסוף הניתוח אלא נשארת בהמתנה לבדיקת טכנאי, " +
  "וכל עוד היא ממתינה היא „ריצה קודמת של אותה בדיקה שעדיין פעילה” שבסיבת הדילוג השנייה. " +
  `ריצה שנשארת בהמתנה מסומנת בידי השרת כפגה לכל היותר ${SCHEDULE_REVIEW_EXPIRY_HOURS} שעות מתחילתה — ` +
  "גם כשהשיגור עצור — כך שחלון הדילוג הזה נסגר בבדיקת טכנאי או בתפוגה, ולא מעצמו. " +
  `לכן התדירות שנבחרת כאן קובעת את המרווח המינימלי בין ניתוחים ולא את הקצב בפועל: ${SCHEDULE_THROTTLED_INTERVAL_COUNT} ` +
  "מהאפשרויות ברשימה קצרות מחלון ההמתנה הזה, ובחירה באחת מהן אינה מוסיפה ניתוחים כל עוד הריצה הקודמת ממתינה.";
// Pass 73 (the UNCOUNTED EFFECT). Pass 72 handed over the same per-value question keyed to the
// DEFAULT rather than to the member set. That census is a NEGATIVE result: six closed option
// sets, two of which label their default AS the default (tier_1's own desc, and avatar_color's
// empty option), and of the other four the default's consequence is already armed -- autonomy
// by pass 23 (all three modes identical over 1,098 scenarios), model by pass 16, availability
// by pass 71 (the default is the zero-penalty member, and the payload suppresses it so the
// column default applies), and this dialog's useState(24) by pass 72's throttle clause, which
// states the window in hours and the count of members below it.
//
// The defect is one question over, in the sentence enumerating what a SKIPPED due time does.
// scheduler._bump_next is the single writer of both timestamps (an assignment to the check's
// last-run column occurs at exactly 1 site in that module, inside it) and has FIVE call sites:
// 3 skip arms (a guardian audit already running, the agent not enabled, a run of this check in
// flight) and 2 firing arms. So a skip writes that column with the same line, and the same
// value, a real run writes. Three operator-visible sentences here said a skipped due time
// advances ONLY the next run time: this notice, the activated ternary's disabled arm (pass 25)
// and SCHEDULE_DUPLICATE_COST_DISABLED (pass 36). Arm count on the omitted column: 0
// operator-visible, 1 source comment -- pass 25's own comment further down, which states the
// truth nine lines from a sentence denying it, and whose consequence pass 25 published as an
// unowned carry-forward.
//
// What the destinations render: AgentDetailPage -- the page BOTH of this dialog's exits
// navigate to -- prints its last-run suffix whenever that column is set, and renders no run
// chip at all because latest_run_status is null, so nothing on that row says the check never
// ran. ScheduledChecksPage fills its own last-run column (plus an age line) beside its
// not-yet-run cell in the run-result column, i.e. it contradicts itself in adjacent cells. The
// authority was already in the tree, in the one sibling that is honest about it:
// EmployeeWorkspace renders the same column as a last run ATTEMPT with no recorded run, and
// attempt is the reading this clause borrows. Reachable with no operator action beyond the one
// click: every employee this wizard creates is created switched off (pass 25), so every value
// such a check's last-run column can ever carry is a skip bump -- and the direction of harm is
// pass 36's, a never-fired check reading as recently run, so the operator's own check of their
// own check comes back reassuring.
//
// Rendered ONCE, here, for pass 40's reason verified rather than inherited: firingNotice
// renders unconditionally immediately below the activated ternary and above the uncertainty
// notices, so it is on screen in every state those two reach. The other two sites lose the
// false exclusivity and name the second timestamp (a list, not a second copy of the
// consequence). Scoped to a SKIPPED due time, which this notice DEFINES as its own two causes:
// pass 40's platform-suspension arm is worded as not processed at all precisely because
// nothing advances there and _bump_next is never reached.
export const SCHEDULE_SKIP_ATTEMPT_CLAUSE =
  "מועד ההרצה האחרונה אינו עדות לכך שהבדיקה רצה: השרת מקדם אותו גם אחרי הרצה אמיתית וגם בכל מועד מדולג. לכן בדף העובד הבדיקה תציג „אחרון” מתקדם גם אם לא רצה אף פעם, ובמסך „בדיקות מתוזמנות” אותו מועד יופיע בעמודת „הרצה אחרונה” לצד „טרם הורץ” בעמודת „תוצאת ריצה”.";

export function ScheduleDialog({
  agentName,
  agentId,
  agentEnabled = false,
  publishIntent = null,
  publish = null,
  // The LIVE draft, so the dialog can name what the window between submit and this screen
  // dropped. Defaults to null, so every render that does not pass it produces no notice and is
  // byte-identical to what shipped.
  draftIntent = null,
  canOpenEmployeePage = true,
  onScheduled,
  onSkip,
}) {
  const [intervalHours, setIntervalHours] = useState(24);
  // Pass 32: ScheduledCheckCreate carries five fields and this dialog sent four. The one it
  // never sent -- `description` -- is the ticket body scheduler._fire_scheduled_check builds
  // (`description=check.description or f"...: {check.name}"`), and manager_agent hands that
  // body to run_analysis as ticket_description: it is the whole statement of the work a
  // periodic run examines. _fire_guardian_posture_check exists for exactly this reason -- its
  // docstring says the generic path "only builds a ticket from the check's static description,
  // so the model never saw the posture snapshot and the run produced no findings". The sibling
  // admin surface (ScheduledChecksPage) collects it; this path asked only for the interval, so
  // the dialog stated the WHEN and never the WHAT. Optional on the server and optional here,
  // with the empty case stated rather than left looking like a configured check.
  const [subject, setSubject] = useState("");
  const subjectTrimmed = subject.trim();
  // Hoisted so the create call and both new sentences name the SAME value: the operator reads
  // the exact check name that becomes the ticket title, not a paraphrase of it. The 200-char
  // cap is the server's MAX_CHECK_NAME_CHARS (pinned cross-language), which exists because the
  // scheduler files the check as a ticket titled "[SCHEDULED CHECK] <name>" into a 255-char
  // Ticket.title -- a longer name made every firing fail and retry on every poll.
  const checkName = `בדיקה תקופתית — ${agentName}`.slice(0, 200);

  // Pass 25: read from the create response (AgentResponse.is_enabled), never assumed -- the
  // wizard was reading 2 of that response's 22 fields and this was not one of them. The
  // reassuring arm below requires `=== true`, so a payload that stops carrying the field takes
  // the cautious wording rather than silently producing the promise (pass 23's rule).
  const activated = agentEnabled === true;
  const scheduleMutation = useMutation({
    mutationFn: () => scheduledChecksApi.create({
      agent_id: agentId,
      name: checkName,
      // The one accepted field this dialog never sent. Sent as null rather than "" when the
      // operator leaves it empty, so the row records "no subject" and the server's own
      // `check.description or <fallback>` arm is what fills the ticket body.
      description: subjectTrimmed || null,
      interval_hours: intervalHours,
      is_enabled: true,
    }),
    onSuccess: onScheduled,
  });

  // The did-the-write-land axis for this dialog's own write. Before pass 35 the error branch
  // below had ONE arm for every failure, so a request nothing answered read as a described
  // error over a row create_check had already committed. Same shared predicate as the publish
  // panel and the create step -- not a second gateway-status list.
  const scheduleUnanswered =
    scheduleMutation.isError && !publishApplicationAnswered(scheduleMutation.error);

  // The other half of the same axis (pass 55, the COMMITTED PREFIX; the measurement is at
  // SCHEDULE_COMMITTED_ROW_CAUSE). An ANSWERED error proves nothing was written only when
  // the status is one this route can only have produced before its body ran, because its
  // commit runs inside the body with two statements after it. Mutually exclusive with the
  // arm above by construction (answered against not answered) and with the session notice
  // below (401 and 403 are both members of the pre-body list, so a signed-out refusal is
  // determinate and this arm cannot fire on it); both asserted in the other language.
  const scheduleRowMayExist =
    scheduleMutation.isError &&
    publishApplicationAnswered(scheduleMutation.error) &&
    !publishRefusedBeforeTheRouteBody(scheduleMutation.error);

  // What a firing PRODUCES. Rendered unconditionally: it is a property of the scheduler, not of
  // this employee's activation state, so pass 20's shadowing arm is not repeated here. Both skip
  // causes are named because a sentence that says only "opens a ticket" is false on a skipped
  // due time, and the disabled employee is the one shape this dialog always has.
  const firingNotice =
    `מועד הרצה שאינו מדולג פותח פנייה חדשה בשם „[SCHEDULED CHECK] ${checkName}” ` +
    "ומריץ ניתוח AI אחד עבור העובד. מועד מדולג — עובד מושבת, או ריצה קודמת של אותה בדיקה שעדיין פעילה — " +
    "מקדם את מועד הריצה הבא ואת מועד ההרצה האחרונה. " + SCHEDULE_SKIP_ATTEMPT_CLAUSE +
    " הניתוח מתבקש לאבחן מצב מערכת ולא לפנות ללקוח, " +
    "וטיוטת תשובה ללקוח נמחקת בצד השרת גם אם המודל הפיק אחת. " +
    SCHEDULE_PLATFORM_SUSPENSION_CLAUSE + " " + SCHEDULE_REVIEW_THROTTLE_CLAUSE;
  // Two states, each with its own sentence: a subject was given, or none was. The empty arm
  // quotes the server's own fallback body so the operator reads what the model will actually
  // receive (that literal is pinned against the scheduler in the backend suite).
  const subjectNote = subjectTrimmed
    ? "הטקסט נשמר על הבדיקה ונשלח כתוכן הפנייה בכל הרצה — זה מה שהמודל מקבל כמשימה."
    : `לא הוזן נושא: גוף הפנייה שהמודל יקבל יהיה „בדיקת IT מתוזמנת: ${checkName}” — כלומר שם הבדיקה בלבד, בלי נושא לאבחן. אפשר להזין נושא כאן, או לערוך את הבדיקה אחר כך ממסך „בדיקות מתוזמנות”.`;
  const hasIntent = Boolean(
    publishIntent &&
    ((publishIntent.requested_skill_domains || []).length > 0 ||
      (publishIntent.requested_environment_tools || []).length > 0)
  );
  // The publish outcome is the wizard's final word on the operator's requested intent: while it
  // is in flight both exits (schedule, skip) and Escape stay closed, so no request can be
  // navigated away from before its applied/pending/unsupported state was shown.
  const intentDriftNotice = createIntentDriftNotice(draftIntent, publishIntent, publish);
  const publishInFlight = Boolean(hasIntent && publish?.isPending);

  // No Escape handler previously -- a keyboard user had to tab to the "דלג" button.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === "Escape" && !publishInFlight) onSkip(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onSkip, publishInFlight]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" dir="rtl">
      <div role="dialog" aria-modal="true" aria-labelledby="schedule-dialog-title" className="bg-white rounded-xl shadow-xl border p-6 w-96 space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">✅</span>
          <h3 id="schedule-dialog-title" className="text-base font-semibold text-gray-800">העובד נוצר בהצלחה!</h3>
        </div>

        {!activated && (
          <p
            data-testid="schedule-activation-notice"
            role="note"
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
          >
            העובד נוצר במצב מושבת: הוא לא ישובץ לפנייה, לא יריץ ניתוח, לא יצטרף לשיחה, וכל מסך צוות יציג אותו כמנותק — עד שיופעל מדף העובד.
          </p>
        )}

        {!canOpenEmployeePage && (
          <p
            data-testid="schedule-operator-access-notice"
            role="note"
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
          >
            {EMPLOYEE_PAGE_NO_ACCESS_NOTICE}
          </p>
        )}

        {/* Above the panel, and NOT inside it: with nothing requested at submit `hasIntent` is
            false and the panel does not render at all, which is exactly the shape where an item
            added during the create window would otherwise be mentioned on no screen. */}
        {intentDriftNotice && (
          <p
            data-testid="create-intent-drift-notice"
            role="note"
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
          >
            {intentDriftNotice}
          </p>
        )}

        {hasIntent && publish && (
          <PublishOutcomeSection agentId={agentId} intent={publishIntent} publish={publish} />
        )}

        <p className="text-sm text-gray-600">
          האם ברצונך להוסיף <strong>בדיקה מתוזמנת</strong> עבור <em>{agentName}</em>?
          {/* Pass 25: this sentence promised automatic periodic activation, and
              scheduler._fire_scheduled_check's first act is
              `if not agent or not agent.is_enabled: _bump_next(check); commit; return None` --
              so on the employee this dialog has just created (is_enabled False, always) every due
              firing is skipped while last_run_at/next_run_at still advance, which the detail page
              then renders as a green dot and a recent "last run". The screen's own pre-create
              banner already said the employee is created `במצב מושבת` (1 site, pre-create
              region); this was the 1 site in the post-create region, and it asserted the negation.
              The enabled arm is byte-identical to the old sentence and is currently unreachable
              (no create path sets is_enabled) -- kept because it is the true one if that ever
              changes, with the default pinned in the other language. */}
          {activated
            ? " הסוכן יופעל אוטומטית בכל מרווח הזמן שתבחר."
            : " הבדיקה תיווצר ותיקבע לפי המרווח, אך כל עוד העובד מושבת היא לא תריץ דבר: כל מועד שיגיע יידלג ומועד הריצה הבא ומועד ההרצה האחרונה יתקדמו."}
        </p>
        <p data-testid="schedule-firing-consequence" className="text-xs text-gray-600 bg-gray-50 border rounded-lg p-2">
          {firingNotice}
        </p>
        <div>
          <label className="block text-xs text-gray-500 mb-1">תדירות הרצה</label>
          <select
            value={intervalHours}
            onChange={(e) => setIntervalHours(Number(e.target.value))}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          >
            {INTERVAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1" htmlFor="schedule-subject">נושא הבדיקה — נשלח כגוף הפנייה (אופציונלי)</label>
          <textarea
            id="schedule-subject"
            data-testid="schedule-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            rows={2}
            placeholder="למשל: בדוק מצב גיבויים בשרתי הלקוח ודווח על כשלים מהיממה האחרונה"
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          <p data-testid="schedule-subject-note" className="text-xs text-gray-500 mt-1">{subjectNote}</p>
        </div>
        {scheduleMutation.isError && (
          <p className="text-red-600 text-xs">{scheduleMutation.error?.response?.data?.detail || "שגיאה ביצירת הבדיקה"}</p>
        )}
        {/* Beside the sentence above, not instead of it: that generic sentence says an error
            occurred in the creating, which is true in this state -- it is the SILENCE about
            whether the row exists that this notice fills. Left byte-identical for the same
            reason pass 34 left the create's generic sentence alone, and described here rather
            than quoted so its one literal site stays countable. An answered refusal
            (require_admin's 403, the feature-flag 403, the interval 422, the agent 404) renders
            nothing new here: widening the notice to every error would state an uncertainty over
            a refusal the server described in full. Pass 55: those four are exactly the
            statuses raised BEFORE the commit of create_check, which is why they are
            determinate, and they are not the whole answered population. An answered 500 can
            come from after that commit, so the arm below keys on the pre-body status list
            rather than on isError, and the generic sentence still stands alone on every
            refusal the server really described. */}
        {scheduleUnanswered && (
          <p
            data-testid="schedule-unconfirmed-notice"
            role="note"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700"
          >
            {scheduleUnconfirmedNotice(activated)}
          </p>
        )}
        {scheduleRowMayExist && (
          <p
            data-testid="schedule-committed-row-notice"
            role="note"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700"
          >
            {scheduleCommittedRowNotice(activated)}
          </p>
        )}
        {scheduleMutation.isError && requestEndedTheSession(scheduleMutation.error) && (
          <p
            data-testid="schedule-session-ended-notice"
            role="note"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700"
          >
            {sessionEndedNotice(SESSION_ENDED_SCHEDULE_TAIL)}
          </p>
        )}
        {scheduleMutation.isPending && (
          <p
            data-testid="schedule-inflight-notice"
            role="note"
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
          >
            {SCHEDULE_INFLIGHT_NOTICE}
          </p>
        )}
        <div className="flex gap-2">
          <button
            onClick={() => scheduleMutation.mutate()}
            disabled={scheduleMutation.isPending || publishInFlight}
            className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex-1"
          >
            {scheduleMutation.isPending ? "יוצר..." : `${SCHEDULE_SUBMIT_LABEL} ⏱`}
          </button>
          <button
            onClick={onSkip}
            disabled={publishInFlight}
            className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 text-gray-600 disabled:opacity-50"
          >
            {SKIP_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EmployeeBuilderPage ──────────────────────────────────────────────────────

export const EMPTY_FORM = {
  role_preset: null,
  name: "",
  description: "",
  client_id: null,
  tools: [],
  autonomy_level: "approval_required",
  permission_tier: "tier_1",
  allowed_tools: null,     // null = all tools for tier allowed; explicit list = restricted allowlist
  model: "claude-sonnet-4-6",
  system_prompt: "",
  // Employee identity / persona. No authority (no tier, no tool, no approval) — but NOT
  // presentation-only: specialties_text / role_title / name / description are the manager's
  // entire routing score and availability "offline" is dropped from the candidate set.
  // See conversation_service.score_agent_for_message and routing_eligible_agents.
  persona_preset: null,    // UI-only: which identity preset is applied; never sent to the API
  role_title: "",
  avatar_key: "",
  avatar_color: "",
  availability_status: "available",
  personality: "",
  specialties_text: "",
  intro_text: "",
  // Wizard-only intent (v3 preview): never part of the create payload
  requested_skill_domains: [],
  requested_skill_domain_draft: "",
};

// PROD-BUILDER-059: one key per create ATTEMPT. A retry of the same attempt (a double click,
// or a retry after the response was lost) sends the same key and the server answers with the
// employee it already created; a deliberate new attempt gets a new key. Crypto is available in
// every supported browser; the timestamp fallback keeps a key present in old test environments.
const newCreateIdempotencyKey = () =>
  (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    ? crypto.randomUUID()
    : `builder-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function EmployeeBuilderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [createdAgent, setCreatedAgent] = useState(null);
  // The wizard-only intent captured at submit, replayed to the governed publish endpoint once
  // the employee exists; rendered back to the operator with one outcome per requested item.
  const [publishIntent, setPublishIntent] = useState(null);

  const { data: companies = [], isError: companiesFailed, refetch: refetchCompanies } = useQuery({
    queryKey: ["companies"],
    queryFn: () => companiesApi.list(),
  });

  const publishMutation = useMutation({
    mutationFn: ({ agentId, payload }) => agentsApi.builderPublish(agentId, payload),
  });

  // The key is held in a ref, not state: it must stay byte-identical across React renders and
  // across a retry of the same attempt, and changing it must not re-render the wizard.
  const createIdempotencyKey = useRef(null);
  const createMutation = useMutation({
    mutationFn: (payload) => {
      if (!createIdempotencyKey.current) {
        createIdempotencyKey.current = newCreateIdempotencyKey();
      }
      return agentsApi.create(payload, {
        headers: { "Idempotency-Key": createIdempotencyKey.current },
      });
    },
  });

  // Runs after the single creating action: the wizard-only intent captured at submit is replayed
  // to the governed publish endpoint, and its per-item outcome is rendered back to the operator
  // in the schedule dialog (which stays un-leavable while the publish is in flight). No intent
  // requested -> nothing to publish (and nothing silently dropped: nothing was requested).
  const publishIntentAfterCreate = (agent, intent) => {
    setCreatedAgent(agent);
    setPublishIntent(intent);
    if (
      (intent.requested_skill_domains || []).length > 0 ||
      (intent.requested_environment_tools || []).length > 0
    ) {
      publishMutation.mutate({ agentId: agent.id, payload: intent });
    }
  };

  // Unlike every sibling agent-management page (AgentsPage, AgentDetailPage, AgentPermissionsPage),
  // this wizard had no gate of its own -- only App.jsx's bare "logged in" check stood between an
  // unprivileged technician and all eight steps (the actual POST /api/agents/ still refuses them, but
  // only after they've filled in the whole form). Matches this page's own docstring ("Access:
  // Admin/Owner only.") and AgentPermissionsPage's exact loading/failed/denied pattern.
  const { can, loading: permissionsLoading, failed: permissionsFailed } = useAgentPermissions();

  if (permissionsLoading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (permissionsFailed) {
    return <div role="alert" className="p-8 text-center text-red-600 text-sm">לא ניתן לטעון את ההרשאות שלך. נסה לרענן את הדף.</div>;
  }
  if (!can("agents_manage")) {
    return <div className="p-8 text-center text-gray-500">אין לך הרשאה ליצור סוכנים.</div>;
  }

  // pass 70: the operator's OWN access to every destination this screen names after creation.
  // Read from the same context the gate above uses, so the two cannot disagree; by this point
  // the loading and failed arms have already returned, so this is a two-state axis with no
  // third "unknown" member to represent.
  const canOpenEmployeePage = can(EMPLOYEE_PAGE_VIEW_GRANT);

  // True only for a create failure the APPLICATION did not answer -- the one class that can
  // sit over a durable employee. An answered refusal keeps pass 21's wording unchanged.
  const createFailedUnanswered =
    createMutation.isError && !publishApplicationAnswered(createMutation.error);

  // The create's own session-ended arm (pass 37). Exclusive with the flag above.
  const createSessionEnded =
    createMutation.isError && requestEndedTheSession(createMutation.error);

  // pass 57: the draft stopped being what the server will receive. True while the request is in
  // flight and once the employee exists; deliberately FALSE after an answered refusal, because a
  // resubmit sends the draft as it is then, so the steps' own promises are true again.
  const draftSubmitted = createMutation.isPending || Boolean(createdAgent);

  const canAdvance = () => {
    if (step === 1) return form.name.trim().length > 0;
    if (step === 3 && companiesFailed) return false;      // do not move on with an unknown customer list
    return true;
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep((s) => s + 1);
    else {
      // last step = preview — "Confirm" actually submits the draft shown there
      // Pass 22: one composition, so a pre-create surface can count what is SENT rather than what
      // was typed - the appended line is ~60 characters the 10k bound applies to and no rendered
      // count included.
      const systemPrompt = composeSystemPrompt(form);
      const specialties = parseSpecialties(form.specialties_text);
      // The wizard-only collections are not part of the create payload -- and they are not
      // dropped either: after the single creating action they are replayed to the governed
      // publish endpoint, and every requested item is shown with its exact outcome.
      const intent = {
        requested_skill_domains: [...(form.requested_skill_domains || [])],
        requested_environment_tools: [...form.tools],
      };
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        client_id: form.client_id || undefined,
        model: form.model,
        system_prompt: systemPrompt || undefined,
        autonomy_level: form.autonomy_level,
        permission_tier: form.permission_tier,
        allowed_tools: form.allowed_tools,     // null = all tools for tier; explicit list = restricted
        // Identity / persona: every field is optional and stays undefined when the operator did not
        // set it, so the create payload for someone who skips the identity step is unchanged.
        role_title: form.role_title.trim() || undefined,
        avatar_key: form.avatar_key.trim() || undefined,
        avatar_color: form.avatar_color || undefined,
        availability_status: form.availability_status !== "available" ? form.availability_status : undefined,
        personality: form.personality.trim() || undefined,
        intro_text: form.intro_text.trim() || undefined,
        specialties: specialties.length ? specialties : undefined,
      };
      createMutation.mutate(payload, {
        onSuccess: (agent) => {
          // The attempt is over: a later, deliberate creation must not reuse its key. A failed
          // attempt keeps the key, so its retry is the same attempt to the server.
          createIdempotencyKey.current = null;
          publishIntentAfterCreate(agent, intent);
        },
      });
    }
  };

  return (
    <>
    {createdAgent && (
      <ScheduleDialog
        agentName={createdAgent.name}
        agentId={createdAgent.id}
        agentEnabled={createdAgent.is_enabled}
        publishIntent={publishIntent}
        publish={publishMutation}
        draftIntent={{
          requested_skill_domains: form.requested_skill_domains,
          requested_environment_tools: form.tools,
        }}
        canOpenEmployeePage={canOpenEmployeePage}
        onScheduled={() => navigate(`/agents/${createdAgent.id}`)}
        onSkip={() => navigate(`/agents/${createdAgent.id}`)}
      />
    )}
    <div className="max-w-2xl mx-auto" dir="rtl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">בניית עובד דיגיטלי</h1>
        <p className="text-sm text-gray-500 mt-0.5">אשף v3 להגדרת סוכן AI חדש — זהות, סקופ, מיומנויות, מודל, מערכות, מגבלות ותצוגת מוכנות לפני יצירה</p>
      </div>

      <StepIndicator current={step} />

      <div className="bg-white rounded-xl border shadow-sm p-6 min-h-[400px] space-y-4">
        {step === 1 && <Step1Role form={form} setForm={setForm} />}
        {step === 2 && <StepIdentity form={form} setForm={setForm} draftSubmitted={draftSubmitted} />}
        {step === 3 && <Step2Scope form={form} setForm={setForm} companies={companies} companiesFailed={companiesFailed} onRetryCompanies={refetchCompanies} />}
        {step === 4 && <Step3Skills form={form} setForm={setForm} />}
        {step === 5 && <Step4Model form={form} setForm={setForm} />}
        {step === 6 && <Step5Systems form={form} setForm={setForm} />}
        {step === 7 && <Step6Limits form={form} setForm={setForm} draftSubmitted={draftSubmitted} sentPayload={createMutation.variables || null} />}
        {step === 8 && <Step7Preview form={form} companies={companies} companiesFailed={companiesFailed} onEditStep={setStep} createFailed={createMutation.isError} createUnconfirmed={createFailedUnanswered} canOpenEmployeePage={canOpenEmployeePage} />}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-30"
          >
            ← קודם
          </button>

          <span className="text-xs text-gray-400">{step} / {TOTAL_STEPS}</span>

          <button
            type="button"
            onClick={handleNext}
            disabled={!canAdvance() || createMutation.isPending}
            className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
          >
            {step < TOTAL_STEPS - 1
              ? "הבא ←"
              : step === TOTAL_STEPS - 1
              ? "תצוגה מקדימה ←"
              : createMutation.isPending
              ? "יוצר..."
              : "צור עובד ✓"}
          </button>
        </div>

        {/* The one region on screen during the create's in-flight window. `role="status"` rather
            than `alert`: nothing has gone wrong, and what the operator needs is that the draft is
            already sent. */}
        {createMutation.isPending && (
          <p
            data-testid="create-inflight-notice"
            role="status"
            className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700"
          >
            {CREATE_INFLIGHT_NOTICE}
          </p>
        )}

        {createMutation.isError && (
          <>
          <p className="text-red-600 text-sm text-center">
            {/* Keep the message a string in every case. A 422's list `detail` never arrives as a
                list here -- services/api.js's response interceptor runs every error body through
                flattenValidationDetail first -- so this guard is what stands between an OBJECT
                `detail` (the `{code}` / `{message}` refusal payloads) and "Objects are not valid
                as a React child"; such a body takes the generic sentence below. */}
            {typeof createMutation.error?.response?.data?.detail === "string"
              ? createMutation.error.response.data.detail
              : "שגיאה ביצירת העובד"}
          </p>
          {/* The generic sentence above is every failure the server did not describe, which
              is exactly where an unanswered request lands. It says an error happened; it
              cannot say whether the employee exists, and the only control offered is the
              same button. */}
          {createFailedUnanswered && (
            <p data-testid="create-unconfirmed-notice" role="note" className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {createUnconfirmedNotice(canOpenEmployeePage)}
            </p>
          )}
          {/* The one failure class that loses the whole draft as well as the attempt. Exclusive
              with the notice above, which is the unanswered arm. */}
          {createSessionEnded && (
            <p data-testid="create-session-ended-notice" role="note" className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {sessionEndedNotice(SESSION_ENDED_CREATE_TAIL)}
            </p>
          )}
          </>
        )}
      </div>

    </div>
    </>
  );
}
